import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { loadPrimaryContact } from '../utils/clientPrimaryContact';
import { parseDroppedEmail, plainTextFrom, formatSender, EmailParseError, readClipboard, readClipboardImages } from '../utils/emailParse';
import { stageEmailAttachments, rollbackStaged, linkStagedToTask, stageSummary, fileToAttachment } from '../utils/emailAttachments';
import { fileUrl } from '../utils/fileUrl';
import { uploadFile } from '../utils/uploadFile';
import { fileDelete } from '../utils/fileDelete';
import { keysDelete } from '../utils/keysDelete';
import {
  TASK_STATUS,
  TASK_STATUS_TODO,
  TASK_STATUS_IN_PROGRESS,
  TASK_STATUS_WAITING,
  TASK_STATUS_ON_US,
  TASK_STATUS_TERMINAL,
  TASK_STATUS_SETTABLE,
  TASK_STATUS_AUTO_ONLY,
  TASK_STATUS_PRE_INTAKE,
  TASK_STATUS_COLORS,
  STATUS_ORDER,
  DOC_REQUEST_ITEM_STATUS,
} from '../constants/statuses';
import { CLIENT_LEVEL, FILING_YEARS, buildFilingUpdates } from '../constants/docFiling';
import DocTypeOptions from '../components/DocTypeOptions';
import { useDocVocab, DOC_KEYS } from '../utils/docVocab';
import { isAdminStaff } from '../utils/isAdminStaff';

const TASK_TYPES = ['client','consultant','internal'];   // routing (who the work is for) — 'general' dropped; no rows/logic used it
const TASK_TYPE_LABELS = { client:'Client', consultant:'Consultant', internal:'Internal' };
const PRIORITIES = ['Low','Normal','High','Urgent'];
const TERMINAL_PAGE = 25;   // My Tasks "Completed" page size (bounded fetch + Load older)
const STATUSES = Object.values(TASK_STATUS);
const PRIORITY_COLORS = { Low:'96,165,250', Normal:'52,211,153', High:'251,191,36', Urgent:'248,113,113' };
const STATUS_COLORS = TASK_STATUS_COLORS;

export default function TaskEngine({ darkMode, staffUser, allClients, allProperties, initialTask, onClearInitialTask }) {
  const [activeTab, setActiveTab] = useState('my');
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [terminal, setTerminal] = useState([]);                 // completed/cancelled for the CURRENT tab+filter (paged) — open lists are open-only; this feeds the "Completed" section
  const [terminalTotal, setTerminalTotal] = useState(0);        // TRUE count (may exceed what's loaded) for the "Completed (N)" header
  const [showTerminal, setShowTerminal] = useState(false);      // "Completed" section collapsed by default
  const [assignedNewlyDone, setAssignedNewlyDone] = useState(0);// my-assigned tasks completed since last viewed (bounded head-count; assigned loads open-only)
  const [taskVersion, setTaskVersion] = useState(0);            // bumped each loadTasks → re-runs the terminal loader so counts stay fresh after a completion
  const [searchTerminal, setSearchTerminal] = useState([]);     // DB search over ALL my terminal tasks (so a completed task is found regardless of what's paged in)
  const [groupSiblings, setGroupSiblings] = useState([]);       // the selected task's duplication group (any status, incl. completed) — for the group view + "From the group" notes
  const [assignedTasks, setAssignedTasks] = useState([]);       // tasks I assigned to OTHERS (all statuses; view splits open/completed/cancelled)
  const [staffList, setStaffList] = useState([]);
  const [ptoCoverage, setPtoCoverage] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detailMsg, setDetailMsg] = useState('');   // detail-view action errors (e.g. privacy toggle), surfaced not swallowed
  const [cancelPending, setCancelPending] = useState(false);   // Cancelled selected → inline reason field, nothing writes until it has content
  const [cancelReason, setCancelReason] = useState('');
  // Edit mode (title / due date / priority) — staged in editDraft until Save. Status/reassign/private stay immediate.
  const [editMode, setEditMode] = useState(false);
  const [editDraft, setEditDraft] = useState({ title:'', due_date:'', priority:'Normal' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editMsg, setEditMsg] = useState('');
  const [newSubtask, setNewSubtask] = useState('');   // the "add a subtask" checklist input
  const [dupOpen, setDupOpen] = useState(false);      // detail "Duplicate for…" picker open
  const [dupAssignee, setDupAssignee] = useState(''); // chosen assignee for the duplicate
  const [dupBusy, setDupBusy] = useState(false);
  const [promoteSub, setPromoteSub] = useState('');       // subtask id whose "assign to…" picker is open ('' = none)
  const [promoteAssignee, setPromoteAssignee] = useState(''); // chosen assignee for promoting a subtask to a child task
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [seriesGroupId, setSeriesGroupId] = useState(null);  // New Task form: shared group_id across a "Save & add for another" series
  useEffect(() => { setCancelPending(false); setCancelReason(''); setEditMode(false); setEditMsg(''); setNewSubtask(''); setDupOpen(false); setDupAssignee(''); setPromoteSub(''); setPromoteAssignee(''); }, [selectedTask?.id]);   // reset pending-cancel + edit mode + subtask input + duplicate + promote pickers when the task changes
  // Fetch the selected task's duplication group directly (any status, incl. completed — allTasks is open-only after the
  // cap fix, so it can't carry completed siblings). Feeds the group view + the read-only "From the group" notes.
  useEffect(() => {
    const gid = selectedTask?.group_id;
    if (!gid) { setGroupSiblings([]); return; }
    (async () => {
      const { data, error } = await supabase.from('tasks').select('*').eq('group_id', gid).is('deleted_at', null).order('created_at', { ascending: true });
      if (error) { console.warn('[groupSiblings] read failed:', error.message); setGroupSiblings([]); return; }
      setGroupSiblings(data || []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTask?.id, selectedTask?.group_id]);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showPTO, setShowPTO] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [kpiFilter, setKpiFilter] = useState('');   // '' | 'todo' | 'waiting' | 'overdue' — set by clicking a KPI card; only ever non-empty on My Tasks (cleared on tab change)
  const [sortKey, setSortKey] = useState('');       // '' = default (query order); else a column key. Click header: asc → desc → back to default.
  const [sortDir, setSortDir] = useState('asc');
  const [searchQ, setSearchQ] = useState('');
  // Search-to-DB for the Completed section — ANY tab now. When searching, fetch matching terminal tasks (scoped to the
  // current tab/filter) so a completed one is findable regardless of what's paged in (the #8 "where did it go" gap).
  // Debounced 300ms. MUST stay below the searchQ/activeTab/filterAssignee declarations it reads (referencing above their
  // useState is a TDZ crash).
  useEffect(() => {
    const q = searchQ.trim();
    if (!q || !staffUser) { setSearchTerminal([]); return; }
    const safe = q.replace(/[,()*]/g, ' ').trim();   // strip chars that would break PostgREST or()/ilike grammar
    if (!safe) { setSearchTerminal([]); return; }
    const handle = setTimeout(async () => {
      const pat = `*${safe}*`;
      let query = supabase.from('tasks').select('*').is('deleted_at', null).in('status', ['Complete', 'Cancelled']);
      if (activeTab === 'assigned') query = query.eq('assigned_by', staffUser.id).neq('assigned_to', staffUser.id);
      else if (activeTab === 'all') { if (filterAssignee) query = query.eq('assigned_to', filterAssignee); }
      else query = query.eq('assigned_to', staffUser.id);   // 'my'
      const { data, error } = await query
        .or(`title.ilike.${pat},client_name.ilike.${pat},property_name.ilike.${pat},task_type.ilike.${pat}`)
        .order('updated_at', { ascending:false }).limit(50);
      if (error) { console.warn('[searchTerminal] read failed:', error); return; }
      setSearchTerminal(data || []);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ, activeTab, filterAssignee, staffUser]);
  const [newTask, setNewTask] = useState({ title:'', task_type:'internal', priority:'Normal', status:'Open', client_id:'', client_name:'', property_id:'', property_name:'', due_date:'', description:'', assigned_to:'', assigned_to_name:'', source:'manual', source_email:'' });
  const [emailPaste, setEmailPaste] = useState('');
  // ── Dropped email (Outlook drag) ──────────────────────────────────────────────────────────────
  // ⛔ Declared beside emailPaste, ABOVE every effect and callback that reads them. A dep array
  // evaluates during render, and this file already carries four use-before-define findings.
  const [droppedEmail, setDroppedEmail] = useState(null);   // the parsed message, or null
  const [dropActive, setDropActive] = useState(false);      // drag hovering the zone
  const [dropBusy, setDropBusy] = useState(false);
  const [dropError, setDropError] = useState(null);         // a STRING the user can act on, or null
  const [pendingAttachments, setPendingAttachments] = useState([]);   // normalised, not yet uploaded
  const [attachNote, setAttachNote] = useState(null);       // counted result of the last capture
  const emailFileInputRef = React.useRef(null);
  const attachInputRef = React.useRef(null);
  const [attachDragActive, setAttachDragActive] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractedTasks, setExtractedTasks] = useState([]);   // review-panel drafts from the email extractor (always reviewed, even at N=1)
  const [creatingBatch, setCreatingBatch] = useState(false);  // double-submit guard for "Create N tasks"
  // ⛔ saveTask had NO in-flight guard while createBatch did, and attachment staging widened the
  // window from milliseconds to seconds — a double-click now creates two tasks with two copies of
  // every file. Regression introduced the same day uploads moved ahead of the insert.
  const [savingTask, setSavingTask] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [replyCc, setReplyCc] = useState('');  // Gap 3: CC recipients, comma-separated
  const [replySubject, setReplySubject] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replyMode, setReplyMode] = useState('note'); // 'note' or 'email'
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveMsgErr, setSaveMsgErr] = useState(false);   // false = green ✓ success, true = red ⚠ error
  const flashTimer = React.useRef(null);
  // Single entry point for the floating banner: errors render red ⚠, successes green ✓. Owns auto-dismiss.
  const flash = (msg, isError = false, ms = isError ? 6000 : 3500) => {
    setSaveMsg(msg); setSaveMsgErr(isError);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => { setSaveMsg(''); setSaveMsgErr(false); }, ms);
  };
  const [ptoForm, setPtoForm] = useState({ pto_staff_id:'', pto_staff_name:'', cover_staff_id:'', cover_name:'', start_date:'', end_date:'', notes:'' });
  const [ptoTaskIds, setPtoTaskIds] = useState([]);          // tasks selected for the coverer in the PTO form
  const [coverageActive, setCoverageActive] = useState([]);  // task_coverage rows active TODAY → coverer's My Tasks (roster counts now come from the RPC)
  // Per-staff open-task counts from staff_workload_counts() — a SECURITY DEFINER aggregate that INCLUDES
  // confidential tasks the viewer can't read (capacity, not content) and attributes a PTO-covered task to
  // the coverer only. null = not loaded yet OR the call failed; workloadErr distinguishes them. ⛔ There is
  // deliberately NO fallback to client-derived counts: those are the undercount this replaces, and showing
  // them on failure would silently restore the bug the numbers are meant to have fixed.
  const [workloadCounts, setWorkloadCounts] = useState(null);   // Map(staff_id -> {todo, in_progress, waiting})
  const [workloadErr, setWorkloadErr]       = useState('');
  const [ptoMsg, setPtoMsg] = useState('');                  // IN-PANEL feedback (top saveMsg banner is off-screen when the panel is open)
  const [ptoSaving, setPtoSaving] = useState(false);         // double-submit guard
  const [clientProperties, setClientProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);

  // ── Document Request context (for tasks where related_type = 'document_request') ──
  const [docRequest, setDocRequest] = useState(null);           // the document_requests row
  const [docRequestItems, setDocRequestItems] = useState([]);   // document_request_items rows
  const [docRequestToken, setDocRequestToken] = useState(null); // upload_tokens row (if request approved)
  const [docRequestAoaFiles, setDocRequestAoaFiles] = useState([]); // file_exchange rows (ntrg_to_client direction)
  const [docReqLoading, setDocReqLoading] = useState(false);
  const [docReqBusy, setDocReqBusy] = useState(false);          // any Step 5 action in flight
  const [docReqError, setDocReqError] = useState('');
  const [clientContactEmail, setClientContactEmail] = useState(''); // picked from client row
  // Step 5b: per-item "remove" inline panel state
  const [removeItemOpen, setRemoveItemOpen] = useState({});     // {itemId: true/false}
  const [removeItemReason, setRemoveItemReason] = useState({}); // {itemId: 'reason text'}
  const [removeItemBusy, setRemoveItemBusy] = useState({});     // {itemId: true/false}
  const [showRemovedItems, setShowRemovedItems] = useState(false); // collapsible "Removed Items" section
  // Step 8: Partial delivery state — when admin clicks "Deliver Partial", we
  // record which item ids will be marked delivered post-send. Cleared after
  // the email confirm flow completes.
  const [partialDeliveryPending, setPartialDeliveryPending] = useState(null);

  // Gap 2: per-attachment action state (consultant's AOA attachments)
  const [removeAttachOpen, setRemoveAttachOpen] = useState({});   // {fileId: true/false}
  const [removeAttachReason, setRemoveAttachReason] = useState({});// {fileId: 'reason'}
  const [removeAttachBusy, setRemoveAttachBusy] = useState({});   // {fileId: true/false}
  const [previewAttachBusy, setPreviewAttachBusy] = useState({}); // {fileId: true/false}

  // ── Task attachments (task_attachments join → file_exchange). Detail-view only. ──
  // Reuses the doc store + 'client-files' bucket; NO parallel plumbing. Uploaded files are
  // fully UNSCOPED (client/property/parcel null, folder 'Task Attachments', not viewable/need_review)
  // so they surface ONLY on the task — never in a client's Filed Documents or the consultant portal.
  const [taskAttachments, setTaskAttachments] = useState([]);      // [{id, created_at, file: {…file_exchange}}]
  const [attaching, setAttaching] = useState(false);               // upload/link in flight
  const [attachMsg, setAttachMsg] = useState('');                  // in-panel result (mirrors ptoMsg pattern)
  const [attachPickerOpen, setAttachPickerOpen] = useState(false); // "attach existing" open (client tasks only)
  const [attachPickerFiles, setAttachPickerFiles] = useState([]);  // that client's file_exchange rows
  const [attachPickerLoading, setAttachPickerLoading] = useState(false);

  // ── "File document" — reuses intake's filing model (constants/docFiling): UPDATE the attachment's
  // EXISTING file_exchange row's scope in place. task_attachments is NEVER touched → file stays on the task.
  const [filingFor, setFilingFor] = useState(null);   // the attachment being filed (or null)
  const [filingForm, setFilingForm] = useState({ clientId:'', clientName:'', docType:'', propertyId:'', propertyName:'', parcelId:'', year:String(new Date().getFullYear()), viewableClient:false, viewableConsultant:false });
  const [filingProps, setFilingProps] = useState([]);    // properties for the chosen client
  const [filingParcels, setFilingParcels] = useState([]); // parcels for the chosen property
  const [filingBusy, setFilingBusy] = useState(false);
  const { vocab } = useDocVocab();   // folders & types — the filing write resolves through it
  const [filingMsg, setFilingMsg] = useState('');

  // ── Step 6: email send confirmation flow ──
  // After clicking an email button, we mailto: and show a modal asking the admin
  // to confirm whether they actually clicked Send in Outlook.
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [emailConfirmData, setEmailConfirmData] = useState(null);
  // emailConfirmData shape: {
  //   purpose: 'client_request' | 'consultant_notify' | 'consultant_partial' | 'generic',
  //   to: string,
  //   subject: string,
  //   body: string,
  //   onConfirmYes: async () => void   // runs when admin says "yes I sent it"
  // }
  const [clientContacts, setClientContacts] = useState([]); // available contact emails for the task's client

  // Step 6: toast shown after successful email log. Auto-dismisses + navigates
  // back to tasks list after a short countdown. Admin can click to go faster
  // or the × to stay on the current view.
  const [emailSentToast, setEmailSentToast] = useState(null);
  // emailSentToast shape: { secondsLeft: number, taskWillClose: boolean }

  const isDark = darkMode;
  const isAdmin = isAdminStaff(staffUser);

  const c = isDark ? {
    bg:'var(--ink)', card:'rgba(47,56,84,0.6)', border:'#374060', text:'#e8eaf2',
    muted:'var(--slate)', sub:'#a8b0cc', input:'var(--ink)', inputBorder:'#434C68',
    th:'rgba(26,34,56,0.5)', rowHover:'rgba(255,255,255,0.03)',
  } : {
    bg:'var(--bg)', card:'#ffffff', border:'var(--line)', text:'var(--ink)',
    muted:'var(--slate)', sub:'#4a5068', input:'#ffffff', inputBorder:'#c8ccd8',
    th:'var(--line-soft)', rowHover:'rgba(0,0,0,0.02)',
  };

  const s = {
    card: { background:c.card, border:`1px solid ${c.border}`, borderRadius:'10px', overflow:'hidden', marginBottom:'14px', boxShadow: isDark?'none':'0 1px 4px rgba(0,0,0,0.06)' },
    cardHeader: { padding:'12px 18px', borderBottom:`1px solid ${c.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' },
    cardTitle: { fontSize:'13px', fontWeight:'700', color:c.text },
    table: { width:'100%', borderCollapse:'collapse' },
    th: { padding:'8px 14px', textAlign:'left', fontSize:'10px', fontWeight:'700', color:c.muted, letterSpacing:'0.08em', textTransform:'uppercase', background:c.th },
    td: { padding:'10px 14px', borderBottom:`1px solid ${isDark?'rgba(67,76,104,0.25)':'#eef0f6'}`, fontSize:'13px', color:c.text, verticalAlign:'middle' },
    input: { width:'100%', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.text, fontSize:'13px', outline:'none', fontFamily:"'Public Sans',system-ui,sans-serif", boxSizing:'border-box' },
    select: { width:'100%', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.text, fontSize:'13px', outline:'none', fontFamily:"'Public Sans',system-ui,sans-serif" },
    textarea: { width:'100%', padding:'8px 11px', background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.text, fontSize:'13px', outline:'none', fontFamily:"'Public Sans',system-ui,sans-serif", resize:'vertical', minHeight:'80px', boxSizing:'border-box' },
    btn: { padding:'7px 14px', background:'linear-gradient(135deg,#B40000,#9C0000)', border:'none', borderRadius:'7px', color: 'white', fontSize:'12px', fontWeight:'700', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btnGhost: { padding:'7px 14px', background:'transparent', border:`1px solid ${c.inputBorder}`, borderRadius:'7px', color:c.sub, fontSize:'12px', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    btnSm: { padding:'4px 10px', fontSize:'11px', fontWeight:'600', borderRadius:'5px', border:'none', cursor:'pointer', fontFamily:"'Public Sans',system-ui,sans-serif" },
    badge: (color) => ({ background:`rgba(${color},0.12)`, color:`rgb(${color})`, borderRadius:'20px', padding:'3px 9px', fontSize:'11px', fontWeight:'600', whiteSpace:'nowrap' }),
    label: { fontSize:'11px', color:c.muted, marginBottom:'4px', fontFamily:"'DM Mono',monospace", letterSpacing:'0.06em', textTransform:'uppercase', display:'block' },
  };

  const loadTasks = useCallback(async () => {
    if (!staffUser) return;
    const { data: myT, error: myErr } = await supabase.from('tasks').select('*').eq('assigned_to', staffUser.id).is('deleted_at', null).not('status','in','("Complete","Cancelled")').order('due_date', { ascending:true, nullsFirst:false });
    if (myErr) console.warn('[loadTasks] my tasks read failed:', myErr);
    const myTasks = myT || [];
    // All Tasks — OPEN ONLY. Loading every status here truncates at the 1000-row cap once tasks accumulate (and nothing
    // purges completed). Open work is self-limiting (~280 across the team); completed comes from loadTerminal. Visible
    // to everyone. ⛔ CONFIDENTIALITY IS ENFORCED BY RLS, NOT BY THIS CLIENT: the policy in
    // 2026-08-07_rls_batch2_tasks.sql returns an is_private row only to its assigner or assignee, so the
    // app does NO masking anywhere — a row absent from any result here is absent BY POLICY, and every row
    // present is one this viewer may read in full. (The client-side mask this comment used to describe was
    // the pre-RLS mechanism; it went dark the day that policy landed and was removed 2026-08-15.)
    const { data: allT, error: allErr } = await supabase.from('tasks').select('*').is('deleted_at', null).not('status','in','("Complete","Cancelled")').order('created_at', { ascending:false });
    if (allErr) console.warn('[loadTasks] all tasks read failed:', allErr);
    const allTasksData = allT || [];
    // Tasks I ASSIGNED to someone else — OPEN ONLY (same cap reasoning; completed comes from loadTerminal). Null assigned_by → excluded.
    const { data: asg, error: asgErr } = await supabase.from('tasks').select('*')
      .eq('assigned_by', staffUser.id).neq('assigned_to', staffUser.id).is('deleted_at', null)
      .not('status','in','("Complete","Cancelled")')
      .order('created_at', { ascending:false });
    if (asgErr) console.warn('[loadTasks] assigned-by-me read failed:', asgErr);
    setAssignedTasks(asg || []);

    // The completed/cancelled ("terminal") set is loaded separately + scoped to the active tab by loadTerminal — bump
    // taskVersion so it refreshes after this reload (e.g. a task just completed). Kept OUT of the open lists so nothing
    // truncates at the cap.
    setTaskVersion(v => v + 1);
    // "Newly done" badge for Tasks I Assigned — a bounded HEAD count (assigned loads open-only, so it can't be counted
    // client-side): my-assigned tasks completed since I last viewed the tab.
    let lastSeen = null; try { lastSeen = localStorage.getItem(`wh_assigned_seen_${staffUser.id}`); } catch {}
    let ndQ = supabase.from('tasks').select('id', { count: 'exact', head: true }).eq('assigned_by', staffUser.id).neq('assigned_to', staffUser.id).eq('status', 'Complete');
    if (lastSeen) ndQ = ndQ.gt('completed_at', lastSeen);
    const { count: ndCount } = await ndQ;
    setAssignedNewlyDone(ndCount || 0);

    // Active PTO coverage grants (today within [start,end], inclusive). Query is scoped to ACTIVE windows, so it stays
    // tiny regardless of how many historical rows accumulate. One fetch serves the coverer's My Tasks + the roster.
    // LOCAL date (project rule — toISOString is UTC and rolls to tomorrow in the evening, activating/expiring early).
    const _n = new Date();
    const today = `${_n.getFullYear()}-${String(_n.getMonth()+1).padStart(2,'0')}-${String(_n.getDate()).padStart(2,'0')}`;
    const { data: cov, error: covErr } = await supabase.from('task_coverage').select('task_id,covered_by_id,start_date,end_date').lte('start_date', today).gte('end_date', today);
    if (covErr) console.warn('[loadTasks] coverage query failed:', covErr);
    const covActive = cov || [];
    setCoverageActive(covActive);

    // Workload counts — the ONLY source for the roster's active/waiting and (unfiltered) the KPI cards.
    // Failure must be visible: null the map and surface the reason, never zeros and never client-derived.
    const { data: wl, error: wlErr } = await supabase.rpc('staff_workload_counts');
    if (wlErr) {
      console.warn('[loadTasks] staff_workload_counts failed:', wlErr);
      setWorkloadCounts(null); setWorkloadErr(wlErr.message || 'unavailable');
    } else {
      setWorkloadCounts(new Map((wl || []).map(r => [r.staff_id, r])));
      setWorkloadErr('');
    }
    // Open tasks THIS user is currently covering (assigned to the person on PTO) → merged into My Tasks, flagged.
    const myCoveredIds = covActive.filter(cv => cv.covered_by_id === staffUser.id).map(cv => cv.task_id);
    let coveredTasks = [];
    if (myCoveredIds.length) {
      const { data: ct, error: ctErr } = await supabase.from('tasks').select('*').in('id', myCoveredIds).is('deleted_at', null).not('status','in','("Complete","Cancelled")');
      if (ctErr) console.warn('[loadTasks] covered-tasks query failed:', ctErr);
      coveredTasks = (ct || []).map(t => ({ ...t, _covering: true }));
    }
    const myPlusCovered = [...myTasks, ...coveredTasks.filter(ct => !myTasks.some(m => m.id === ct.id))];

    // ── ENRICH DOC-REQUEST TASKS ───────────────────────────────────────────
    // For ALL tasks with related_type='document_request' (any status), fetch the
    // request's `request_code` so we can show it in the task list.
    // For PRE-INTAKE tasks, ALSO check if intake is pending and override the
    // displayed status to 'Waiting on Intake' so admins see at the list level.
    const PRE_INTAKE_STATUSES = TASK_STATUS_PRE_INTAKE;
    const allTaskRows = [...myPlusCovered, ...allTasksData];   // include covered tasks so a covered doc-request task also enriches
    const allDocReqIds = Array.from(new Set(
      allTaskRows
        .filter(t => t.related_type === 'document_request' && t.related_id)
        .map(t => t.related_id)
    ));

    if (allDocReqIds.length === 0) {
      setTasks(myPlusCovered);   // FIX: the no-enrichment fast path must also carry covered tasks (was setTasks(myTasks) → dropped them)
      setAllTasks(allTasksData);
      return;
    }

    try {
      // Single query: pull request_code + upload_token_id for all referenced requests
      const { data: docReqs } = await supabase.from('document_requests')
        .select('id, request_code, upload_token_id, status')
        .in('id', allDocReqIds);
      const reqById = Object.fromEntries((docReqs || []).map(r => [r.id, r]));

      // For pre-intake tasks only, build a token map for the intake check
      const preIntakeTaskReqIds = allTaskRows
        .filter(t => t.related_type === 'document_request' && t.related_id && PRE_INTAKE_STATUSES.includes(t.status))
        .map(t => t.related_id);
      const preIntakeTokenIds = Array.from(new Set(
        preIntakeTaskReqIds.map(id => reqById[id]?.upload_token_id).filter(Boolean)
      ));
      let tokenStringByTokenId = {};
      if (preIntakeTokenIds.length > 0) {
        const { data: tokenRows } = await supabase.from('upload_tokens')
          .select('id, token').in('id', preIntakeTokenIds);
        tokenStringByTokenId = Object.fromEntries((tokenRows || []).map(r => [r.id, r.token]));
      }
      const tokenByDocReqId = {};
      preIntakeTaskReqIds.forEach(reqId => {
        const tokenId = reqById[reqId]?.upload_token_id;
        if (tokenId && tokenStringByTokenId[tokenId]) {
          tokenByDocReqId[reqId] = tokenStringByTokenId[tokenId];
        }
      });
      const tokens = Object.values(tokenByDocReqId);
      let pendingByToken = {};
      if (tokens.length > 0) {
        const { data: pendingFiles } = await supabase.from('file_exchange')
          .select('upload_token')
          .in('upload_token', tokens)
          .eq('need_review', true);
        (pendingFiles || []).forEach(f => {
          if (!f.upload_token) return;
          pendingByToken[f.upload_token] = (pendingByToken[f.upload_token] || 0) + 1;
        });
      }

      // Enrich every task: attach _requestCode (display) + maybe _displayStatus
      const enrich = (t) => {
        if (t.related_type !== 'document_request' || !t.related_id) return t;
        const out = { ...t };
        const req = reqById[t.related_id];
        if (req?.request_code) out._requestCode = req.request_code;
        // Intake recomputation only for pre-intake tasks
        if (PRE_INTAKE_STATUSES.includes(t.status)) {
          const tok = tokenByDocReqId[t.related_id];
          if (tok) {
            const pending = pendingByToken[tok] || 0;
            if (pending > 0) {
              out._displayStatus = TASK_STATUS.WAITING_ON_INTAKE;
              out._intakePendingCount = pending;
            }
          }
        }
        return out;
      };
      setTasks(myPlusCovered.map(enrich));
      setAllTasks(allTasksData.map(enrich));
    } catch (e) {
      console.warn('[loadTasks enrich]', e);
      // Fallback: use raw rows if enrichment fails
      setTasks(myPlusCovered);
      setAllTasks(allTasksData);
    }
  }, [staffUser, isAdmin]);

  const loadStaff = useCallback(async () => {
    const { data, error } = await supabase.from('ntrg_staff').select('*').order('name');
    if (error) console.warn('[loadStaff] read failed:', error);
    setStaffList(data || []);
  }, []);

  const loadPTO = useCallback(async () => {
    const _n = new Date();  // LOCAL today (toISOString is UTC → drops a still-active window in the evening)
    const todayLocal = `${_n.getFullYear()}-${String(_n.getMonth()+1).padStart(2,'0')}-${String(_n.getDate()).padStart(2,'0')}`;
    const { data, error } = await supabase.from('pto_coverage').select('*').gte('end_date', todayLocal).order('start_date');
    if (error) console.warn('[loadPTO] read failed:', error);
    setPtoCoverage(data || []);
  }, []);

  useEffect(() => {
    loadTasks();
    loadStaff();
    loadPTO();
  }, [loadTasks, loadStaff, loadPTO]);

  // ── LIVE STATUS REFRESH ──────────────────────────────────────────────────
  // Poll every 60s + refresh on window focus so admins see "Waiting on Intake"
  // banners appear at the LIST level when clients drop files (without having
  // to open individual tasks).
  useEffect(() => {
    if (!staffUser) return;
    const focusHandler = () => loadTasks();
    window.addEventListener('focus', focusHandler);
    const intervalId = setInterval(() => loadTasks(), 60000);
    return () => {
      window.removeEventListener('focus', focusHandler);
      clearInterval(intervalId);
    };
  }, [staffUser, loadTasks]);

  // Step-Gap1: If we arrived here from DocumentIntake's "Back to Task" button,
  // there's a sessionStorage sidecar telling us which task to auto-open. Watch
  // for tasks to finish loading, then open the requested task once.
  useEffect(() => {
    if (!tasks.length) return;
    let returnTaskId = null;
    try { returnTaskId = sessionStorage.getItem('wh_intake_return_task'); } catch {}
    if (!returnTaskId) return;
    const t = tasks.find(x => x.id === returnTaskId);
    if (t) {
      setSelectedTask(t);
      try {
        sessionStorage.removeItem('wh_intake_return_task');
        sessionStorage.removeItem('wh_intake_token');
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  // Auto-open task if navigated from client/property view
  useEffect(() => {
    if (initialTask) {
      setSelectedTask(initialTask);
      if (onClearInitialTask) onClearInitialTask();
    }
  }, [initialTask, onClearInitialTask]);

  // ── Document Request loader ──
  // When a task's related_type is 'document_request', fetch the request + items + token + AOA files
  async function loadDocRequestContext(task) {
    if (!task || task.related_type !== 'document_request' || !task.related_id) {
      setDocRequest(null);
      setDocRequestItems([]);
      setDocRequestToken(null);
      setDocRequestAoaFiles([]);
      return;
    }
    setDocReqLoading(true);
    setDocReqError('');
    try {
      // 1. Fetch the request itself
      const { data: req } = await supabase.from('document_requests')
        .select('*').eq('id', task.related_id).maybeSingle();
      if (!req) { setDocRequest(null); setDocReqLoading(false); return; }
      setDocRequest(req);

      // 2. Items
      const { data: items } = await supabase.from('document_request_items')
        .select('*').eq('request_id', req.id).order('display_order', { ascending: true });
      setDocRequestItems(items || []);

      // 3. Upload token (if already generated — i.e. admin has approved)
      if (req.upload_token_id) {
        const { data: tok } = await supabase.from('upload_tokens')
          .select('*').eq('id', req.upload_token_id).maybeSingle();
        setDocRequestToken(tok || null);
      } else {
        setDocRequestToken(null);
      }

      // 4. AOA templates attached by consultant (ntrg_to_client files on this request)
      const { data: aoa } = await supabase.from('file_exchange')
        .select('*')
        .eq('upload_session', req.id)
        .eq('direction', 'ntrg_to_client');
      setDocRequestAoaFiles(aoa || []);

      // 5. Try to pre-pick a client contact email (primary first, asset/property fallback)
      //    Also populate clientContacts[] with all available contacts so admin can pick in Compose Email.
      const contactList = [];
      if (req.client_id) {
        // The client screen's primary contact (utils/clientPrimaryContact) — the one record Add / Edit Client edit too.
        const pc = await loadPrimaryContact(req.client_id).catch((e) => { console.warn('[doc request] primary contact read failed:', e?.message || e); return null; });
        if (pc?.email) {
          setClientContactEmail(pc.email);
          contactList.push({ role: 'Primary Contact', name: [pc.first_name, pc.last_name].filter(Boolean).join(' '), email: pc.email });
        }
      }
      // Property-level contacts (asset manager + property manager) if this request has a property
      if (req.property_id) {
        const { data: prop } = await supabase.from('properties')
          .select('asset_managers, asset_managers_email, property_managers, property_managers_email')
          .eq('id', req.property_id).maybeSingle();
        if (prop?.asset_managers_email) {
          contactList.push({ role: 'Asset Manager', name: prop.asset_managers || '', email: prop.asset_managers_email });
        }
        if (prop?.property_managers_email) {
          contactList.push({ role: 'Property Manager', name: prop.property_managers || '', email: prop.property_managers_email });
        }
      }
      setClientContacts(contactList);
    } catch (e) {
      console.error('[loadDocRequestContext]', e);
      setDocReqError('Could not load request details: ' + (e.message || 'unknown error'));
    }
    setDocReqLoading(false);
  }

  useEffect(() => {
    loadDocRequestContext(selectedTask);
    loadAttachments(selectedTask?.id);
    setAttachMsg(''); setAttachPickerOpen(false); setAttachPickerFiles([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTask?.id, selectedTask?.related_id]);

  // ── Task attachments (detail-view only) ───────────────────────────────────
  async function loadAttachments(taskId) {
    if (!taskId) { setTaskAttachments([]); return; }
    const { data, error } = await supabase
      .from('task_attachments')
      .select('id, created_at, file:file_exchange_id (id, file_name, file_size_bytes, storage_path, folder_name, folder_key, created_at, client_id, property_id, direction, file_type, type_key, year_subfolder, filed_at, viewable_client, viewable_consultant)')
      .eq('task_id', taskId)
      .order('created_at', { ascending: true });
    if (error) { console.warn('[loadAttachments]', error.message); setTaskAttachments([]); return; }  // read-swallow → warn, don't fake empty
    setTaskAttachments(data || []);
  }

  // Upload a NEW file → fully UNSCOPED file_exchange row (task-only) → link via task_attachments.
  // Unscoped on purpose: surfaces ONLY on the task, never in a client's Filed Documents / Intake / consultant portal.
  async function uploadNewAttachment(file) {
    if (!file || !selectedTask?.id) return;
    setAttaching(true); setAttachMsg('');
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `tasks/${selectedTask.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}_${safeName}`;
      const { error: upErr } = await uploadFile(storagePath, file);
      if (upErr) { setAttachMsg(`⚠ Upload failed: ${upErr.message}`); setAttaching(false); return; }
      const { data: fx, error: fxErr } = await supabase.from('file_exchange').insert({
        client_id: null, property_id: null, parcel_id: null,
        direction: 'ntrg_internal', folder_key: DOC_KEYS.taskAttachments,
        file_name: file.name, file_size_bytes: file.size, storage_path: storagePath,
        uploaded_by_name: staffUser?.name || 'NTRG Staff',
        viewable_client: false, viewable_consultant: false, need_review: false,
      }).select('id').single();
      if (fxErr || !fx) { setAttachMsg(`⚠ Couldn't record the file: ${fxErr?.message || 'no row returned'}`); setAttaching(false); return; }
      const { error: linkErr } = await supabase.from('task_attachments').insert({
        task_id: selectedTask.id, file_exchange_id: fx.id, created_by: staffUser?.id || null,
      });
      if (linkErr) { setAttachMsg(`⚠ File uploaded but linking to the task failed: ${linkErr.message}`); setAttaching(false); return; }
      setAttachMsg('✓ Attached');
      await loadAttachments(selectedTask.id);
    } finally { setAttaching(false); }
  }

  // Attach an EXISTING file_exchange row — join insert only; the file stays wherever it already belongs.
  async function attachExisting(fileExchangeId) {
    if (!fileExchangeId || !selectedTask?.id) return;
    setAttaching(true); setAttachMsg('');
    const { error } = await supabase.from('task_attachments').insert({
      task_id: selectedTask.id, file_exchange_id: fileExchangeId, created_by: staffUser?.id || null,
    });
    setAttaching(false);
    if (error) { setAttachMsg(error.code === '23505' ? '⚠ Already attached' : `⚠ Attach failed: ${error.message}`); return; }  // 23505 = unique (task_id, file_exchange_id)
    setAttachMsg('✓ Attached'); setAttachPickerOpen(false);
    await loadAttachments(selectedTask.id);
  }

  // POSITIVE proof that unlinking THIS task link leaves the file reachable from nowhere (a task-only upload,
  // never filed, not linked elsewhere). ⛔ FAIL SAFE: returns true ONLY on proof of orphanhood; any client scope,
  // property tag, non-internal direction, parcel-doc link, other task link, OR query error → false (unlink only).
  // An orphan is recoverable debris; a wrongly-deleted client document is not — so destroy demands certainty.
  async function orphanedAfterUnlink(fx, joinIdBeingRemoved) {
    try {
      if (!fx?.id) return false;
      if (fx.client_id) return false;                                      // client-scoped → shows in per-client Files
      if (fx.property_id) return false;                                    // property-tagged → may show in property-keyed views
      if (fx.direction && fx.direction !== 'ntrg_internal') return false;  // a client/ntrg exchange file, not a task-only upload
      const { data: pd, error: pdErr } = await supabase.from('parcel_documents').select('id').eq('file_exchange_id', fx.id).limit(1);
      if (pdErr || (pd && pd.length)) return false;                        // ambiguous OR parcel-filed → reachable/fail-safe
      const { data: links, error: lErr } = await supabase.from('task_attachments').select('id').eq('file_exchange_id', fx.id);
      if (lErr || (links || []).some(l => l.id !== joinIdBeingRemoved)) return false;  // ambiguous OR another task links it
      return true;                                                         // no scope, no parcel, no other link → orphan
    } catch { return false; }                                             // any throw → fail safe (unlink only)
  }

  // Remove from a task. Unlink is the default and always safe. If unlinking leaves the file a PROVEN orphan
  // (see orphanedAfterUnlink), cascade-delete it via /api/file-delete (R2 object → verify gone → row, audited
  // action='delete') behind a confirm — otherwise the R2 object + row leak invisibly, hundreds-deep at import scale.
  // NOTE: named removeTaskAttachment (not removeAttachment) — a consultant-AOA removeAttachment(file,reason) exists
  // later in the file and, being a same-name function declaration, would otherwise win and hijack this call.
  async function removeTaskAttachment(attachment) {
    const joinId = attachment?.id;
    const fx = attachment?.file;
    if (!joinId || !selectedTask?.id) return;

    const willDestroy = await orphanedAfterUnlink(fx, joinId);
    if (willDestroy && !window.confirm(`"${fx.file_name || 'This file'}" isn't filed anywhere else — removing it from this task deletes it permanently. Continue?`)) return;

    const { error, count } = await supabase.from('task_attachments').delete({ count: 'exact' }).eq('id', joinId);
    if (error) { setAttachMsg(`⚠ Remove failed: ${error.message}`); return; }
    if (!count) { setAttachMsg('⚠ Nothing removed (already gone?)'); await loadAttachments(selectedTask.id); return; }  // 0-rows-no-error guard

    if (willDestroy) {   // R2 object + row; leaves the (now-unlinked) task_attachments behind — already deleted above
      const { error: delErr } = await fileDelete(fx.id);
      setAttachMsg(delErr ? `Unlinked — but file cleanup failed (${delErr.message}); it's now unreferenced debris.` : '✓ Removed & deleted');
    }
    await loadAttachments(selectedTask.id);
  }

  // "Attach existing" picker — client-scoped (only meaningful when the task has a client). Excludes already-attached.
  async function loadClientFilesForAttach() {
    if (!selectedTask?.client_id) return;
    setAttachPickerLoading(true);
    const attachedIds = new Set(taskAttachments.map(a => a.file?.id).filter(Boolean));
    const { data, error } = await supabase.from('file_exchange')
      .select('id, file_name, file_size_bytes, folder_name, created_at')
      .eq('client_id', selectedTask.client_id)
      .order('created_at', { ascending: false })
      .range(0, 999);   // a single client's files sit well under the 1000 cap; range keeps the bound explicit
    setAttachPickerLoading(false);
    if (error) { setAttachMsg(`⚠ Couldn't load client files: ${error.message}`); return; }
    setAttachPickerFiles((data || []).filter(f => !attachedIds.has(f.id)));
  }

  // Open an attachment (signed URL, same 'client-files' bucket as every other doc).
  async function openAttachment(storagePath) {
    if (!storagePath) return;
    const url = await fileUrl(storagePath, 'open').catch(() => null);
    if (!url) { setAttachMsg('⚠ Could not open the file.'); return; }
    window.open(url, '_blank');
  }

  // ── File document — intake's filing form/model, filing the attachment's EXISTING row in place ──────────
  async function openFiling(attachment) {
    const fx = attachment.file;
    setFilingFor(attachment); setFilingMsg('');
    const clientId = selectedTask?.client_id || '';
    // A client-level doc type must NOT inherit the task's property — that prefill is how a Contract would
    // silently acquire one. It preselects the explicit client-level option instead, so the form SHOWS it.
    const preType = vocab?.resolveType(fx?.type_key || fx?.file_type) || null;   // the folder table's row for the file's type
    const preClientLevel = !!preType && vocab.folder(preType.folder_key)?.level === 'client';
    const preProp = preClientLevel ? CLIENT_LEVEL : (selectedTask?.property_id || fx?.property_id || '');
    setFilingForm({
      clientId, clientName: selectedTask?.client_name || '',
      docType: preType?.key || '',
      propertyId: preProp, propertyName: preClientLevel ? '' : (selectedTask?.property_name || ''),
      parcelId: '',
      // Already filed → keep its year, INCLUDING deliberate "no year" (''). Never filed → today's year as the
      // convenience default. Defaulting a re-file to the current year would silently rewrite 2021 → 2026.
      year: fx?.filed_at ? (fx.year_subfolder || '') : String(new Date().getFullYear()),
      // Prefill visibility from the ROW, not false — this form is also the re-file/edit path, and defaulting
      // to false silently un-shares a doc that was visible to the client or consultant.
      viewableClient: !!fx?.viewable_client, viewableConsultant: !!fx?.viewable_consultant,
    });
    setFilingParcels([]);
    if (clientId) await loadFilingProps(clientId, preProp);
    else setFilingProps([]);
  }

  async function loadFilingProps(clientId, preselectPropertyId) {
    if (!clientId) { setFilingProps([]); return; }
    const { data, error } = await supabase.from('properties')
      .select('id, name, city, state, master_property_id, client_id')
      .eq('client_id', clientId).order('name', { ascending: true }).range(0, 999);   // one client's properties are well under the cap
    if (error) { console.warn('[loadFilingProps]', error.message); setFilingProps([]); return; }
    setFilingProps(data || []);
    if (preselectPropertyId) {
      const p = (data || []).find(x => String(x.id) === String(preselectPropertyId));
      if (p) loadFilingParcels(p);
    }
  }

  async function loadFilingParcels(property) {
    if (!property?.master_property_id) { setFilingParcels([]); return; }
    const { data, error } = await supabase.from('parcels')
      .select('id, account_number, parcel_name, re_pp')
      .eq('master_property_id', property.master_property_id).order('account_number', { ascending: true }).range(0, 999);
    if (error) { console.warn('[loadFilingParcels]', error.message); setFilingParcels([]); return; }
    setFilingParcels(data || []);
  }

  // File in place — the EXACT intake write (buildFilingUpdates): UPDATE the existing file_exchange row, no re-upload,
  // no second row. Stamps client_id (from the filing form) + property/parcel scope. task_attachments is untouched, so the file stays attached to the task.
  async function fileAttachment() {
    const fx = filingFor?.file;
    if (!fx?.id || !selectedTask?.id) return;
    if (!filingForm.clientId) { setFilingMsg('⚠ Pick a client.'); return; }
    if (!filingForm.docType)  { setFilingMsg('⚠ Pick a document type.'); return; }
    let filing;
    try {
      filing = buildFilingUpdates({
        vocab,
        docType: filingForm.docType,
        clientId: filingForm.clientId,
        propertyId: filingForm.propertyId || null,
        parcelId: filingForm.parcelId || null,
        year: filingForm.year || null,
        viewableClient: filingForm.viewableClient,
        viewableConsultant: filingForm.viewableConsultant,
        existingPropertyId: fx.property_id || null,
        staffName: staffUser?.name || 'NTRG Staff',
      });
    } catch (e) { setFilingMsg(`⚠ ${e.message}`); return; }   // unknown type / table not loaded — nothing written
    const { updates, folder, docFields, clearParcelDocs } = filing;
    setFilingBusy(true); setFilingMsg('');
    const { error: upErr } = await supabase.from('file_exchange').update(updates).eq('id', fx.id);
    if (upErr) { setFilingMsg(`⚠ Filing failed: ${upErr.message}`); setFilingBusy(false); return; }
    if (updates.parcel_id) {   // the helper's answer, not the form's — null on the client-level path
      const { error: pdErr } = await supabase.from('parcel_documents').upsert({
        parcel_id: updates.parcel_id, file_exchange_id: fx.id, ...docFields,
        year_subfolder: filingForm.year || null, property_id: updates.property_id,
      }, { onConflict: 'file_exchange_id' });
      if (pdErr) console.warn('[fileAttachment] parcel_documents:', pdErr.message);  // row already filed; audit-only → warn
    } else if (clearParcelDocs) {
      // Re-filed as client-level: parcel_documents is a SECOND source of parcel visibility, so nulling
      // parcel_id alone would leave the doc showing on a parcel it no longer belongs to.
      const { error: pdDelErr } = await supabase.from('parcel_documents').delete().eq('file_exchange_id', fx.id);
      if (pdDelErr) console.warn('[fileAttachment] parcel_documents cleanup:', pdDelErr.message);
    }
    await supabase.from('activity_log').insert({
      staff_user: staffUser?.name || 'NTRG Staff', action: 'file', entity_type: 'file',
      entity_name: fx.file_name, client_name: filingForm.clientName || null, property_name: filingForm.propertyName || null,
      details: { doc_type: docFields.file_type, folder, from: 'task_attachment', task_id: selectedTask.id },
    }).then(() => {}, () => {});
    setFilingBusy(false); setFilingMsg('✓ Filed'); setFilingFor(null);
    await loadAttachments(selectedTask.id);   // task_attachments untouched → file still on the task
  }

  // Esc key closes the selected task detail view (UX: Back-to-Tasks shortcut)
  useEffect(() => {
    if (!selectedTask) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        // Don't steal Esc from textareas / inputs / open dialogs
        const tag = (e.target?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        setSelectedTask(null);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedTask]);

  // Auto-refresh the task list whenever the detail view closes. This covers
  // the "client uploaded while I was in a task → task status changed → I go
  // back to the list → list now shows the fresh status" flow. Without this,
  // the list shows stale data until the next manual refresh.
  // Interim fix until full liveness (Supabase realtime) is built.
  const prevSelectedTaskIdRef = React.useRef(null);
  useEffect(() => {
    const prev = prevSelectedTaskIdRef.current;
    const curr = selectedTask?.id || null;
    // Fired when we transition from a task to no-task (Back, Esc, toast auto-nav, etc.)
    if (prev && !curr) {
      loadTasks();
    }
    prevSelectedTaskIdRef.current = curr;
  }, [selectedTask, loadTasks]);

  // Auto-transition: when all items are addressed, flip task to either
  // 'Waiting on Intake' (if any files are still raw/unfiled) or 'Ready to
  // Deliver' (if all files have been processed through Document Intake).
  //
  // "Intaked" = file_exchange rows for this task's upload_token where
  // need_review=false AND filed_at IS NOT NULL.
  //
  // Triggers: item status change OR task opened for view OR intake completed.
  useEffect(() => {
    if (!selectedTask || !docRequest || !docRequestItems.length) return;
    // Don't clobber these terminal-ish states
    if ([TASK_STATUS.COMPLETE, TASK_STATUS.CANCELLED, TASK_STATUS.READY_TO_DELIVER].includes(selectedTask.status)) return;
    // Only auto-transition from active in-progress states
    if (![TASK_STATUS.OPEN, TASK_STATUS.IN_PROGRESS, TASK_STATUS.WAITING_ON_INTAKE, TASK_STATUS.PARTIAL_DELIVERED].includes(selectedTask.status)) return;

    // RACE GUARD: If the request has been approved (upload_token_id is set), we
    // need docRequestToken loaded BEFORE we check intake state. Otherwise the
    // file_exchange query is skipped and status defaults to Ready to Deliver,
    // clobbering Waiting on Intake.
    if (docRequest.upload_token_id && !docRequestToken?.token) return;

    const active = docRequestItems.filter(i => !i.removed_at);
    if (!active.length) return;
    const allAddressed = active.every(i => i.status === 'fulfilled' || i.status === 'na');
    if (!allAddressed) return;

    // All items are accounted for. Now check: are all client-uploaded files for
    // this request intaked? If not, status is Waiting on Intake; if yes, Ready to Deliver.
    (async () => {
      try {
        let targetStatus = TASK_STATUS.READY_TO_DELIVER;
        let transitionReason = 'All items addressed and files intaked — awaiting admin to notify consultant and close.';

        // Query file_exchange for any unintaked files on this upload_token
        if (docRequestToken?.token) {
          const { data: allFiles } = await supabase.from('file_exchange')
            .select('id, need_review, filed_at, split_at')
            .eq('upload_token', docRequestToken.token)
            .eq('direction', 'client_to_ntrg');

          // A split original (split_at) is intaked through its pages — its own filed_at stays null by design.
          const unintaked = (allFiles || []).filter(f =>
            !f.split_at && (f.need_review === true || f.filed_at === null)
          );

          if (unintaked.length > 0) {
            targetStatus = TASK_STATUS.WAITING_ON_INTAKE;
            transitionReason = `All items received. ${unintaked.length} file${unintaked.length > 1 ? 's' : ''} awaiting admin intake before consultant notification.`;
          }
        }

        // If the task is already at the target status, no-op
        if (selectedTask.status === targetStatus) return;

        const { error: atStatusErr } = await supabase.from('tasks').update({
          status: targetStatus,
          updated_at: new Date().toISOString(),
        }).eq('id', selectedTask.id);
        if (atStatusErr) console.warn('[auto-transition] status update failed:', atStatusErr);

        const { error: atActErr } = await supabase.from('task_activity').insert({
          task_id: selectedTask.id,
          staff_id: null,
          staff_name: 'System',
          action: targetStatus === TASK_STATUS.WAITING_ON_INTAKE ? 'auto_waiting_on_intake' : 'auto_ready_to_deliver',
          comment: transitionReason,
        });
        if (atActErr) console.warn('[auto-transition] activity insert failed:', atActErr);

        const sysEntry = {
          id: Date.now().toString(),
          direction: 'system',
          from_name: 'System',
          body: transitionReason,
          label: targetStatus,
          sent_at: new Date().toISOString(),
        };
        const newThread = [...(selectedTask.email_thread || []), sysEntry];
        const { error: atThreadErr } = await supabase.from('tasks').update({ email_thread: newThread }).eq('id', selectedTask.id);
        if (atThreadErr) console.warn('[auto-transition] thread update failed:', atThreadErr);
        setSelectedTask(prev => ({ ...prev, status: targetStatus, email_thread: newThread }));
        loadTasks();
      } catch (e) {
        console.warn('[auto-transition] failed:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docRequestItems, selectedTask?.status, docRequestToken?.token]);

  // Intake status check: also fires when the task is loaded/re-loaded.
  // This catches the case where admin returns to a task after intaking files
  // in Document Intake (files are now filed, task should flip to Ready to Deliver).
  // State to drive the "Waiting on Intake" UI callout.
  const [unintakedFileCount, setUnintakedFileCount] = useState(0);

  useEffect(() => {
    if (!selectedTask || !docRequestToken?.token) { setUnintakedFileCount(0); return; }
    let cancelled = false;
    (async () => {
      const { data: allFiles } = await supabase.from('file_exchange')
        .select('id, need_review, filed_at, split_at')
        .eq('upload_token', docRequestToken.token)
        .eq('direction', 'client_to_ntrg');
      if (cancelled) return;
      const unintaked = (allFiles || []).filter(f =>
        !f.split_at && (f.need_review === true || f.filed_at === null)   // a split original counts through its pages
      );
      setUnintakedFileCount(unintaked.length);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTask?.id, docRequestToken?.token, docRequestItems]);

  async function loadClientProperties(clientId, clientName) {
    if (!clientId && !clientName) { setClientProperties([]); return; }
    setLoadingProps(true);
    // Try by client_id first
    const { data: props } = await supabase.from('properties')
      .select('id, name, city, state, master_property_id')
      .eq('client_id', clientId)
      .order('name');
    if (props && props.length > 0) {
      setClientProperties(props);
    } else {
      // fallback: generated id from name
      const genId = clientName.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'').slice(0,30);
      const { data: props2 } = await supabase.from('properties')
        .select('id, name, city, state, master_property_id')
        .eq('client_id', genId)
        .order('name');
      setClientProperties(props2 || []);
    }
    setLoadingProps(false);
  }

  // Exact (case-insensitive) client-name match → the real record; no/ambiguous match → null (never guess; leave blank + picker).
  const matchClient = (name) => {
    if (!name) return null;
    const n = String(name).trim().toLowerCase();
    return (allClients || []).find(cl => cl.name?.trim().toLowerCase() === n) || null;
  };
  // Exact (case-insensitive) staff-name match → the real record; ambiguous/partial/none → null (never guess the assignee).
  const matchStaffByName = (name) => {
    if (!name) return null;
    const n = String(name).trim().toLowerCase();
    return (staffList || []).find(s => s.name?.trim().toLowerCase() === n) || null;
  };

  // ⛔ TEXT IS AN ARGUMENT, not read from state. setEmailPaste is asynchronous, so the drop handler
  // — which parses a file and immediately extracts from it — would otherwise send the PREVIOUS
  // contents of the box, or an empty string on the first drop. Defaults to the state for the
  // unchanged paste path.
  async function extractFromEmail(textArg) {
    const text = typeof textArg === 'string' ? textArg : emailPaste;
    if (!text.trim() || extracting) return;
    setExtracting(true);
    try {
      const res = await fetch('/api/extract-tasks', { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify({ text, staff: (staffList||[]).map(s => s.name).filter(Boolean) }) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { flash(json.error || `Extract failed (HTTP ${res.status})`, true); setExtracting(false); return; }
      const tasks = Array.isArray(json.tasks) ? json.tasks : [];
      if (tasks.length === 0) { flash('No tasks found in this email — nothing was created', true); setExtractedTasks([]); setExtracting(false); return; }
      // Map each extracted task → an editable review-panel draft (client resolved, assignee defaulting to me).
      setExtractedTasks(tasks.map((t, i) => {
        const m = matchClient(t.client_name);
        const sa = matchStaffByName(t.suggested_assignee);   // exact match or null — never guess the assignee
        return {
          _key: `${Date.now()}-${i}`, include: true,
          title: t.title || '', task_type: 'internal', priority: t.priority || 'Normal',   // default all extracted → Internal for now (client/property data not fully loaded); editable in the panel
          due_date: t.due_date || '', description: t.description || '',
          client_id: m?.id || '', client_name: m?.name || t.client_name || '', property_name: t.property_name || '',
          assigned_to: sa ? (sa.id || sa.staff_id) : (staffUser?.id || ''),
          assigned_to_name: sa ? sa.name : (staffUser?.name || ''),
          _suggestedFromEmail: !!sa,   // provenance: this assignee came from the email (a fuzzy suggestion) — flag it in the panel
        };
      }));
    } catch (e) {
      flash('Error reaching the extractor — please try again.', true);
    }
    setExtracting(false);
  }

  // ── Drop an email from Outlook ────────────────────────────────────────────────────────────────
  // Parse in the browser, put the plain text in the extractor box, and run the SAME extraction the
  // paste path runs. Nothing about the pipeline below this point changes.
  //
  // ⛔ EVERY FAILURE IS LOUD AND TOTAL. A file that will not parse clears the box and shows why —
  // it never leaves a half-filled form that looks ready to extract from.
  // ── Take one or more saved email files, however they arrived ─────────────────────────────────
  //
  // ⛔ SAVE-THEN-DROP IS THE SUPPORTED ROUTE, not dragging out of Outlook. An Outlook drag offers a
  // VIRTUAL file the browser has to materialise, and it does so inconsistently — one worked once in
  // testing and could not be reproduced. A file saved to disk and then dropped, or picked with the
  // browse button, is an ordinary file drag that works every time on every platform. The zone still
  // accepts a direct Outlook drag because it costs nothing when it happens to work; it is simply no
  // longer what we tell people to do.
  //
  // MULTIPLE FILES: each is parsed and the FIRST usable one drives the extraction, with the rest
  // contributing their attachments. Save-then-drop makes batching natural, so silently ignoring
  // files 2..n would lose work the user believed they had handed over.
  async function ingestEmailFiles(files) {
    const list = Array.from(files || []);
    if (!list.length || dropBusy || extracting) return;
    setDropError(null); setDroppedEmail(null); setExtractedTasks([]);
    setPendingAttachments([]); setAttachNote(null);
    setDropBusy(true);
    setEmailPaste('');
    try {
      let primary = null;
      let text = null;
      const atts = [];
      for (const f of list) {
        const msg = await parseDroppedEmail(f);        // throws loudly; one bad file fails the batch
        const t = plainTextFrom(msg);
        if (!primary && t) { primary = msg; text = t; }
        atts.push(...(msg.attachments || []));
      }
      if (!primary) throw new EmailParseError('No readable text in those files, so there is nothing to extract.', { code: 'no_text' });
      setDroppedEmail(primary);
      setPendingAttachments(atts);
      setEmailPaste(text);
      setDropBusy(false);
      await extractFromEmail(text);          // text passed, not read back out of state
      return;
    } catch (err) {
      setDropError(err instanceof EmailParseError ? err.message : `Couldn't read that file: ${err.message}`);
      setDroppedEmail(null); setEmailPaste(''); setPendingAttachments([]);
    }
    setDropBusy(false);
  }

  // ── Attach files to a task BEFORE it exists ───────────────────────────────────────────────────
  //
  // ⛔ THE PATH THAT IS TRUE REGARDLESS OF OUTLOOK. Every other route here depends on a platform
  // behaviour we do not control — whether a drag hands over a file, whether the clipboard carries
  // image bytes, whether Save As is available. Choosing a file does not. It works in every browser,
  // on every OS, and on a phone it opens Photos and the camera.
  //
  // These join the SAME pendingAttachments list an email's attachments go into, so they stage,
  // dedupe, roll back and link through one path. A picked file is never a second mechanism.
  async function addPendingFiles(files) {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    try {
      const added = [];
      for (const f of list) added.push(await fileToAttachment(f));
      setPendingAttachments(prev => [...prev, ...added]);
      setDropError(null);
      const n = added.length;
      setAttachNote(`${n} file${n === 1 ? '' : 's'} ready to attach${added.some(a => /^image\//i.test(a.mimeType)) ? ' (images included)' : ''}.`);
    } catch (err) {
      setDropError(`Couldn't read that file: ${err.message}`);
    }
  }

  // ⛔ Removal is by INDEX, not by name. Two files can legitimately share a name — that is the whole
  // reason staging de-collides them — so matching on name would remove the wrong one.
  const removePendingAt = (i) => setPendingAttachments(prev => prev.filter((_, idx) => idx !== i));

  // Paste straight into the attachment box. Copying an IMAGE on its own puts real bytes on the
  // clipboard, unlike copying a message body — so this works where paste-a-body cannot.
  async function handleAttachPaste(e) {
    const cb = readClipboard(e.clipboardData);
    if (!cb.imageFiles.length) return;          // no image: let the event do whatever it would
    e.preventDefault();
    const captured = await readClipboardImages({ imageFiles: cb.imageFiles, html: null });
    if (!captured.length) return;
    // Pasted images arrive nameless and are documents in their own right here, not body decoration.
    setPendingAttachments(prev => [...prev, ...captured.map(a => ({ ...a, disposition: 'attachment' }))]);
    setDropError(null);
    setAttachNote(`${captured.length} pasted image${captured.length === 1 ? '' : 's'} ready to attach.`);
  }

  async function handleEmailDrop(e) {
    e.preventDefault();
    setDropActive(false);
    if (dropBusy || extracting) return;
    // ⛔ Read dataTransfer SYNCHRONOUSLY — it is emptied the moment this handler yields.
    const files = e.dataTransfer ? Array.from(e.dataTransfer.files || []) : [];
    const typesSeen = e.dataTransfer ? Array.from(e.dataTransfer.types || []) : [];
    if (!files.length) {
      setDropError(
        `That drop carried no file (your email program offered: ${typesSeen.join(', ') || 'nothing'}). `
        + 'Dragging straight out of Outlook does not reliably hand over a file. Save the email first '
        + '(File → Save As), then drop the saved file here — or paste the text below.'
      );
      return;
    }
    await ingestEmailFiles(files);
  }

  // ── Paste ─────────────────────────────────────────────────────────────────────────────────────
  //
  // ⛔ THE ORIGINAL COMPLAINT, and it was solvable here all along: "the images don't come through
  // when we paste". There was no paste handler in this app at all — pasting went into a plain
  // textarea, which receives text and discards everything else before any of our code runs.
  //
  // What this cannot fix: when Outlook on Windows copies a message BODY it references images as
  // local temp files (file:///…/msohtmlclip1/…), and a browser cannot read those. The bytes are not
  // on the clipboard, so nothing can recover them. readClipboard COUNTS those and says why, so a
  // missing picture is a stated fact rather than a silent disappearance.
  async function handleEmailPaste(e) {
    // ⛔ Synchronous read, same reason as dataTransfer.
    const cb = readClipboard(e.clipboardData);
    if (!cb.imageFiles.length && !/<img\b/i.test(cb.html || '')) return;   // plain text: leave the textarea alone
    e.preventDefault();

    const text = cb.text || plainTextFrom({ textBody: null, htmlBody: cb.html });
    const captured = await readClipboardImages(cb);

    setEmailPaste(text || '');
    setDroppedEmail(null);
    setPendingAttachments(captured);
    setDropError(null);

    const bits = [];
    if (captured.length) bits.push(`${captured.length} image${captured.length === 1 ? '' : 's'} captured from the clipboard`);
    if (cb.unreadable.length) {
      // Grouped by reason: fifty identical "local file" notices help nobody.
      const why = [...new Set(cb.unreadable.map((u) => u.why))].join('; ');
      bits.push(`⛔ ${cb.unreadable.length} image${cb.unreadable.length === 1 ? '' : 's'} could NOT be captured — ${why}. Save the email and drop the file to get those.`);
    }
    if (!captured.length && !cb.unreadable.length) bits.push('No images on the clipboard.');
    setAttachNote(bits.join(' · '));
  }

  const setDraft = (key, patch) => setExtractedTasks(prev => prev.map(t => t._key === key ? { ...t, ...patch } : t));

  // Create the checked tasks. INDIVIDUAL inserts so we can report WHICH failed (a batch insert is all-or-nothing).
  // Partial success: keep the failed cards in the panel to retry, tell the user which ones. Never a silent partial.
  async function createBatch() {
    if (creatingBatch) return;
    const toCreate = extractedTasks.filter(t => t.include && t.title.trim());
    if (!toCreate.length) { flash('Check at least one task with a title first.', true); return; }
    setCreatingBatch(true);
    try {

    // ⛔ ATTACHMENTS LAND FIRST, AND NOTHING IS CREATED IF ANY OF THEM FAILS. A task built from an
    // email whose images never uploaded looks complete and is not — the failure this whole build
    // exists to prevent. stageEmailAttachments is all-or-nothing and rolls its own uploads back, so
    // a failure here leaves the database exactly as it was and the review panel still populated for
    // a retry.
    let staged = [];
    if (pendingAttachments.length) {
      setAttachNote('Uploading attachments…');
      const res = await stageEmailAttachments(pendingAttachments, { uploadedByName: staffUser?.name, context: droppedEmail?.subject });
      if (res.error) {
        setCreatingBatch(false);
        setAttachNote(null);
        flash(`⛔ ${res.error.message} No tasks were created.`, true);
        return;
      }
      staged = res.staged;
      setAttachNote(stageSummary(res));
    }

    const failed = []; let created = 0;
    const createdIds = [];
    for (const t of toCreate) {
      const payload = {
        title: t.title.trim(), task_type: t.task_type, priority: t.priority, status: 'Open',
        description: t.description || '', due_date: t.due_date || null,
        client_id: t.client_id || '', client_name: t.client_name || '', property_name: t.property_name || '',
        assigned_to: t.assigned_to || staffUser?.id, assigned_to_name: t.assigned_to_name || staffUser?.name,
        assigned_by: staffUser?.id || null, assigned_by_name: staffUser?.name || null, is_private: false,
        // ⛔ source_email_from is written for the FIRST time here. TaskEngine.js renders
        // "From: {source_email_from}" in the task detail and nothing has ever populated it, so that
        // line has never appeared. A dropped email finally has a sender to put in it; a pasted one
        // still has none, and null keeps the line hidden rather than printing an empty "From:".
        //
        // ⛔ source_email_date was `new Date()` — the date the TASK was created, not the date the
        // EMAIL was sent, which is the only thing that field could usefully mean. A dropped email
        // carries the real sent date; paste has no date to offer and falls back to today, as before.
        source: droppedEmail ? 'email_drop' : 'email_paste',
        source_email: emailPaste,
        source_email_from: droppedEmail ? formatSender(droppedEmail.from) : null,
        source_email_date: droppedEmail && droppedEmail.sentAt
          ? new Date(droppedEmail.sentAt).toLocaleDateString()
          : new Date().toLocaleDateString(),
        email_thread: [],
      };
      const { data: row, error } = await supabase.from('tasks').insert(payload).select('id').single();
      if (error || !row) failed.push({ key: t._key, title: t.title, msg: error?.message || 'no row returned' });
      else { created++; createdIds.push(row.id); }
    }

    // ⛔ EVERY created task gets the SAME files. The email is the provenance of all of them, so an
    // attachment that belonged to only one of three extracted tasks is not a distinction the email
    // gives us — and hiding a file from two of the three would lose it for whoever opens those.
    // task_attachments is a join, so this shares one stored object rather than copying bytes.
    if (staged.length && createdIds.length) {
      const linkFailures = [];
      for (const id of createdIds) {
        const { error: linkErr } = await linkStagedToTask(staged, id, staffUser?.id);
        if (linkErr) linkFailures.push(linkErr.message);
      }
      if (linkFailures.length) {
        // The tasks exist and the files exist; only the join failed. Do NOT roll back created tasks
        // — say exactly what is missing so it can be attached by hand.
        flash(`Tasks created, but ${linkFailures.length} attachment link${linkFailures.length === 1 ? '' : 's'} failed: ${linkFailures[0]}. The files are in Task Attachments; attach them by hand.`, true);
      }
    } else if (staged.length && !createdIds.length) {
      // No task was created at all, so the staged files have nothing to hang off. Remove them
      // rather than leaving unreachable objects behind.
      await rollbackStaged(staged);
    }

    loadTasks();
    if (failed.length === 0) {
      flash(`Created ${created} task${created !== 1 ? 's' : ''}`);
      setExtractedTasks([]); setEmailPaste(''); setDroppedEmail(null); setDropError(null); setPendingAttachments([]); setAttachNote(null); setShowNewTask(false);
    } else {
      const keep = new Set(failed.map(f => f.key));
      setExtractedTasks(prev => prev.filter(t => keep.has(t._key)));   // leave only the failures for retry
      flash(`Created ${created} of ${toCreate.length}. Failed: ${failed.map(f => `"${f.title}"`).join(', ')} — ${failed[0].msg}`, true);
    }
    } catch (e) {
      // Same swallowed-exception hazard as saveTask — a thrown fetch here would otherwise leave the
      // Create button spinning with no explanation. Attachment staging rolls itself back on failure,
      // so what the user is told is accurate: some tasks may exist, and it names how many.
      console.error('[TaskEngine] createBatch threw:', e);
      flash(`Something went wrong creating the tasks — ${e.message || e}. Check your connection; anything already created is in the list.`, true);
      loadTasks();
    } finally {
      setCreatingBatch(false);
    }
  }

  // keepForSeries: "Save & add for another" — save this copy, keep the form open (change the assignee, save the next).
  // All saves in a series share one group_id (minted on the first series save) so the copies are a linked group.
  async function saveTask(keepForSeries = false) {
    // ⛔ A BUTTON THAT DOES NOTHING IS READ AS A BROKEN APP. This returned silently on an empty
    // title — click Save, nothing happens, no message, no reason. Say what is missing.
    if (!newTask.title || !newTask.title.trim()) { flash('Give the task a title before saving.', true); return; }
    if (savingTask) return;                       // in-flight; the button is disabled too
    setSavingTask(true);
    try {
    let gid = seriesGroupId;
    if (keepForSeries && !gid) { gid = crypto.randomUUID(); setSeriesGroupId(gid); }
    const payload = {
      ...newTask,
      assigned_to: newTask.assigned_to || staffUser?.id,
      assigned_to_name: newTask.assigned_to_name || staffUser?.name,
      assigned_by: staffUser?.id || null,
      assigned_by_name: staffUser?.name || null,
      is_private: !!newTask.is_private,
      created_by: staffUser?.name,
      email_thread: [],
      group_id: gid || null,   // set when this save is part of a "Save & add for another" series
    };
    // .select().single() so we get the created row back and can open its detail (attachment block is right there) —
    // kills the "create, reopen, then attach" friction. The detail's ← Back to Tasks (+ Esc) returns to the list.
    // ⛔ SAME ORDERING AS createBatch: files land BEFORE the task exists, and if any of them fails
    // the task is not created at all. A task showing no attachment because an upload silently
    // failed is indistinguishable from a task that never had one.
    let stagedSingle = [];
    if (pendingAttachments.length) {
      const res = await stageEmailAttachments(pendingAttachments, {
        uploadedByName: staffUser?.name, context: droppedEmail?.subject || newTask.title,
      });
      if (res.error) { flash(`⛔ ${res.error.message} The task was not created.`, true); return; }
      stagedSingle = res.staged;
    }

    const { data: created, error } = await supabase.from('tasks').insert(payload).select().single();
    // Surface ANY insert failure (constraint, network, RLS, bad column) — never a silent no-op (cf. the notes Add bug).
    if (error) {
      await rollbackStaged(stagedSingle);      // no task to hang them off — do not leave orphans
      flash(`Save failed: ${error.message || error}`, true); return;
    }
    if (stagedSingle.length && created?.id) {
      const { error: linkErr } = await linkStagedToTask(stagedSingle, created.id, staffUser?.id);
      if (linkErr) flash(`Task saved, but attaching the files failed: ${linkErr.message}. They are in Task Attachments.`, true);
    }
    if (keepForSeries) {   // keep the form + values so you just change the assignee and save the next copy
      flash('Saved — change the assignee and add the next');
      loadTasks();
      return;
    }
    flash('Task created');
    setShowNewTask(false);
    setEmailPaste('');
    setNewTask({ title:'', task_type:'internal', priority:'Normal', status:'Open', client_id:'', client_name:'', property_id:'', property_name:'', due_date:'', description:'', assigned_to:'', assigned_to_name:'', source:'manual', source_email:'', is_private:false });
    setSeriesGroupId(null);
    loadTasks();
    if (created) setSelectedTask(created);   // open the new task's detail (single-save path only; the extractor batch stays on the list)
    } catch (e) {
      // ⛔ THE SILENT FAILURE THIS EXISTS FOR. supabase-js usually RETURNS { error } on a failed
      // request, and every one of those is handled above — but a dropped connection can also make
      // the underlying fetch THROW (DNS, aborted request, an auth refresh failing mid-call). An
      // exception in an async click handler is swallowed by React: no error, no message, no task,
      // and a button that looks like it did nothing. Anyone hitting that concludes Wigley is
      // broken rather than that their network dropped.
      console.error('[TaskEngine] saveTask threw:', e);
      flash(`Couldn't save — ${e.message || e}. Nothing was created; check your connection and try again.`, true);
    } finally {
      setSavingTask(false);   // in finally so a throw can never leave the button stuck disabled
    }
  }

  async function saveNote() {
    if (!noteText || !selectedTask) return;
    setSavingNote(true);
    const note = {
      id: Date.now().toString(),
      direction: 'note',
      from_name: staffUser?.name,
      body: noteText,
      sent_at: new Date().toISOString(),
    };
    const currentThread = selectedTask.email_thread || [];
    const updatedThread = [...currentThread, note];
    const { error: nErr } = await supabase.from('tasks').update({ email_thread: updatedThread, updated_at: new Date().toISOString() }).eq('id', selectedTask.id);
    if (nErr) { flash(`Couldn't save note: ${nErr.message || nErr}`, true); setSavingNote(false); return; }   // note write failed → surface, don't fake success
    const { error: aErr } = await supabase.from('task_activity').insert({ task_id: selectedTask.id, staff_id: staffUser?.id, staff_name: staffUser?.name, action:'comment', comment: noteText.slice(0,200) });
    if (aErr) console.warn('[saveNote] activity insert failed:', aErr);   // note already saved; audit-only → warn, don't block
    // Notify the task's PRINCIPALS — the assigner AND the assignee — minus whoever wrote the note. So a note from a
    // principal reaches the other, and a note from a THIRD PARTY (someone commenting via All Tasks, or an active
    // coverer) reaches BOTH principals — no more silent third-party comments.
    //
    // ⛔ CONFIDENTIAL INVARIANT — DO NOT BREAK. Recipients MUST always be a subset of {assigned_by, assigned_to},
    // i.e. the confidential circle. Never add anyone else to this set (e.g. past commenters, watchers, the coverer).
    // Reason: the bell's deep-link opens a task by id and BYPASSES the list mask, so the ONLY thing keeping a
    // confidential task sealed is that a non-circle person never RECEIVES a notification for it. Widen this recipient
    // set and you leak confidential tasks to whoever gets the notification. (The body also carries no note text or
    // title for a confidential task — the notifications table is RLS-off.)
    const author = staffUser?.id;
    const recipients = [...new Set([selectedTask.assigned_by, selectedTask.assigned_to])].filter(id => id && id !== author);
    if (recipients.length) {
      const body = selectedTask.is_private
        ? 'New note on a confidential task'
        : `New note on "${(selectedTask.title || 'a task').slice(0, 50)}": ${noteText.trim().slice(0, 80)}`;
      const rows = recipients.map(rid => ({ recipient_id: rid, type: 'task_note', entity_type: 'task', entity_id: selectedTask.id, body }));
      const { error: ntErr } = await supabase.from('notifications').insert(rows);
      if (ntErr) console.warn('[saveNote] notification insert failed:', ntErr.message);   // note already saved; don't block
    }
    setSelectedTask(prev => ({ ...prev, email_thread: updatedThread }));
    setNoteText('');
    setSavingNote(false);
    flash('Note added');
  }

  // Cancelled requires a reason (collected via the inline field, passed here). Complete stamps completer.
  async function updateTaskStatus(taskId, status, cancelReason) {
    let extra = {};
    if (status === 'Complete') extra = { completed_at: new Date().toISOString(), completed_by: staffUser?.name, completed_by_id: staffUser?.id || null };
    if (status === 'Cancelled') {
      if (!cancelReason || !cancelReason.trim()) return;   // guard — the inline field requires content before confirm
      extra = { cancellation_reason: cancelReason.trim() };
    }
    // Trail — the most common action left NO record before this. Append a system event to email_thread (shows in
    // Activity, like reassign/edits) and write a structured task_activity row. Fold the thread into the same update.
    const src = (selectedTask && selectedTask.id === taskId) ? selectedTask : null;
    const oldStatus = src?.status || '—';
    const body = status === 'Cancelled' ? `Status: ${oldStatus} → Cancelled — ${cancelReason.trim()}` : `Status: ${oldStatus} → ${status}`;
    const patch = { status, updated_at: new Date().toISOString(), ...extra };
    if (src) patch.email_thread = [ ...(src.email_thread || []), { id: `${Date.now()}`, direction: 'system', from_name: staffUser?.name || 'System', body, label: 'Status', sent_at: new Date().toISOString() } ];
    // Core completion path — MUST NOT lie. If the write fails, surface it and do NOT move the task or update local state.
    const { error } = await supabase.from('tasks').update(patch).eq('id', taskId);
    if (error) { flash(`Couldn't update status: ${error.message || error}`, true); return; }
    const { error: aErr } = await supabase.from('task_activity').insert({ task_id: taskId, staff_id: staffUser?.id, staff_name: staffUser?.name || 'System', action: 'status', old_value: oldStatus, new_value: status });
    if (aErr) console.warn('[updateTaskStatus] activity insert failed:', aErr);   // status already saved; audit-only → warn
    // Subtask write-through (Build 5): if this task was promoted from a parent, reflect its completion back into the
    // parent's checklist line (done = Complete). Ticks/unticks the line only — NEVER auto-completes the parent. src is
    // reliably the row being changed (both callers pass selectedTask.id), so src.parent_task_id is present.
    if (src?.parent_task_id) await syncParentSubtaskLine(src.parent_task_id, taskId, su => ({ ...su, done: status === 'Complete' }));
    loadTasks();
    if (selectedTask?.id === taskId) setSelectedTask(prev => ({ ...prev, ...patch }));
  }

  // Soft delete — assigner or admin (self-assigned qualifies: assigned_by === me). On a CONFIDENTIAL task, admins are
  // excluded (they can't see it, and shouldn't delete what they can't see) — assigner only. Reversible (deleted_at);
  // the row + activity/thread/attachments/coverage all stay, just filtered from reads.
  async function deleteTask() {
    if (!selectedTask) return;
    const canDelete = selectedTask.is_private
      ? staffUser?.id === selectedTask.assigned_by
      : (staffUser?.id === selectedTask.assigned_by || isAdmin);
    if (!canDelete) { flash(selectedTask.is_private ? "Only the person who assigned this confidential task can delete it." : "Only the person who assigned this task (or an admin) can delete it.", true); return; }
    if (!window.confirm("Delete this task? It won't appear anywhere but is kept for audit.")) return;
    const { error } = await supabase.from('tasks').update({ deleted_at: new Date().toISOString(), deleted_by: staffUser?.id || null, updated_at: new Date().toISOString() }).eq('id', selectedTask.id);
    if (error) { flash(`Couldn't delete: ${error.message || error}`, true); return; }
    flash('Task deleted (kept for audit)');
    setSelectedTask(null);
    loadTasks();
  }

  // Toggle a task's confidentiality from the detail view. Only the ASSIGNER sees this control (the person who set the
  // boundary owns it) — surface any error rather than swallow it. The DB CHECK only needs assigned_by non-null, always true here.
  async function togglePrivate(next) {
    if (!selectedTask) return;
    const { error } = await supabase.from('tasks').update({ is_private: next, updated_at: new Date().toISOString() }).eq('id', selectedTask.id);
    if (error) { setDetailMsg(`Couldn't update privacy: ${error.message || error}`); setTimeout(() => setDetailMsg(''), 6000); return; }
    setDetailMsg('');
    setSelectedTask(prev => ({ ...prev, is_private: next }));
    loadTasks();
  }

  // ── The completed/cancelled ("terminal") set for the CURRENT tab+filter. Open lists load open-only (cap-safe);
  // completed comes from here — a paged query with a TRUE count + "Load older" — so it never truncates. Scope mirrors
  // each tab's open list: My = my tasks, Assigned = tasks I delegated, All = the team (or the roster-selected person).
  const buildTerminalQuery = (withCount) => {
    let q = supabase.from('tasks').select('*', withCount ? { count: 'exact' } : undefined)
      .is('deleted_at', null).in('status', ['Complete', 'Cancelled']);
    if (activeTab === 'assigned') q = q.eq('assigned_by', staffUser?.id).neq('assigned_to', staffUser?.id);
    else if (activeTab === 'all') { if (filterAssignee) q = q.eq('assigned_to', filterAssignee); }
    else q = q.eq('assigned_to', staffUser?.id);   // 'my'
    return q.order('updated_at', { ascending: false });
  };
  const loadTerminal = useCallback(async () => {
    if (!staffUser?.id) return;
    const { data, error, count } = await buildTerminalQuery(true).range(0, TERMINAL_PAGE - 1);
    if (error) { console.warn('[loadTerminal] read failed:', error.message); return; }
    setTerminal(data || []); setTerminalTotal(count ?? (data || []).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staffUser?.id, activeTab, filterAssignee]);
  useEffect(() => { loadTerminal(); }, [loadTerminal, taskVersion]);   // scope change OR a task mutation (taskVersion) → refresh

  // "Load older" → next page of the current scope's terminal set, appended. Makes ANY completed task reachable
  // (one months old is beyond the first page but still pageable), rather than silently capped.
  async function loadMoreTerminal() {
    if (!staffUser?.id) return;
    const { data, error } = await buildTerminalQuery(false).range(terminal.length, terminal.length + TERMINAL_PAGE - 1);
    if (error) { console.warn('[loadMoreTerminal] read failed:', error); return; }
    setTerminal(prev => [...prev, ...(data || [])]);
  }

  // Save staged edits (title / due date / priority) — anyone accountable (assignee / assigner / coverer). Each changed
  // field appends a 'system' event to email_thread (so it shows in Activity & Notes, like reassign) AND writes a
  // structured task_activity row (audit). One checked tasks UPDATE (fields + thread folded together); reload after.
  async function saveEdits() {
    if (!selectedTask) return;
    const changes = [];
    const patch = { updated_at: new Date().toISOString() };
    const newTitle = (editDraft.title || '').trim();
    if (!newTitle) { setEditMsg('Title can’t be empty.'); return; }
    if (newTitle !== (selectedTask.title || '')) { patch.title = newTitle; changes.push({ action:'edited_title', label:'title', old: selectedTask.title || '—', new: newTitle }); }
    const newDue = editDraft.due_date || null;
    if (newDue !== (selectedTask.due_date || null)) { patch.due_date = newDue; changes.push({ action:'edited_due', label:'due date', old: selectedTask.due_date || '—', new: newDue || '—' }); }
    if (editDraft.priority !== selectedTask.priority) { patch.priority = editDraft.priority; changes.push({ action:'edited_priority', label:'priority', old: selectedTask.priority || '—', new: editDraft.priority }); }
    if (changes.length === 0) { setEditMode(false); setEditMsg(''); return; }   // nothing changed → just close

    setSavingEdit(true);
    const now = new Date().toISOString();
    const sysEvents = changes.map((ch, i) => ({ id: `${Date.now() + i}`, direction:'system', from_name: staffUser?.name || 'System',
      body: `Changed ${ch.label}: ${ch.old} → ${ch.new}`, label:'Edited', sent_at: now }));
    patch.email_thread = [ ...(selectedTask.email_thread || []), ...sysEvents ];   // folded into the same write — no "edited but no trail" window
    const { error } = await supabase.from('tasks').update(patch).eq('id', selectedTask.id);
    if (error) { setEditMsg(`Couldn’t save: ${error.message || error}`); setSavingEdit(false); return; }
    for (const ch of changes) {   // structured audit rows — warn (don't block) since the edit itself already saved
      const { error: aErr } = await supabase.from('task_activity').insert({ task_id: selectedTask.id, staff_id: staffUser?.id, staff_name: staffUser?.name || 'System', action: ch.action, old_value: String(ch.old), new_value: String(ch.new) });
      if (aErr) console.warn('[saveEdits] activity insert failed:', aErr);
    }
    // Child→parent subtask sync: if this is a promoted subtask and its TITLE changed, update the parent's checklist line.
    if (patch.title && selectedTask.parent_task_id) await syncParentSubtaskLine(selectedTask.parent_task_id, selectedTask.id, su => ({ ...su, text: patch.title }));
    // Notify on MATERIAL edits only — due date / priority (a title rename is cosmetic → skip). One notification per save,
    // summarizing the material changes. SAME recipient rule + CONFIDENTIAL INVARIANT as notes: recipients are ALWAYS a
    // subset of {assigned_by, assigned_to} (never widen — the bell deep-link bypasses the list mask), and a confidential
    // task's body carries no detail.
    const material = changes.filter(ch => ch.action === 'edited_due' || ch.action === 'edited_priority');
    if (material.length) {
      const author = staffUser?.id;
      const recipients = [...new Set([selectedTask.assigned_by, selectedTask.assigned_to])].filter(id => id && id !== author);
      if (recipients.length) {
        const summary = material.map(ch => `${ch.label} → ${ch.new}`).join('; ');
        const body = selectedTask.is_private
          ? 'A confidential task you’re on was updated'
          : `Updated on "${(patch.title || selectedTask.title || 'a task').slice(0, 50)}": ${summary}`;
        const rows = recipients.map(rid => ({ recipient_id: rid, type: 'task_edit', entity_type: 'task', entity_id: selectedTask.id, body }));
        const { error: ntErr } = await supabase.from('notifications').insert(rows);
        if (ntErr) console.warn('[saveEdits] notification insert failed:', ntErr.message);   // edit already saved; don't block
      }
    }
    setSavingEdit(false); setEditMode(false); setEditMsg('');
    setSelectedTask(prev => ({ ...prev, ...patch }));
    loadTasks();
  }

  // Patch one task's subtasks across EVERY in-memory list that can render or reopen it, so the row badge and a
  // navigate-away-then-reopen reflect the change immediately — without a full loadTasks (the deliberate no-churn choice
  // for rapid ticking). Cheap in-memory maps, no refetch. This is the single place that keeps the lists consistent.
  const patchSubtasksInLists = (taskId, next) => {
    const patch = arr => arr.map(t => t.id === taskId ? { ...t, subtasks: next } : t);
    setTasks(patch); setAllTasks(patch); setAssignedTasks(patch);
    setGroupSiblings(patch); setTerminal(patch); setSearchTerminal(patch);
  };
  // Subtasks — a simple checklist stored in tasks.subtasks (JSONB array of {id,text,done}). Ticks/adds/removes write
  // immediately (checklist UX), checked. Single choke point for add/tick/remove/promote: updates selectedTask AND the
  // lists (via patchSubtasksInLists) so a stale reopen / stale row badge can't happen; still no loadTasks (subtasks
  // don't affect KPI/sort, and rapid ticking shouldn't churn the whole list).
  async function writeSubtasks(next) {
    if (!selectedTask) return false;
    const taskId = selectedTask.id;
    const { error } = await supabase.from('tasks').update({ subtasks: next, updated_at: new Date().toISOString() }).eq('id', taskId);
    if (error) { setDetailMsg(`Couldn't save subtasks: ${error.message || error}`); setTimeout(() => setDetailMsg(''), 6000); return false; }
    setSelectedTask(prev => (prev && prev.id === taskId ? { ...prev, subtasks: next } : prev));
    patchSubtasksInLists(taskId, next);
    return true;
  }
  // Sync a promoted subtask's PARENT checklist line from its CHILD task (Build 5 follow-up). Used when the child is
  // renamed (→ line text), reassigned (→ child_assignee_name), or completed (→ done). Fetches the parent, patches the
  // line whose child_task_id === childId via mutate(item), writes it back (checked), and refreshes the in-memory lists
  // so the parent's line updates without a reopen. Only child→parent (there is no parent-line edit UI by design).
  async function syncParentSubtaskLine(parentId, childId, mutate) {
    if (!parentId || !childId) return;
    const { data: p, error } = await supabase.from('tasks').select('id, subtasks').eq('id', parentId).maybeSingle();
    if (error) { console.warn('[syncParentSubtaskLine] parent fetch failed:', error); return; }
    if (!p || !Array.isArray(p.subtasks)) return;
    const nextSubs = p.subtasks.map(su => su.child_task_id === childId ? mutate(su) : su);
    if (JSON.stringify(nextSubs) === JSON.stringify(p.subtasks)) return;   // nothing changed
    const { error: wErr } = await supabase.from('tasks').update({ subtasks: nextSubs, updated_at: new Date().toISOString() }).eq('id', parentId);
    if (wErr) { console.warn('[syncParentSubtaskLine] write failed:', wErr); return; }
    patchSubtasksInLists(parentId, nextSubs);
    if (selectedTask?.id === parentId) setSelectedTask(prev => (prev && prev.id === parentId ? { ...prev, subtasks: nextSubs } : prev));
  }
  async function addSubtask() {
    const text = newSubtask.trim();
    if (!text || !selectedTask) return;
    const subs = Array.isArray(selectedTask.subtasks) ? selectedTask.subtasks : [];
    const ok = await writeSubtasks([...subs, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, text, done: false }]);
    if (ok) setNewSubtask('');
  }
  function toggleSubtask(id) {
    const subs = Array.isArray(selectedTask?.subtasks) ? selectedTask.subtasks : [];
    writeSubtasks(subs.map(s => s.id === id ? { ...s, done: !s.done } : s));
  }
  function removeSubtask(id) {
    const subs = Array.isArray(selectedTask?.subtasks) ? selectedTask.subtasks : [];
    writeSubtasks(subs.filter(s => s.id !== id));
  }
  // Promote a checklist item → a REAL child task with its own owner (Build 5). The parent KEEPS the line: we link it by
  // writing child_task_id into the JSONB, so the breakdown never loses it. The line's checkbox is then driven by the
  // child's completion (see updateTaskStatus write-through), not ticked by hand. No auto-complete of the parent.
  // CONFIDENTIAL: the child INHERITS is_private from the parent (safer default) — and because is_private + the child's
  // OWN assigned_by/assigned_to define the circle, that means "confidential between the child's two people
  // {promoter, assignee}", NOT "the parent's circle carries over". Gated to canMutateSelected (the parent's circle), so
  // only someone already inside the seal can open a line up. Child inserted FIRST, then the link written: a failed link
  // leaves a real orphan child (recoverable) rather than a dangling pointer to a task that doesn't exist.
  async function promoteSubtask(subId, assigneeId, assigneeName) {
    if (!selectedTask || !subId || !assigneeId) return;
    const subs = Array.isArray(selectedTask.subtasks) ? selectedTask.subtasks : [];
    const item = subs.find(s => s.id === subId);
    if (!item || item.child_task_id) return;   // gone or already promoted
    setPromoteBusy(true);
    const childId = crypto.randomUUID();
    const payload = {
      id: childId, title: item.text, task_type: selectedTask.task_type, priority: selectedTask.priority || 'Normal', status: 'Open',
      client_id: selectedTask.client_id || '', client_name: selectedTask.client_name || '',
      property_id: selectedTask.property_id || '', property_name: selectedTask.property_name || '',
      due_date: selectedTask.due_date || null, description: '',
      assigned_to: assigneeId, assigned_to_name: assigneeName,
      assigned_by: staffUser?.id || null, assigned_by_name: staffUser?.name || null,
      is_private: !!selectedTask.is_private, parent_task_id: selectedTask.id,
      source: 'subtask', email_thread: [], subtasks: null,
    };
    const { error } = await supabase.from('tasks').insert(payload);
    if (error) { setPromoteBusy(false); setDetailMsg(`Couldn't assign subtask: ${error.message || error}`); setTimeout(() => setDetailMsg(''), 6000); return; }
    const ok = await writeSubtasks(subs.map(s => s.id === subId ? { ...s, child_task_id: childId, child_assignee_name: assigneeName } : s));
    setPromoteBusy(false);
    if (!ok) return;   // writeSubtasks surfaced the error; the child exists, just unlinked
    setPromoteSub(''); setPromoteAssignee('');
    flash(`Subtask assigned to ${assigneeName}`);
    // writeSubtasks already synced selectedTask + every list (the checklist link shows immediately, reopen-safe).
    // loadTasks additionally brings in the NEW child row so it appears in the lists/roster/KPI.
    loadTasks();
  }
  // Open a promoted subtask's child task in the detail. The child may not be in the loaded lists (it can be assigned to
  // someone else / already completed), so try the in-memory lists first, then fetch by id. Confidential children the
  // viewer isn't entitled to simply don't come back from the fetch (RLS), so the "no longer exists" branch below
  // covers both a deleted child and a confidential one — deliberately indistinguishable, so neither is advertised.
  async function openChildTask(childId) {
    if (!childId) return;
    const local = [...(tasks || []), ...(allTasks || []), ...(assignedTasks || []), ...(terminal || [])].find(t => t.id === childId);
    if (local) { setSelectedTask(local); return; }
    const { data, error } = await supabase.from('tasks').select('*').eq('id', childId).is('deleted_at', null).maybeSingle();
    if (error) { setDetailMsg(`Couldn't open the subtask's task: ${error.message || error}`); setTimeout(() => setDetailMsg(''), 6000); return; }
    if (data) setSelectedTask(data);
    else { setDetailMsg('That subtask’s task no longer exists.'); setTimeout(() => setDetailMsg(''), 6000); }
  }

  // Duplication (#7): copy a task for another person. Each copy is its own single-owner task; siblings share a group_id
  // (minted on first duplicate) so they can be seen as a group ("N of M"). Status resets to Open, is_private is dropped
  // (a new assigner→assignee pair; re-mark if needed), subtasks copy their text but reset done, the thread starts fresh.
  const buildCopyPayload = (src, assigneeId, assigneeName, gid) => ({
    title: src.title, task_type: src.task_type, priority: src.priority, status: 'Open',
    client_id: src.client_id || '', client_name: src.client_name || '',
    property_id: src.property_id || '', property_name: src.property_name || '',
    due_date: src.due_date || null, description: src.description || '',
    assigned_to: assigneeId || staffUser?.id, assigned_to_name: assigneeName || staffUser?.name,
    assigned_by: staffUser?.id || null, assigned_by_name: staffUser?.name || null,
    is_private: false, group_id: gid, source: 'duplicate', email_thread: [],
    subtasks: Array.isArray(src.subtasks) && src.subtasks.length
      ? src.subtasks.map(s => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, text: s.text, done: false }))
      : null,
  });
  async function duplicateTask(assigneeId, assigneeName) {
    if (!selectedTask) return;
    setDupBusy(true);
    let gid = selectedTask.group_id;
    if (!gid) {   // first duplicate → mint a group id and backfill the source so both are in the group
      gid = crypto.randomUUID();
      const { error: gErr } = await supabase.from('tasks').update({ group_id: gid, updated_at: new Date().toISOString() }).eq('id', selectedTask.id);
      if (gErr) { setDetailMsg(`Couldn't start the group: ${gErr.message || gErr}`); setTimeout(() => setDetailMsg(''), 6000); setDupBusy(false); return; }
      setSelectedTask(prev => ({ ...prev, group_id: gid }));
    }
    const { error } = await supabase.from('tasks').insert(buildCopyPayload(selectedTask, assigneeId, assigneeName, gid));
    setDupBusy(false);
    if (error) { setDetailMsg(`Couldn't duplicate: ${error.message || error}`); setTimeout(() => setDetailMsg(''), 6000); return; }
    setDupOpen(false); setDupAssignee('');
    flash(`Duplicated for ${assigneeName || 'you'}`);
    loadTasks();
  }

  // Reassign a task to another staffer. Preserves assigned_by (the delegator of record) EXCEPT when handing off a
  // self-assigned task — then I become the assigner. CONFIDENTIAL tasks KEEP is_private and just move the circle:
  // the new assignee gains visibility, the old assignee loses it, the assigner stays in. (Only the assigner can reach
  // this control on a confidential task — see canReassign.) The email_thread trail is FOLDED into the same update as
  // the patch (one atomic write — no "reassigned but no trail" window); the task_activity insert is checked too.
  async function reassignTask(newId) {
    if (!selectedTask || !newId || newId === selectedTask.assigned_to) return;   // guard: no falsy id can blank assigned_to
    const st = staffList.find(x => (x.id || x.staff_id) === newId);
    const wasSelfAssigned = selectedTask.assigned_by && selectedTask.assigned_by === selectedTask.assigned_to;
    const sys = { id: Date.now().toString(), direction:'system', from_name: staffUser?.name || 'System',
      body: `Reassigned from ${selectedTask.assigned_to_name || '—'} to ${st?.name || '—'}`, label:'Reassigned', sent_at: new Date().toISOString() };
    const newThread = [ ...(selectedTask.email_thread || []), sys ];
    const patch = {
      assigned_to: newId,
      assigned_to_name: st?.name || '',
      email_thread: newThread,                              // folded in — trail persists with the reassign or not at all
      updated_at: new Date().toISOString(),
      ...(wasSelfAssigned ? { assigned_by: staffUser?.id || null, assigned_by_name: staffUser?.name || null } : {}),
    };
    const { error } = await supabase.from('tasks').update(patch).eq('id', selectedTask.id);
    if (error) { setDetailMsg(`Couldn't reassign: ${error.message || error}`); setTimeout(() => setDetailMsg(''), 6000); return; }
    // Activity trail (separate table — can't fold). Surface its failure too rather than let the audit row silently vanish.
    const { error: aErr } = await supabase.from('task_activity').insert({ task_id: selectedTask.id, staff_id: staffUser?.id,
      staff_name: staffUser?.name || 'System', action:'reassigned', old_value: selectedTask.assigned_to_name || null, new_value: st?.name || null });
    if (aErr) { setDetailMsg(`Reassigned, but the activity log failed to record it: ${aErr.message || aErr}`); setTimeout(() => setDetailMsg(''), 6000); }
    else setDetailMsg('');
    // Child→parent subtask sync: if this is a promoted subtask, refresh the parent's "→ assignee" label.
    if (selectedTask.parent_task_id) await syncParentSubtaskLine(selectedTask.parent_task_id, selectedTask.id, su => ({ ...su, child_assignee_name: st?.name || '' }));
    setSelectedTask(prev => ({ ...prev, ...patch }));
    loadTasks();
  }

  // ───────────────────────────────────────────────────────────────────────
  // STEP 5 — Document Request admin actions
  // ───────────────────────────────────────────────────────────────────────

  // Helper: append a system event to tasks.email_thread + log to task_activity
  async function logTaskEvent(taskId, taskRow, eventLabel, eventDetail, activityAction) {
    const sysEntry = {
      id: Date.now().toString(),
      direction: 'system',
      from_name: staffUser?.name || 'System',
      body: eventDetail,
      label: eventLabel,                         // 'Approved', 'Ready to Deliver', 'Completed', etc.
      sent_at: new Date().toISOString(),
    };
    const currentThread = taskRow.email_thread || [];
    const updatedThread = [...currentThread, sysEntry];
    const { error: teErr } = await supabase.from('tasks').update({ email_thread: updatedThread, updated_at: new Date().toISOString() }).eq('id', taskId);
    if (teErr) console.warn('[logTaskEvent] thread update failed:', teErr);
    const { error: taErr } = await supabase.from('task_activity').insert({
      task_id: taskId,
      staff_id: staffUser?.id,
      staff_name: staffUser?.name || 'System',
      action: activityAction,
      comment: eventDetail.slice(0, 200),
    });
    if (taErr) console.warn('[logTaskEvent] activity insert failed:', taErr);
    return updatedThread;
  }

  // Admin clicks "Approve & Generate Upload Link"
  async function approveDocRequest() {
    if (!selectedTask || !docRequest) return;
    setDocReqBusy(true);
    setDocReqError('');
    try {
      // Generate a secure 64-char hex token
      let tokenValue = '';
      const bytes = new Uint8Array(32);
      if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        crypto.getRandomValues(bytes);
        tokenValue = Array.from(bytes).map(b => b.toString(16).padStart(2,'0')).join('');
      } else {
        // No crypto RNG → FAIL CLOSED. Never fall back to Math.random(): V8's xorshift128+ is
        // reversible — observing a few tokens lets an attacker reconstruct the PRNG state and predict
        // others. crypto.getRandomValues is universally available in supported browsers, so this
        // never runs; if it somehow does, refuse to mint a guessable token.
        throw new Error('Secure random number generator unavailable — cannot mint an upload token.');
      }

      // 1. Mint upload_tokens row
      const { data: newTok, error: tokErr } = await supabase.from('upload_tokens').insert({
        token: tokenValue,
        client_id: docRequest.client_id,
        client_name: docRequest.property_name ? null : null, // stored elsewhere — not needed here
        property_id: docRequest.property_id,
        property_name: docRequest.property_name,
        created_by: staffUser?.name || 'NTRG',
        note: `Doc request: ${docRequest.property_name} — approved by ${staffUser?.name} on ${new Date().toLocaleDateString()}`,
        expires_at: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(), // 90 days
        active: true,
        uploader_type: 'client',
        consultant_firm: null,   // NTRG-as-sender rule: no consultant leak
        files_received: 0,
      }).select().single();
      if (tokErr) throw new Error('Token mint failed: ' + tokErr.message);

      // upload_tokens.client_name may be required — re-patch in separate update (schema-safe)
      if (docRequest.client_id) {
        const { data: cl } = await supabase.from('clients').select('name').eq('id', docRequest.client_id).maybeSingle();
        if (cl?.name) {
          await supabase.from('upload_tokens').update({ client_name: cl.name }).eq('id', newTok.id);
        }
      }

      // 2. Wire the token to the request + set request status = approved
      await supabase.from('document_requests').update({
        upload_token_id: newTok.id,
        status: 'approved',
        approved_at: new Date().toISOString(),
        reviewed_by: staffUser?.name,
        reviewed_at: new Date().toISOString(),
      }).eq('id', docRequest.id);

      // 3. Log to task_activity + email_thread (approval event).
      //    NOTE (Step 6): we NO LONGER flip task.status to 'In Progress' here.
      //    That transition happens only when the client email is actually sent.
      //    Until then, the task stays 'Open' with the Document Request panel
      //    showing 'Approved — email not yet sent'.
      const newThread = await logTaskEvent(
        selectedTask.id,
        selectedTask,
        'Approved',
        `Upload link generated. Awaiting admin to compose & send the client email.`,
        'approved'
      );

      // 4. Update local state so UI reflects changes without reload
      setDocRequest(prev => ({ ...prev, upload_token_id: newTok.id, status: 'approved' }));
      setDocRequestToken(newTok);
      setSelectedTask(prev => ({ ...prev, email_thread: newThread }));

      // 5. Auto-switch to Compose Email tab and pre-fill with the client template.
      //    Use a short setTimeout so the new docRequestToken is in state when the
      //    template builder runs.
      setTimeout(() => {
        // NOTE: buildClientRequestEmail() uses docRequestToken from state, which
        // won't be updated inside this setTimeout closure. Build the email manually
        // using newTok directly instead.
        const activeItems = docRequestItems.filter(i => !i.removed_at);
        const items = activeItems.map(i => `  • ${i.document_type}${i.other_description ? `: ${i.other_description}` : ''}`).join('\n');
        const uploadUrl = `${window.location.origin}/upload/${newTok.token}`;
        const expiry = newTok.expires_at
          ? new Date(newTok.expires_at).toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' })
          : '';
        const contact = clientContacts.find(c => c.email === clientContactEmail) || clientContacts[0];
        const firstName = firstNameOf(contact?.name || contact?.email || '');
        const propertyName = docRequest.property_name || selectedTask?.property_name || 'your property';
        const taxYear = docRequest.tax_year || '';
        const builtSubject = `Document request — ${propertyName}${taxYear ? ` · Tax Year ${taxYear}` : ''}`;
        const builtBody = `Hi ${firstName},

NTRG is requesting the following documents related to your property tax appeal:

Property:  ${propertyName}${taxYear ? `\nTax Year: ${taxYear}` : ''}

Requested documents:
${items}

Please use this secure link to upload the documents directly:
${uploadUrl}

${expiry ? `This link expires on ${expiry}.\n\n` : ''}If you've already provided any of these documents, please reply to let us know.

If you have any questions, please don't hesitate to reach out.

Best regards,
${staffUser?.name || 'NTRG Team'}
National Tax Resource Group`;

        setReplyTo(contact?.email || clientContactEmail || '');
        setReplySubject(builtSubject);
        setReplyText(builtBody);
        setReplyMode('email');  // auto-switch to Compose Email tab
      }, 100);

      loadTasks();
      flash('Request approved. Review and send the client email →', false, 5000);
    } catch (e) {
      console.error('[approveDocRequest]', e);
      setDocReqError(e.message || 'Failed to approve request');
    }
    setDocReqBusy(false);
  }

  // Admin clicks "Notify Consultant & Complete"
  async function notifyAndCompleteDocRequest() {
    if (!selectedTask || !docRequest) return;
    // Step 6: instead of silently closing the task, pre-fill the Compose Email
    // tab with the consultant notification template. The actual close happens
    // when admin sends + confirms the email.

    // Resolve consultant email (fetched on-demand because consultants are
    // referenced but not part of the initial load)
    const consultantEmail = await resolveConsultantEmail();
    if (!consultantEmail) {
      // No consultant email on file — fall back to legacy "mark complete directly"
      const ok = window.confirm(
        `No consultant email on file. Mark this task Complete without sending a notification?\n\n(Admin can still notify the consultant manually through their own Outlook if needed.)`
      );
      if (!ok) return;
      setDocReqBusy(true);
      try {
        await supabase.from('document_requests').update({
          status: 'fulfilled',
          fulfilled_at: new Date().toISOString(),
          completed_at: new Date().toISOString(),
        }).eq('id', docRequest.id);
        await supabase.from('tasks').update({
          status: 'Complete',
          completed_at: new Date().toISOString(),
          completed_by: staffUser?.name,
          completed_by_id: staffUser?.id || null,
          updated_at: new Date().toISOString(),
        }).eq('id', selectedTask.id);
        const newThread = await logTaskEvent(
          selectedTask.id, selectedTask, 'Completed',
          `Task closed by ${staffUser?.name}. No consultant email on file — notification not sent via WigleyHub.`,
          'completed'
        );
        setDocRequest(prev => ({ ...prev, status: 'fulfilled' }));
        setSelectedTask(prev => ({ ...prev, status: 'Complete', email_thread: newThread }));
        loadTasks();
        flash('Task marked complete. No consultant email sent (no email on file).', false, 4000);
      } catch (e) {
        console.error('[notifyAndComplete fallback]', e);
        setDocReqError(e.message || 'Failed to complete task');
      }
      setDocReqBusy(false);
      return;
    }

    // Normal path: pre-fill Compose Email tab with consultant template
    const { subject, body } = buildConsultantNotifyEmail();
    setReplyTo(consultantEmail);
    setReplySubject(subject);
    setReplyText(body);
    setReplyMode('email');
    flash('Consultant email pre-filled. Review and click Send & Log →', false, 5000);
  }

  // Gap 2: Preview a consultant-attached file (AOA template).
  // Generates a signed URL and opens in a new tab.
  async function previewAttachment(file) {
    if (!file?.storage_path) {
      setDocReqError('This attachment has no storage path — cannot preview.');
      return;
    }
    setPreviewAttachBusy(prev => ({ ...prev, [file.id]: true }));
    try {
      const url = await fileUrl(file.storage_path, 'open').catch(() => null);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('No signed URL returned');
      }
    } catch (e) {
      console.error('[previewAttachment]', e);
      setDocReqError('Preview failed: ' + (e.message || 'unknown error'));
    }
    setPreviewAttachBusy(prev => ({ ...prev, [file.id]: false }));
  }

  // Gap 2: Admin removes a consultant-attached file with a required reason.
  // Soft-delete on file_exchange row + storage object delete + history log entry.
  async function removeAttachment(file, reason) {
    if (!selectedTask || !docRequest || !file?.id) return;
    if (!reason || !reason.trim()) {
      setDocReqError('Please provide a reason for removing this attachment.');
      return;
    }
    const trimmedReason = reason.trim();
    setRemoveAttachBusy(prev => ({ ...prev, [file.id]: true }));
    setDocReqError('');
    try {
      // 1. Snapshot previous state for audit history
      const prev = {
        folder_name: file.folder_name || null,
        file_type: file.file_type || null,
        year_subfolder: file.year_subfolder || null,
        property_id: file.property_id || null,
        parcel_id: file.parcel_id || null,
        viewable_client: file.viewable_client ?? null,
        viewable_consultant: file.viewable_consultant ?? null,
        need_review: file.need_review ?? null,
        client_id: file.client_id || null,
      };

      // 2. Write audit history row FIRST (before delete cascades it away)
      //    Uses the file_exchange_history table from today's migration.
      //    IMPORTANT: must happen before the delete, because file_exchange_history
      //    has ON DELETE CASCADE — deleting the file_exchange row would delete history.
      //    Workaround: drop the FK constraint, OR insert history with file_exchange_id
      //    pointing to the row, then delete. Since we have ON DELETE CASCADE, we
      //    take a different approach: keep the file_exchange row (soft-delete via
      //    need_review=true + an explicit removed_reason marker).
      // Plan change: soft-delete only. Set need_review=true to hide from intake
      // queue, clear viewable_client/viewable_consultant so nobody sees it, set
      // file_name to a removed marker.
      const { error: upErr } = await supabase.from('file_exchange').update({
        need_review: true,
        viewable_client: false,
        viewable_consultant: false,
        filed_at: null,    // unfile it
        file_name: `[REMOVED by ${staffUser?.name || 'admin'}] ${file.file_name}`,
      }).eq('id', file.id);
      if (upErr) throw new Error('Remove failed: ' + upErr.message);

      // 3. Delete the R2 object (best-effort — this is a soft-delete: the row + history stay, we just free the
      //    blob). Post-cutover the bytes live in R2, so this routes through keysDelete (R2 delete + verify + audit
      //    action='delete'); the old supabase.storage.remove was a silent no-op that leaked the object.
      if (file.storage_path) {
        const { error: stErr, failed } = await keysDelete([file.storage_path]);
        if (stErr || (failed && failed.length)) console.warn('[removeAttachment] R2 delete failed (non-fatal):', stErr?.message || failed);
      }

      // 4. Write audit history row for the removal.
      await supabase.from('file_exchange_history').insert({
        file_exchange_id: file.id,
        changed_by: staffUser?.name || 'Unknown',
        change_reason: trimmedReason,
        action: 'attachment_removed',
        prev_client_id: prev.client_id,
        prev_property_id: prev.property_id,
        prev_parcel_id: prev.parcel_id,
        prev_folder_name: prev.folder_name,
        prev_year_subfolder: prev.year_subfolder,
        prev_file_type: prev.file_type,
        prev_viewable_client: prev.viewable_client,
        prev_viewable_consultant: prev.viewable_consultant,
        prev_need_review: prev.need_review,
      });

      // 5. Log system event to task email_thread + activity
      const updatedThread = await logTaskEvent(
        selectedTask.id,
        selectedTask,
        'Attachment Removed',
        `"${file.file_name}" (consultant-attached) removed — reason: "${trimmedReason}"`,
        'attachment_removed'
      );
      setSelectedTask(prev => ({ ...prev, email_thread: updatedThread }));

      // 6. Update local attachments list
      setDocRequestAoaFiles(list => list.filter(f => f.id !== file.id));

      // 7. Collapse the inline remove panel
      setRemoveAttachOpen(prev => ({ ...prev, [file.id]: false }));
      setRemoveAttachReason(prev => ({ ...prev, [file.id]: '' }));
      flash(`"${file.file_name}" removed.`);
    } catch (e) {
      console.error('[removeAttachment]', e);
      setDocReqError(e.message || 'Failed to remove attachment');
    }
    setRemoveAttachBusy(prev => ({ ...prev, [file.id]: false }));
  }

  // Gap 2: Pre-fill Compose Email with a reference to the consultant-attached file.
  // Switches to the email tab with a draft that mentions the file by name.
  function emailConsultantAboutAttachment(file) {
    if (!file || !docRequest) return;
    // Try to resolve consultant's email. Same lookup as notifyAndCompleteDocRequest uses.
    // For now, we rely on the admin to type it manually — we just pre-fill the draft.
    // (If docRequest.consultant_firm has a known email on file, we could look it up; skip for simplicity.)
    const subject = `Question about attachment: ${file.file_name}`;
    const body = [
      `Hi,`,
      ``,
      `Quick question about the attachment you included on this request:`,
      `  • ${file.file_name}`,
      ``,
      `[Your question here]`,
      ``,
      `Thanks,`,
      `${staffUser?.name || 'NTRG'}`,
    ].join('\n');
    setReplyTo('');   // Admin picks the recipient from Quick-pick or types manually
    setReplyCc('');
    setReplySubject(subject);
    setReplyText(body);
    setReplyMode('email');
    flash(`Draft pre-filled for attachment "${file.file_name}". Pick the recipient and click Send.`, false, 5000);
  }

  // Step 5b: Admin removes a single item with a required reason.
  // Uses soft-delete (sets removed_at/removed_by/removal_reason).
  // If this removes the LAST active item, cascade: cancel request + cancel task.
  async function removeItem(item, reason) {
    if (!selectedTask || !docRequest || !item?.id) return;
    if (!reason || !reason.trim()) {
      setDocReqError('Please provide a reason for removing this item.');
      return;
    }
    const trimmedReason = reason.trim();
    setRemoveItemBusy(prev => ({ ...prev, [item.id]: true }));
    setDocReqError('');
    try {
      // 1. Soft-delete the item
      const { error: upErr } = await supabase.from('document_request_items').update({
        removed_at: new Date().toISOString(),
        removed_by: staffUser?.name || 'Unknown',
        removal_reason: trimmedReason,
      }).eq('id', item.id);
      if (upErr) throw new Error('Remove failed: ' + upErr.message);

      // 2. Log system event to email_thread + task_activity
      const updatedThread = await logTaskEvent(
        selectedTask.id,
        selectedTask,
        'Item Removed',
        `"${item.document_type}" removed from request — reason: "${trimmedReason}"`,
        'item_removed'
      );
      setSelectedTask(prev => ({ ...prev, email_thread: updatedThread }));

      // 3. Update local items state (mark removed in place; keep for the "Removed" section)
      const nowIso = new Date().toISOString();
      const updatedItems = docRequestItems.map(it =>
        it.id === item.id
          ? { ...it, removed_at: nowIso, removed_by: staffUser?.name, removal_reason: trimmedReason }
          : it
      );
      setDocRequestItems(updatedItems);

      // 4. Check: did we just remove the LAST active item? → cascade cancel
      const activeRemaining = updatedItems.filter(it => !it.removed_at);
      if (activeRemaining.length === 0) {
        // Cancel the request + cancel the task
        await supabase.from('document_requests').update({
          status: 'cancelled',
          updated_at: new Date().toISOString(),
        }).eq('id', docRequest.id);

        await supabase.from('tasks').update({
          status: 'Cancelled',
          updated_at: new Date().toISOString(),
        }).eq('id', selectedTask.id);

        const cancelThread = await logTaskEvent(
          selectedTask.id,
          { ...selectedTask, email_thread: updatedThread },
          'Request Cancelled',
          'All items were removed — request and task automatically cancelled.',
          'request_cancelled'
        );

        setDocRequest(prev => ({ ...prev, status: 'cancelled' }));
        setSelectedTask(prev => ({ ...prev, status: 'Cancelled', email_thread: cancelThread }));
        loadTasks();
        flash('Last item removed — request cancelled automatically.');
      } else {
        flash(`"${item.document_type}" removed from request.`);
      }

      // 5. Collapse the inline remove panel
      setRemoveItemOpen(prev => ({ ...prev, [item.id]: false }));
      setRemoveItemReason(prev => ({ ...prev, [item.id]: '' }));
    } catch (e) {
      console.error('[removeItem]', e);
      setDocReqError(e.message || 'Failed to remove item');
    }
    setRemoveItemBusy(prev => ({ ...prev, [item.id]: false }));
  }

  // Copy the upload URL to clipboard
  function copyUploadLink() {
    if (!docRequestToken?.token) return;
    const url = `${window.location.origin}/upload/${docRequestToken.token}`;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        flash('Upload link copied to clipboard.');
      });
    }
  }

  // ───────────────────────────────────────────────────────────────────────
  // STEP 6 — Email via mailto: (opens Outlook / default mail client)
  // ───────────────────────────────────────────────────────────────────────

  // Build a mailto: URI. All parts get encoded.
  function buildMailtoUri(to, subject, body, cc) {
    const params = [];
    if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
    if (body) params.push(`body=${encodeURIComponent(body)}`);
    if (cc) params.push(`cc=${encodeURIComponent(cc)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return `mailto:${to}${qs}`;
  }

  // Open the user's mail client with pre-filled content.
  // Then open the confirmation dialog — the admin says Yes/No after they send.
  function openMailtoWithConfirm({ purpose, to, subject, body, cc, onConfirmYes }) {
    const uri = buildMailtoUri(to, subject, body, cc);
    // Use location.href (opens Outlook reliably) rather than window.open (may be blocked)
    window.location.href = uri;
    // Present confirmation
    setEmailConfirmData({ purpose, to, subject, body, cc, onConfirmYes });
    setEmailConfirmOpen(true);
  }

  // When the admin confirms "Yes, I sent it", append to task.email_thread +
  // log to task_activity, then run any purpose-specific follow-up.
  async function confirmEmailSent() {
    if (!emailConfirmData || !selectedTask) return;
    const { purpose, to, cc, subject, body, onConfirmYes } = emailConfirmData;
    try {
      // 1. Append outbound message to email_thread
      const message = {
        id: Date.now().toString(),
        direction: 'outbound',
        from_name: staffUser?.name,
        from_email: staffUser?.email,
        to_email: to,
        cc_emails: cc || null,  // Gap 3: preserve CC recipients in thread log
        subject,
        body,
        sent_at: new Date().toISOString(),
        bcc_staff_email: staffUser?.email,
        purpose,  // 'client_request' | 'consultant_notify' | 'consultant_partial' | 'generic'
      };
      const currentThread = selectedTask.email_thread || [];
      const updatedThread = [...currentThread, message];

      await supabase.from('tasks').update({
        email_thread: updatedThread,
        updated_at: new Date().toISOString(),
      }).eq('id', selectedTask.id);

      await supabase.from('task_activity').insert({
        task_id: selectedTask.id,
        staff_id: staffUser?.id,
        staff_name: staffUser?.name,
        action: 'email_sent',
        new_value: to,
        comment: `[${purpose}] ${subject}`.slice(0, 200),
      });

      setSelectedTask(prev => ({ ...prev, email_thread: updatedThread }));

      // 2. Run purpose-specific follow-up (e.g., flip task to In Progress, or to Complete)
      if (onConfirmYes) {
        await onConfirmYes(updatedThread);
      }

      // 3. Clear the compose form now that the email is confirmed sent + logged
      setReplyText('');
      setReplyTo('');
      setReplyCc('');   // Gap 3: also clear CC field
      setReplySubject('');

      // 4. Show "Email logged" toast with a 5-second countdown.
      //    After countdown, auto-navigate back to the Tasks list.
      //    Admin can click the toast to go faster, or × to stay.
      setEmailSentToast({ secondsLeft: 5, purpose });
    } catch (e) {
      console.error('[confirmEmailSent]', e);
      flash('Error logging email — please try again.', true);
    }
    setEmailConfirmOpen(false);
    setEmailConfirmData(null);
  }

  // Countdown + auto-close for the email-sent toast.
  useEffect(() => {
    if (!emailSentToast) return;
    if (emailSentToast.secondsLeft <= 0) {
      setEmailSentToast(null);
      setSelectedTask(null);  // Back to Tasks list
      return;
    }
    const timer = setTimeout(() => {
      setEmailSentToast(prev => prev ? { ...prev, secondsLeft: prev.secondsLeft - 1 } : null);
    }, 1000);
    return () => clearTimeout(timer);
  }, [emailSentToast]);

  function cancelEmailConfirm() {
    setEmailConfirmOpen(false);
    setEmailConfirmData(null);
  }

  // Extract first name from a full name or email.
  function firstNameOf(full) {
    if (!full) return 'there';
    // If it looks like an email, get the local-part's first token
    if (full.includes('@')) {
      const local = full.split('@')[0];
      return local.split(/[._-]/)[0].replace(/^\w/, c => c.toUpperCase());
    }
    return full.trim().split(/\s+/)[0];
  }

  // Build the NTRG-branded client request email body.
  function buildClientRequestEmail() {
    if (!docRequest || !docRequestToken) return { subject: '', body: '', to: '' };
    const activeItems = docRequestItems.filter(i => !i.removed_at);
    const items = activeItems.map(i => `  • ${i.document_type}${i.other_description ? `: ${i.other_description}` : ''}`).join('\n');
    const uploadUrl = `${window.location.origin}/upload/${docRequestToken.token}`;
    const expiry = docRequestToken.expires_at
      ? new Date(docRequestToken.expires_at).toLocaleDateString('en-US', { month:'long', day:'numeric', year:'numeric' })
      : '';
    const contact = clientContacts.find(c => c.email === clientContactEmail) || clientContacts[0];
    const firstName = firstNameOf(contact?.name || contact?.email || '');
    const propertyName = docRequest.property_name || selectedTask?.property_name || 'your property';
    const taxYear = docRequest.tax_year || '';
    const subject = `Document request — ${propertyName}${taxYear ? ` · Tax Year ${taxYear}` : ''}`;
    const body = `Hi ${firstName},

NTRG is requesting the following documents related to your property tax appeal:

Property:  ${propertyName}${taxYear ? `\nTax Year: ${taxYear}` : ''}

Requested documents:
${items}

Please use this secure link to upload the documents directly:
${uploadUrl}

${expiry ? `This link expires on ${expiry}.\n\n` : ''}If you've already provided any of these documents, please reply to let us know.

If you have any questions, please don't hesitate to reach out.

Best regards,
${staffUser?.name || 'NTRG Team'}
National Tax Resource Group`;
    return { subject, body, to: contact?.email || clientContactEmail || '' };
  }

  // Build the NTRG-branded consultant notification email body.
  function buildConsultantNotifyEmail() {
    if (!docRequest) return { subject: '', body: '', to: '' };
    const activeItems = docRequestItems.filter(i => !i.removed_at);
    const hasPriorDeliveries = activeItems.some(i => i.delivered_to_consultant_at);
    const lines = activeItems.map(i => {
      if (i.status === 'fulfilled' && i.fulfilled_at) {
        const d = new Date(i.fulfilled_at).toLocaleDateString('en-US',{month:'short',day:'numeric'});
        // If item was already delivered in a partial delivery, label that way.
        if (i.delivered_to_consultant_at) {
          const deliveredDate = new Date(i.delivered_to_consultant_at).toLocaleDateString('en-US',{month:'short',day:'numeric'});
          return `  ✓ ${i.document_type}${i.other_description ? `: ${i.other_description}` : ''} (previously delivered ${deliveredDate})`;
        }
        const label = i.shortcut_fulfilled ? ` (already provided prior to request, confirmed ${d})` : ` (received ${d})`;
        return `  ✓ ${i.document_type}${i.other_description ? `: ${i.other_description}` : ''}${label}`;
      }
      if (i.status === 'na') {
        return `  — ${i.document_type}${i.other_description ? `: ${i.other_description}` : ''} (N/A)`;
      }
      return `  • ${i.document_type}${i.other_description ? `: ${i.other_description}` : ''}`;
    }).join('\n');
    const propertyName = docRequest.property_name || selectedTask?.property_name || 'property';
    const taxYear = docRequest.tax_year || '';
    const consultantFirm = docRequest.consultant_firm || '';
    const reqCode = docRequest.request_code ? `\n\nReference: ${docRequest.request_code}` : '';
    const subject = hasPriorDeliveries
      ? `Final delivery — ${propertyName}${taxYear ? ` · Tax Year ${taxYear}` : ''}`
      : `Documents ready — ${propertyName}${taxYear ? ` · Tax Year ${taxYear}` : ''}`;
    const intro = hasPriorDeliveries
      ? `The remaining documents from your request on ${propertyName}${taxYear ? ` (Tax Year ${taxYear})` : ''} are now available. This completes the request.`
      : `The documents you requested for ${propertyName}${taxYear ? ` (Tax Year ${taxYear})` : ''} are ready for your review in the consultant portal.`;
    const body = `Hi${consultantFirm ? ` ${firstNameOf(consultantFirm)}` : ' there'},

${intro}

${hasPriorDeliveries ? 'Full item summary:' : 'Items delivered:'}
${lines}

Please log into the portal to view and download the documents.${reqCode}

Best regards,
${staffUser?.name || 'NTRG Team'}
National Tax Resource Group`;
    return { subject, body, to: docRequest.consultant_id ? '' : '' }; // consultant email resolution below
  }

  // ───────────────────────────────────────────────────────────────────────
  // Partial delivery email builder — used when SOME items are ready but
  // others are still owed. Lists what's being delivered now AND what's
  // still outstanding so the consultant has full context.
  // ───────────────────────────────────────────────────────────────────────
  function buildPartialNotifyEmail(deliveringNow, stillOwed) {
    if (!docRequest) return { subject: '', body: '' };
    const propertyName = docRequest.property_name || selectedTask?.property_name || 'property';
    const taxYear = docRequest.tax_year || '';
    const consultantFirm = docRequest.consultant_firm || '';
    const reqCode = docRequest.request_code ? `\nReference: ${docRequest.request_code}` : '';

    const deliveringLines = deliveringNow.map(i => {
      const d = i.fulfilled_at ? new Date(i.fulfilled_at).toLocaleDateString('en-US',{month:'short',day:'numeric'}) : '';
      const label = i.shortcut_fulfilled ? ` (already provided prior to request, confirmed ${d})` : d ? ` (received ${d})` : '';
      return `  ✓ ${i.document_type}${i.other_description ? `: ${i.other_description}` : ''}${label}`;
    }).join('\n');

    const owedLines = stillOwed.map(i => {
      const tag = i.status === 'not_ready' && i.promised_by_date
        ? ` (expected ${new Date(i.promised_by_date + 'T00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})})`
        : '';
      return `  • ${i.document_type}${i.other_description ? `: ${i.other_description}` : ''}${tag}`;
    }).join('\n');

    const subject = `Documents available — ${propertyName}${taxYear ? ` · Tax Year ${taxYear}` : ''} (Partial)`;
    const body = `Hi${consultantFirm ? ` ${firstNameOf(consultantFirm)}` : ' there'},

We've processed ${deliveringNow.length} of ${deliveringNow.length + stillOwed.length} items for your request on ${propertyName}${taxYear ? ` (Tax Year ${taxYear})` : ''}. The following are now available in your consultant portal:

${deliveringLines}

Still outstanding from this request:

${owedLines}

We'll continue to follow up with the client and send another delivery once additional items come in.${reqCode}

Best regards,
${staffUser?.name || 'NTRG Team'}
National Tax Resource Group`;
    return { subject, body };
  }

  // Resolve the consultant email for notification
  async function resolveConsultantEmail() {
    if (!docRequest?.consultant_id) return '';
    const { data: con } = await supabase.from('consultants')
      .select('email').eq('id', docRequest.consultant_id).maybeSingle();
    return con?.email || '';
  }

  // ───────────────────────────────────────────────────────────────────────
  // Derived helpers for Step 5 render (ignore removed items for all state calculations)
  function docReqAllAddressed() {
    const active = docRequestItems.filter(i => !i.removed_at);
    if (!active.length) return false;
    return active.every(i => i.status === 'fulfilled' || i.status === 'na');
  }

  // ───────────────────────────────────────────────────────────────────────
  // Step 8: Partial delivery helpers
  // ───────────────────────────────────────────────────────────────────────

  // Items eligible for partial delivery: fulfilled AND not yet delivered to
  // consultant. These are the items that, on partial delivery, will be marked
  // delivered_to_consultant_at = now.
  function partialDeliveryDeliverable() {
    const active = docRequestItems.filter(i => !i.removed_at);
    return active.filter(i => i.status === 'fulfilled' && !i.delivered_to_consultant_at);
  }

  // Items still owed: pending or not_ready (client hasn't provided yet).
  // N/A items count as resolved, so they're not "owed".
  function partialDeliveryOwed() {
    const active = docRequestItems.filter(i => !i.removed_at);
    return active.filter(i => i.status === 'pending' || i.status === 'not_ready');
  }

  // Whether the Deliver Partial button should appear:
  //   - At least one item is fulfilled-but-not-delivered (something to deliver)
  //   - At least one item is still owed (otherwise normal full delivery applies)
  //   - Intake is clean (no unintaked files would block the delivery anyway)
  //   - Task isn't terminal
  function canDeliverPartial() {
    if (!docRequest || !selectedTask) return false;
    if ([TASK_STATUS.COMPLETE, TASK_STATUS.CANCELLED, TASK_STATUS.READY_TO_DELIVER].includes(selectedTask.status)) return false;
    if (unintakedFileCount > 0) return false;
    return partialDeliveryDeliverable().length > 0 && partialDeliveryOwed().length > 0;
  }

  // Action: Pre-fill the Compose Email tab with the partial-delivery template,
  // wire up the post-send onConfirmYes to mark items delivered + flip task
  // to Partial Delivered. Same UX pattern as notifyAndCompleteDocRequest.
  async function deliverPartialDocRequest() {
    if (!selectedTask || !docRequest) return;
    const deliveringNow = partialDeliveryDeliverable();
    const stillOwed = partialDeliveryOwed();
    if (deliveringNow.length === 0) {
      setDocReqError('No items are ready to deliver right now.');
      setTimeout(() => setDocReqError(''), 4000);
      return;
    }
    if (stillOwed.length === 0) {
      // Edge case — admin clicked partial but nothing's owed. Fall through to
      // the normal full-delivery flow.
      notifyAndCompleteDocRequest();
      return;
    }

    const consultantEmail = await resolveConsultantEmail();
    if (!consultantEmail) {
      const ok = window.confirm(
        `No consultant email on file. Mark these ${deliveringNow.length} items as delivered without sending a notification?`
      );
      if (!ok) return;
      // Fallback: just mark items + flip task without email
      setDocReqBusy(true);
      try {
        const nowIso = new Date().toISOString();
        const ids = deliveringNow.map(i => i.id);
        await supabase.from('document_request_items')
          .update({ delivered_to_consultant_at: nowIso })
          .in('id', ids);
        await supabase.from('tasks').update({
          status: TASK_STATUS.PARTIAL_DELIVERED,
          updated_at: nowIso,
        }).eq('id', selectedTask.id);
        const newThread = await logTaskEvent(
          selectedTask.id, selectedTask, 'Partial Delivered',
          `Partial delivery: ${deliveringNow.length} item(s) marked delivered. ${stillOwed.length} still outstanding. No consultant email on file — notification not sent via WigleyHub.`,
          'partial_delivered'
        );
        setSelectedTask(prev => ({ ...prev, status: TASK_STATUS.PARTIAL_DELIVERED, email_thread: newThread }));
        // Re-fetch items to refresh the delivered_to_consultant_at flags
        const { data: items } = await supabase.from('document_request_items')
          .select('*').eq('request_id', docRequest.id).order('display_order', { ascending: true });
        setDocRequestItems(items || []);
        loadTasks();
        flash(`Partial delivery: ${deliveringNow.length} item(s) marked delivered. No consultant email sent.`, false, 4000);
      } catch (e) {
        console.error('[deliverPartial fallback]', e);
        setDocReqError(e.message || 'Failed to deliver partial');
      }
      setDocReqBusy(false);
      return;
    }

    // Normal path: pre-fill Compose Email tab with partial template
    const { subject, body } = buildPartialNotifyEmail(deliveringNow, stillOwed);
    setReplyTo(consultantEmail);
    setReplySubject(subject);
    setReplyText(body);
    setReplyMode('email');
    // Attach partial-delivery state to the email confirm so onConfirmYes can
    // do the right thing post-send (marks delivered_to_consultant_at + status flip)
    setPartialDeliveryPending({ deliveringNowIds: deliveringNow.map(i => i.id), deliveringCount: deliveringNow.length, owedCount: stillOwed.length });
    flash('Partial delivery email pre-filled. Review and click Send & Log →', false, 5000);
  }

  function docReqStageLabel() {
    // Returns a short phrase describing where the request is in its lifecycle
    if (!docRequest) return null;
    if (docRequest.status === 'pending') return 'Awaiting your review';
    if (docRequest.status === 'fulfilled') return 'Completed';
    if (docRequest.status === 'cancelled') return 'Cancelled';
    // Stage label should reflect the live task status when in intake-bridge states
    if (selectedTask?.status === TASK_STATUS.WAITING_ON_INTAKE) return 'Waiting on Intake';
    if (selectedTask?.status === TASK_STATUS.PARTIAL_DELIVERED) return 'Partial Delivered';
    if (selectedTask?.status === TASK_STATUS.READY_TO_DELIVER) return 'Ready to Deliver';
    if (selectedTask?.status === TASK_STATUS.COMPLETE) return 'Completed';
    if (selectedTask?.status === TASK_STATUS.CANCELLED) return 'Cancelled';
    if (docReqAllAddressed()) return 'Ready to Deliver';
    const active = docRequestItems.filter(i => !i.removed_at);
    const pending = active.filter(i => i.status === DOC_REQUEST_ITEM_STATUS.PENDING).length;
    const notReady = active.filter(i => i.status === DOC_REQUEST_ITEM_STATUS.NOT_READY).length;
    if (pending > 0 || notReady > 0) return 'Awaiting client action';
    return 'In Progress';
  }

  async function sendReply() {
    if (!replyText || !selectedTask) return;
    if (!replyTo || !replyTo.includes('@')) {
      flash('Please enter a valid recipient email address.', true);
      return;
    }
    setSendingReply(true);

    // Detect purpose from content: if this is a doc-request context with an approved
    // token and a request in 'approved' status, treat as client request email.
    // If the task is Ready to Deliver, treat as consultant notification.
    // If a partial-delivery flow is staged (partialDeliveryPending set), treat
    // as a partial consultant notification — different post-send side effects.
    let purpose = 'generic';
    let onConfirmYes = null;
    if (partialDeliveryPending && docRequest) {
      purpose = 'consultant_partial';
      onConfirmYes = async () => {
        const { deliveringNowIds, deliveringCount, owedCount } = partialDeliveryPending;
        const nowIso = new Date().toISOString();
        // 1. Mark each delivered item with delivered_to_consultant_at
        await supabase.from('document_request_items')
          .update({ delivered_to_consultant_at: nowIso })
          .in('id', deliveringNowIds);
        // 2. Flip task to Partial Delivered
        await supabase.from('tasks').update({
          status: TASK_STATUS.PARTIAL_DELIVERED,
          updated_at: nowIso,
        }).eq('id', selectedTask.id);
        // 3. Log partial delivery activity
        const { error: pdErr } = await supabase.from('task_activity').insert({
          task_id: selectedTask.id,
          staff_id: staffUser?.id,
          staff_name: staffUser?.name,
          action: 'partial_delivered',
          comment: `Partial delivery sent: ${deliveringCount} item(s) delivered, ${owedCount} still outstanding.`,
        });
        if (pdErr) console.warn('[partial-delivery] activity insert failed:', pdErr);
        // 4. Refresh local state
        const { data: items } = await supabase.from('document_request_items')
          .select('*').eq('request_id', docRequest.id).order('display_order', { ascending: true });
        setDocRequestItems(items || []);
        setSelectedTask(prev => ({ ...prev, status: TASK_STATUS.PARTIAL_DELIVERED }));
        loadTasks();
        // 5. Clear pending state
        setPartialDeliveryPending(null);
      };
    } else if (docRequest && docRequestToken && docRequest.status === 'approved' && selectedTask.status === TASK_STATUS.OPEN) {
      purpose = 'client_request';
      onConfirmYes = async () => {
        // Flip task to In Progress once admin confirms the client email was sent
        await supabase.from('tasks').update({
          status: TASK_STATUS.IN_PROGRESS,
          updated_at: new Date().toISOString(),
        }).eq('id', selectedTask.id);
        // Also log to request's email_sent_at
        await supabase.from('document_requests').update({
          email_sent_at: new Date().toISOString(),
        }).eq('id', docRequest.id);
        setSelectedTask(prev => ({ ...prev, status: TASK_STATUS.IN_PROGRESS }));
        setDocRequest(prev => ({ ...prev, email_sent_at: new Date().toISOString() }));
        loadTasks();
      };
    } else if (docRequest && selectedTask.status === TASK_STATUS.READY_TO_DELIVER) {
      purpose = 'consultant_notify';
      onConfirmYes = async () => {
        // Flip task to Complete and request to fulfilled
        await supabase.from('document_requests').update({
          status: 'fulfilled',
          fulfilled_at: new Date().toISOString(),
          completed_at: new Date().toISOString(),
          consultant_notified_at: new Date().toISOString(),
        }).eq('id', docRequest.id);
        await supabase.from('tasks').update({
          status: TASK_STATUS.COMPLETE,
          completed_at: new Date().toISOString(),
          completed_by: staffUser?.name,
          updated_at: new Date().toISOString(),
        }).eq('id', selectedTask.id);
        // Log the completion event
        const nowThread = (selectedTask.email_thread || []);
        const doneEvent = {
          id: Date.now().toString() + '-completed',
          direction: 'system',
          from_name: staffUser?.name || 'System',
          body: `Consultant notification sent. Task closed by ${staffUser?.name}.`,
          label: 'Completed',
          sent_at: new Date().toISOString(),
        };
        // Note: the outbound email was already appended by confirmEmailSent().
        // We need to re-fetch to get the authoritative thread then add the completion event on top.
        const { data: freshTask, error: ftErr } = await supabase.from('tasks').select('email_thread').eq('id', selectedTask.id).maybeSingle();
        if (ftErr) console.warn('[consultant-complete] fresh-task read failed:', ftErr);
        const updatedThread = [...(freshTask?.email_thread || nowThread), doneEvent];
        const { error: ctErr } = await supabase.from('tasks').update({ email_thread: updatedThread }).eq('id', selectedTask.id);
        if (ctErr) console.warn('[consultant-complete] thread update failed:', ctErr);
        const { error: caErr } = await supabase.from('task_activity').insert({
          task_id: selectedTask.id,
          staff_id: staffUser?.id,
          staff_name: staffUser?.name,
          action: 'completed',
          comment: `Consultant notified. Task closed by ${staffUser?.name}.`,
        });
        if (caErr) console.warn('[consultant-complete] activity insert failed:', caErr);
        setDocRequest(prev => ({ ...prev, status: 'fulfilled' }));
        setSelectedTask(prev => ({ ...prev, status: TASK_STATUS.COMPLETE, email_thread: updatedThread }));
        loadTasks();
      };
    }

    // Fire mailto + open confirmation dialog.
    // NOTE: do NOT clear the form here. If the admin clicks "No I didn't send it"
    // in the modal, the form should be preserved so they can retry or edit.
    // The form is cleared only on successful "Yes" confirmation (inside confirmEmailSent).
    openMailtoWithConfirm({
      purpose,
      to: replyTo,
      cc: replyCc || null,   // Gap 3: pass through comma-separated CC list (or null)
      subject: replySubject || `Re: ${selectedTask.title}`,
      body: replyText,
      onConfirmYes,
    });

    setSendingReply(false);
  }

  async function savePTO() {
    // Early return NOW SURFACES which field is missing, in-panel (was a silent return — same class as the notes/saveTask bug).
    if (!ptoForm.cover_staff_id || !ptoForm.start_date || !ptoForm.end_date) {
      const missing = [!ptoForm.cover_staff_id && 'a cover person', !ptoForm.start_date && 'a start date', !ptoForm.end_date && 'an end date'].filter(Boolean).join(', ');
      setPtoMsg(`⚠ Please choose ${missing} before saving.`);
      return;
    }
    if (ptoSaving) return;                 // double-submit guard — no duplicate windows from a second click
    setPtoSaving(true); setPtoMsg('');
    const cover = staffList.find(s => s.id === ptoForm.cover_staff_id);
    const ptoStaffId = ptoForm.pto_staff_id || staffUser?.id;   // who's ON pto (admin may arrange for someone else)
    const ptoStaff = staffList.find(s => (s.id || s.staff_id) === ptoStaffId);
    const { data: covRow, error: pErr } = await supabase.from('pto_coverage').insert({
      staff_id: ptoStaffId,
      staff_name: ptoStaff?.name || staffUser?.name,
      cover_staff_id: ptoForm.cover_staff_id,
      cover_name: cover?.name || '',
      start_date: ptoForm.start_date,
      end_date: ptoForm.end_date,
      notes: ptoForm.notes,
      created_by: staffUser?.name,   // who ARRANGED it (audit) — unchanged
    }).select('id').single();
    if (pErr) { setPtoMsg(`⚠ PTO save failed: ${pErr.message || pErr}`); setPtoSaving(false); return; }
    // One task_coverage grant per selected task — the coverer sees/acts on them during the window (assigned_to unchanged).
    let coverageWarn = '';
    if (ptoTaskIds.length) {
      const rows = ptoTaskIds.map(tid => ({ task_id: tid, coverage_id: covRow.id, covered_by_id: ptoForm.cover_staff_id, start_date: ptoForm.start_date, end_date: ptoForm.end_date }));
      const { error: tErr } = await supabase.from('task_coverage').insert(rows);
      if (tErr) coverageWarn = ` — but task coverage failed: ${tErr.message || tErr}`;
    }
    const fmt = d => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' });
    const n = ptoTaskIds.length;
    // Local-time date math (project rule — never toISOString for date-only logic). Tell the user if it isn't active yet.
    const now = new Date();
    const [sy, sm, sd] = ptoForm.start_date.split('-').map(Number);
    const startLocal = new Date(sy, sm - 1, sd);
    const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const daysUntil = Math.round((startLocal - todayLocal) / 86400000);
    const activeNote = daysUntil <= 0 ? ' — active now' : daysUntil === 1 ? ` — starts ${fmt(ptoForm.start_date)} (tomorrow), not active yet` : ` — starts ${fmt(ptoForm.start_date)} (in ${daysUntil} days), not active yet`;
    setPtoMsg(`✓ Coverage set for ${cover?.name || 'coverer'} (${fmt(ptoForm.start_date)}–${fmt(ptoForm.end_date)}, ${n} task${n!==1?'s':''})${activeNote}${coverageWarn}`);
    // DON'T auto-close — leave the confirmation visible. Reset the form so a stray re-save can't duplicate the window.
    setPtoForm({ pto_staff_id:'', pto_staff_name:'', cover_staff_id:'', cover_name:'', start_date:'', end_date:'', notes:'' });
    setPtoTaskIds([]); setPtoSaving(false);
    loadPTO(); loadTasks();
  }

  // LOCAL-time date math (project rule) — parse 'YYYY-MM-DD' as LOCAL midnight, compare against LOCAL start-of-today.
  // Prevents the UTC bug where new Date('YYYY-MM-DD') is UTC midnight (prior evening in CDT) → a task due TODAY reads overdue.
  const _localMidnight = (dateStr) => { const [y,m,d] = dateStr.split('-').map(Number); return new Date(y, m-1, d); };
  const _todayMidnight = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); };

  function isOverdue(task) {
    if (!task.due_date) return false;
    const eff = task._displayStatus || task.status;
    if (TASK_STATUS_TERMINAL.includes(eff)) return false;
    return _localMidnight(task.due_date) < _todayMidnight();   // strictly before today = overdue; due today = NOT overdue
  }

  function daysUntil(dateStr) {
    if (!dateStr) return null;
    return Math.round((_localMidnight(dateStr) - _todayMidnight()) / 86400000);   // today→0, tomorrow→1, yesterday→-1
  }

  // skipKpi=true → apply every filter EXCEPT the KPI-card bucket (used to compute the card counts themselves, so a
  // card counts within the current filter context but isn't narrowed by its own selection).
  const applyFilters = (arr, skipKpi = false) => arr.filter(t => {
    const eff = t._displayStatus || t.status;
    if (filterStatus && eff !== filterStatus) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (filterClient && !t.client_name?.toLowerCase().includes(filterClient.toLowerCase())) return false;
    if (filterAssignee && t.assigned_to !== filterAssignee) return false;
    // KPI-card bucket filter — MUST use the same precedence as the cards (WAITING > OVERDUE > status), or
    // clicking a card lists a different set than the card counted. A past-due waiting task stays in waiting.
    if (!skipKpi && kpiFilter) {
      const kWaiting = TASK_STATUS_WAITING.includes(eff);
      const kOverdue = !kWaiting && isOverdue(t);
      if (kpiFilter === 'waiting'    && !kWaiting) return false;
      if (kpiFilter === 'overdue'    && !kOverdue) return false;
      if (kpiFilter === 'todo'       && !(!kWaiting && !kOverdue && TASK_STATUS_TODO.includes(eff))) return false;
      if (kpiFilter === 'inprogress' && !(!kWaiting && !kOverdue && TASK_STATUS_IN_PROGRESS.includes(eff))) return false;
    }
    if (searchQ) {
      const q = searchQ.toLowerCase();
      const matchTitle = t.title?.toLowerCase().includes(q);
      const matchClient = t.client_name?.toLowerCase().includes(q);
      const matchProperty = t.property_name?.toLowerCase().includes(q);
      const matchCode = t._requestCode?.toLowerCase().includes(q);
      // Task type: match both the display label ("Consultant") and the raw code ("consultant").
      const matchType = (TASK_TYPE_LABELS[t.task_type] || '').toLowerCase().includes(q) || t.task_type?.toLowerCase().includes(q);
      if (!matchTitle && !matchClient && !matchProperty && !matchCode && !matchType) return false;
    }
    return true;
  });
  // Column sort. sortKey '' = default (query order: created_at desc, or due_date asc on My open). Status sorts by
  // STATUS_ORDER (lifecycle) and Priority by PRIORITIES index (severity) — never alphabetical. Header click: asc → desc → default.
  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); }
    else if (sortDir === 'asc') { setSortDir('desc'); }
    else { setSortKey(''); setSortDir('asc'); }
  };
  const sortRows = (arr) => {
    if (!sortKey) return arr;
    const dir = sortDir === 'desc' ? -1 : 1;
    const val = (t) => {
      switch (sortKey) {
        case 'title':    return (t.title || '').toLowerCase();
        case 'type':     return (TASK_TYPE_LABELS[t.task_type] || t.task_type || '').toLowerCase();
        case 'client':   return (t.client_name || '').toLowerCase();
        case 'assignee': return (t.assigned_to_name || '').toLowerCase();
        case 'assigner': return (t.assigned_by_name || '').toLowerCase();
        case 'priority': return PRIORITIES.indexOf(t.priority);                       // Low=0 … Urgent=3 (severity)
        case 'status':   return STATUS_ORDER.indexOf(t._displayStatus || t.status);   // lifecycle order
        case 'due':      return t.due_date ? _localMidnight(t.due_date).getTime() : 0;
        default:         return 0;
      }
    };
    return [...arr].sort((a, b) => {
      if (sortKey === 'due') {   // nulls always last, both directions
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
      }
      const av = val(a), bv = val(b);
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  };
  // Open list for the active tab (all three sources are open-only now): My = tasks, Assigned = assignedTasks, All =
  // allTasks (person-filtered via applyFilters when the roster is clicked).
  const displayTasks = activeTab === 'my' ? tasks : activeTab === 'assigned' ? assignedTasks : allTasks;
  const openList = sortRows(applyFilters(displayTasks));
  // Completed/cancelled ("terminal") split — from the scoped paged query, or the DB search while searching. Any tab.
  const searchingTerminal = !!searchQ.trim();
  const terminalF  = sortRows(applyFilters(searchingTerminal ? searchTerminal : terminal));
  const doneF      = terminalF.filter(t => t.status === 'Complete');
  const cancelledF = terminalF.filter(t => t.status === 'Cancelled');
  // group_id → count, from the loaded allTasks, for the row "⧉ group of N" indicator (cheap map vs per-row scans).
  const groupSizes = {};
  (allTasks || []).forEach(t => { if (t.group_id) groupSizes[t.group_id] = (groupSizes[t.group_id] || 0) + 1; });
  // "Newly done" badge = assignedNewlyDone (head-counted in loadTasks; assigned loads open-only so it can't be counted here).
  const assignedSeenKey = `wh_assigned_seen_${staffUser?.id || 'anon'}`;

  // Workload roster (top of All Tasks) — "who has capacity NOW", sorted ASCENDING by active so the lightest
  // load surfaces first. ALL THREE numbers now come from staff_workload_counts(); no bucket is computed here
  // any more, so the local WORKLOAD_ACTIVE / WORKLOAD_WAITING aliases are gone. The status sets live in SQL,
  // mirroring statuses.js.
  // "From the group" — notes on SIBLING tasks (independent-task model: each task keeps its own thread; this is a
  // read-only aggregate so a note on a sibling isn't invisible). Confidential siblings need no filter here: the
  // siblings fetch is an ordinary select, so RLS has already withheld any the viewer isn't in the circle for.
  const groupNotes = (groupSiblings || [])
    .filter(sib => sib.id !== selectedTask?.id)
    .flatMap(sib => (sib.email_thread || []).filter(ev => ev.direction === 'note').map(ev => ({ ...ev, _sibName: sib.assigned_to_name || '—', _sibId: sib.id })))
    .sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));
  // Team Workload roster — visible to everyone now (was admin-only).
  // active/waiting come from staff_workload_counts() (SECURITY DEFINER), NOT from allTasks: RLS hides other
  // people's confidential tasks from this client, so anything derived here reads them as free capacity.
  // The RPC also folds PTO coverage — a covered task counts ONCE, for the COVERER, so the owner reads lower
  // while away and the coverer reads higher. That is real capacity, and it is the OPPOSITE of the
  // productivity report, which credits open work to the OWNER on purpose (see src/reports/cards/Productivity.jsx:13-18).
  const workloadRoster = staffList
    .filter(st => st.active !== false)
    .map(st => {
      const sid = st.id || st.staff_id;
      // ⛔ "no row" ≠ "no data". staff_workload_counts() GROUPs BY staff_id over open tasks, so a staffer with
      // nothing open simply has no row — that is ZERO work, and must read 0. '—' is reserved for the RPC being
      // unavailable (workloadCounts null), the one case where we genuinely don't know. Conflating them showed
      // idle staff as unknown and hid the real failure state behind an ordinary Tuesday.
      const haveCounts = !!workloadCounts;
      const wl = workloadCounts?.get(sid);
      // Bubbles are CAPACITY, so active re-includes the overdue slices: an overdue task is still
      // work this person is carrying, and a panel sorted "lightest load first" must not rank
      // someone with five late tasks as the freest on the team. The KPI cards read the same row
      // as EXCLUSIVE buckets. Same numbers, two presentations, one source.
      const wlOverdue = wl ? wl.overdue_todo + wl.overdue_in_progress : 0;
      return {
        sid, name: st.name,
        active:  haveCounts ? (wl ? wl.todo + wl.in_progress + wlOverdue : 0) : null,   // null → '—' (unavailable) only
        waiting: haveCounts ? (wl ? wl.waiting : 0) : null,
        overdue: haveCounts ? wlOverdue : null,   // from the RPC now — sees confidential tasks, excludes waiting
      };
    })
    .sort((a, b) => (a.active ?? Infinity) - (b.active ?? Infinity));   // ascending: most capacity first

  // Assignee column shows on All Tasks (now everyone) and Tasks-I-Assigned. On assigned-completed rows, show who + when.
  const showAssigneeCol = activeTab === 'all' || activeTab === 'assigned';
  // Who may MUTATE the selected task (status, attachments, edit, subtasks): assignee / assigner / active coverer.
  // Everyone can still SEE and comment (notes stay open). canEdit/canEditSub in the detail use this same set inline.
  const canMutateSelected = !!selectedTask && (
    staffUser?.id === selectedTask.assigned_to ||
    staffUser?.id === selectedTask.assigned_by ||
    coverageActive.some(cv => cv.task_id === selectedTask.id && cv.covered_by_id === staffUser?.id)
  );
  const renderTaskRow = (task, showCompletion) => {
    const days = daysUntil(task.due_date);
    const overdue = isOverdue(task);
    // No confidentiality check here — see the note at the All-Tasks fetch. A confidential task the viewer
    // isn't in the circle for never reaches this function, so every row rendered is one they may read.
    // Completed/Cancelled rows are history, not work — recede them: subtle tint, muted title, outline "View" button.
    // Non-colour cues (button shape, title weight, the zone divider above the Completed header) keep it accessible;
    // the Complete/Cancelled badge and the ✓ completer line keep their own colours and stay fully legible.
    const isTerminal = TASK_STATUS_TERMINAL.includes(task.status);
    const baseBg = isTerminal ? (isDark ? 'rgba(255,255,255,0.022)' : 'rgba(0,0,0,0.022)') : 'transparent';
    return (
      <tr key={task.id} style={{ cursor: 'pointer', background: baseBg }}
        onClick={() => { setSelectedTask(task); setReplyTo(''); setReplyCc(''); setReplyText(''); setReplySubject(''); }}
        onMouseEnter={e=>{ e.currentTarget.style.background=c.rowHover; }}
        onMouseLeave={e=>e.currentTarget.style.background=baseBg}>
        <td style={s.td}>
          <div style={{ fontWeight:'600', color: isTerminal ? c.sub : c.text }}>{task.title}</div>
          {Array.isArray(task.subtasks) && task.subtasks.length > 0 && <div style={{ fontSize:'10px', color:c.muted, marginTop:'2px' }}>☑ {task.subtasks.filter(s=>s.done).length}/{task.subtasks.length}</div>}
          {task.group_id && groupSizes[task.group_id] > 1 && <div style={{ fontSize:'10px', color:'#60a5fa', marginTop:'2px' }}>⧉ group of {groupSizes[task.group_id]}</div>}
          {task.parent_task_id && <div style={{ fontSize:'10px', color:'#60a5fa', marginTop:'2px' }}>↳ subtask</div>}
          {task._covering && <div style={{ fontSize:'10px', color:'#60a5fa', fontWeight:700, marginTop:'2px' }}>🛟 Covering for {task.assigned_to_name}</div>}
          {task._requestCode && <div style={{ fontSize:'10px', color:'#956f1d', fontFamily:'ui-monospace, "SF Mono", Menlo, monospace', fontWeight:'700', marginTop:'2px' }}>{task._requestCode}</div>}
          {task.source==='email_paste' && <div style={{ fontSize:'10px', color:'var(--red)', fontFamily:'monospace', marginTop:'2px' }}>📧 from email</div>}
        </td>
        <td style={{ ...s.td, fontSize:'11px', color:c.muted }}>{TASK_TYPE_LABELS[task.task_type]||task.task_type}</td>
        <td style={s.td}>
          {task.client_name && <div style={{ fontSize:'12px', color:c.text }}>{task.client_name}</div>}
          {task.property_name && <div style={{ fontSize:'11px', color:c.muted }}>{task.property_name}</div>}
          {!task.client_name && !task.property_name && <span style={{ color:c.muted, fontSize:'12px' }}>—</span>}
        </td>
        {showAssigneeCol && <td style={{ ...s.td, fontSize:'12px', color:c.sub }}>{task.assigned_to_name||'—'}</td>}
        {activeTab === 'my' && <td style={{ ...s.td, fontSize:'12px', color:c.sub }}>{task.assigned_by && task.assigned_by === task.assigned_to ? 'self' : (task.assigned_by_name || '—')}</td>}
        <td style={s.td}><span style={s.badge(PRIORITY_COLORS[task.priority]||'96,165,250')}>{task.priority}</span></td>
        <td style={s.td}>
          <span style={s.badge(STATUS_COLORS[task._displayStatus || task.status]||'96,165,250')}>{task._displayStatus || task.status}</span>
          {showCompletion && task.status==='Complete' && (
            <div style={{ fontSize:'10px', color:c.muted, marginTop:'3px' }}>✓ {task.completed_by || '—'}{task.completed_at ? ` · ${new Date(task.completed_at).toLocaleDateString()}` : ''}</div>
          )}
          {task.status==='Cancelled' && task.cancellation_reason && (
            <div style={{ fontSize:'10px', color:'#f87171', marginTop:'3px' }}>✕ {task.cancellation_reason}</div>
          )}
        </td>
        <td style={s.td}>
          {task.due_date ? (
            <div style={{ fontSize:'12px', color: overdue?'#f87171':(days>=0&&days<=3)?'#fbbf24':c.sub, fontWeight: overdue?'700':'400' }}>
              {overdue ? '⚠ ' : ''}{_localMidnight(task.due_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
            </div>
          ) : <span style={{ color:c.muted, fontSize:'12px' }}>—</span>}
        </td>
        <td style={{ ...s.td, textAlign:'center' }}>
          <button style={ isTerminal
              ? { ...s.btnSm, background:'transparent', color:c.sub, border:`1px solid ${c.inputBorder}` }
              : { ...s.btnSm, background:'linear-gradient(135deg,#B40000,#9C0000)', color:'white' } }
            onClick={e => { e.stopPropagation(); setSelectedTask(task); }}>
            {isTerminal ? 'View' : 'Open →'}
          </button>
        </td>
      </tr>
    );
  };
  const sectionRow = (label, count) => (
    <tr><td colSpan="8" style={{ ...s.td, background:c.rowHover, fontWeight:700, fontSize:'11px', color:c.sub, textTransform:'uppercase', letterSpacing:'0.06em' }}>{label} ({count})</td></tr>
  );

  // KPI buckets (reflect CURRENT USER's workload — tasks here is already filtered to My Tasks)
  // TO DO = admin is the bottleneck (needs action from me)
  // WAITING = someone else is the bottleneck (client, consultant, external)
  // OVERDUE = past due AND on-us (TO-DO bucket) — an external-blocked task that's slipped is not my bandwidth, so it
  //   doesn't inflate this number. The per-row ⚠ still shows on EVERY past-due task (isOverdue, unchanged) so a
  //   slipped waiting-on-client task is still visible to chase — it's just not counted against me here.
  // Use _displayStatus (live-recomputed) when present, otherwise fall back to t.status (DB value)
  const effectiveStatus = (t) => t._displayStatus || t.status;
  // Cards are TAB-AWARE and reflect the active non-KPI filters, so the numbers can never contradict the list below:
  // My Tasks → my open set; Tasks I Assigned → tasks I delegated; All Tasks → the whole team. skipKpi so a card isn't
  // narrowed by its own selection.
  const kpiSource = activeTab === 'assigned' ? assignedTasks : activeTab === 'all' ? allTasks : tasks;
  const kpiBase   = applyFilters(kpiSource, true);
  // On MY TASKS and TASKS I ASSIGNED the client-derived numbers are already exact — RLS returns every task
  // where you are the assignee or the assigner, confidential included. Only ALL TASKS undercounts, and only
  // there is the RPC substituted.
  // ASSIGNEE IS ANSWERABLE, the other filters are not: the RPC is GROUPED BY staff_id, so "this person's
  // open work" is a row lookup. Search / client / status / priority ask a question the aggregate cannot
  // answer, and quietly ignoring a filter the user set would be a worse lie than the undercount — those
  // keep the client-derived fallback (which still can't see confidential tasks; the rows aren't here).
  const kpiOtherFilters = !!filterStatus || !!filterPriority || !!filterClient || !!searchQ.trim();
  const kpiFromRpc      = activeTab === 'all' && !kpiOtherFilters && !!workloadCounts;
  // Assignee-filtered → that person's row alone. A person with no open tasks has NO row in the RPC result,
  // which is zero work, not missing data — hence [].filter(Boolean) reducing to zeros rather than a fallback.
  const rpcRows = !kpiFromRpc ? null
    : filterAssignee ? [workloadCounts.get(filterAssignee)].filter(Boolean)
    : [...workloadCounts.values()];
  const rpcTotals = rpcRows
    ? rpcRows.reduce((a, r) => ({
        todo: a.todo + r.todo, ip: a.ip + r.in_progress, waiting: a.waiting + r.waiting,
        overdue: a.overdue + r.overdue_todo + r.overdue_in_progress,
      }), { todo: 0, ip: 0, waiting: 0, overdue: 0 })
    : null;
  // ALL open work across everyone, all five buckets — for the All Tasks tab label. Deliberately separate
  // from rpcTotals, which is scoped to the cards' current tab and filters; this one must ignore both,
  // because the tab count it sits beside (allTasks.length) does too. null when the RPC is unavailable.
  const rpcOpenTotal = workloadCounts
    ? [...workloadCounts.values()].reduce((n, r) => n + r.todo + r.in_progress + r.waiting + r.overdue_todo + r.overdue_in_progress, 0)
    : null;
  // The client fallbacks apply the SAME precedence as the SQL — not waiting, not overdue, then status — so
  // a filtered view uses the same definition as an unfiltered one and the buckets stay exclusive either way.
  const notWaitingNotOverdue = t => !TASK_STATUS_WAITING.includes(effectiveStatus(t)) && !isOverdue(t);
  const toDoCount       = rpcTotals ? rpcTotals.todo    : kpiBase.filter(t => notWaitingNotOverdue(t) && TASK_STATUS_TODO.includes(effectiveStatus(t))).length;
  const inProgressCount = rpcTotals ? rpcTotals.ip      : kpiBase.filter(t => notWaitingNotOverdue(t) && TASK_STATUS_IN_PROGRESS.includes(effectiveStatus(t))).length;
  const waitingCount    = rpcTotals ? rpcTotals.waiting : kpiBase.filter(t => TASK_STATUS_WAITING.includes(effectiveStatus(t))).length;
  // ⛔ THE FOUR CARDS ARE MUTUALLY EXCLUSIVE and sum to all open tasks: every task lands in exactly one.
  // Precedence WAITING > OVERDUE > status, decided in SQL (see the migration header). A past-due WAITING
  // task STAYS in waiting and never counts as overdue — it is late on an external party, not on the
  // assignee. ⛔ This REVERSES the previous rule, under which overdue was `past-due ∩ ON_US` and could pull
  // a task out of waiting. The client fallback below mirrors the same precedence so a filtered view doesn't
  // silently switch definitions — but it still cannot see confidential tasks, which is why the RPC exists.
  const overdueCount = rpcTotals ? rpcTotals.overdue : kpiBase.filter(t => {
    const eff = effectiveStatus(t);
    if (TASK_STATUS_WAITING.includes(eff)) return false;   // waiting wins over overdue
    return isOverdue(t);
  }).length;

  return (
    <div style={{ maxWidth:'1200px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'20px' }}>
        <div>
          <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'24px', fontWeight:'700', color:c.text, marginBottom:'4px' }}>Task Engine</div>
          <div style={{ fontSize:'13px', color:c.muted }}>Manage workflow, paste emails, track correspondence</div>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          <button style={s.btnGhost} onClick={() => { setShowPTO(!showPTO); setPtoMsg(''); }}>🏖 Set PTO Coverage</button>
          <button style={s.btn} onClick={() => { setShowNewTask(!showNewTask); setSelectedTask(null); setSeriesGroupId(null); }}>+ New Task</button>
        </div>
      </div>

      {/* FLOATING so it's visible regardless of scroll — fixes the "form closed, did it work?" gap for New Task / detail
          actions (the banner used to render here inline, above panels, and scrolled off when acting on one). */}
      {saveMsg && <div style={{ position:'fixed', top:'16px', left:'50%', transform:'translateX(-50%)', zIndex:10000, background: saveMsgErr ? '#2e1414' : '#0f2a1e', border:`1px solid ${saveMsgErr ? 'rgba(248,113,113,0.5)' : 'rgba(52,211,153,0.5)'}`, borderRadius:'8px', padding:'10px 18px', fontSize:'13px', color: saveMsgErr ? '#f87171' : '#34d399', boxShadow:'0 6px 24px rgba(0,0,0,0.35)' }}>{saveMsgErr ? '⚠' : '✓'} {saveMsg}</div>}

      {/* Stat cards — the ONE 4-bucket model (statuses.js). In Progress is its own bucket (actively worked), not Waiting.
          TO DO · on us     = Open, Waiting on Intake, Ready to Deliver   (Intake is on US → To Do, not "on them")
          IN PROGRESS       = In Progress, Partial Delivered              (actively being worked)
          WAITING · on them = Waiting on Client, Waiting on Consultant    (blocked on an EXTERNAL party)
          OVERDUE           = past due AND on-us (To Do ∪ In Progress); external-blocked slippage still shows the row ⚠ */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'20px' }}>
        {[
          { key:'todo',       label:'To Do · on us',     value:toDoCount,       color:'96,165,250' },
          { key:'inprogress', label:'In Progress',       value:inProgressCount, color:'52,211,153' },
          { key:'waiting',    label:'Waiting · on them', value:waitingCount,    color:'167,139,250' },
          { key:'overdue',    label:'Overdue',           value:overdueCount,    color:'248,113,113' },
        ].map(s2 => {
          // Click → filter My Tasks to this bucket (switch to My Tasks tab); click the active card again → clear.
          const active = kpiFilter === s2.key;
          return (
            <div key={s2.key} onClick={() => setKpiFilter(active ? '' : s2.key)}
              title={active ? 'Click to clear this filter' : 'Click to filter this tab to these tasks'}
              style={{ ...s.card, padding:'16px 20px', marginBottom:0, borderTop:`3px solid rgb(${s2.color})`, cursor:'pointer',
                outline: active ? `2px solid rgb(${s2.color})` : 'none', outlineOffset:'-1px',
                ...(active ? { background:`rgba(${s2.color},0.10)` } : {}) }}>
              <div style={{ fontSize:'11px', color:c.muted, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'6px' }}>{s2.label}{active ? ' · ✕ clear' : ''}</div>
              <div style={{ fontSize:'28px', fontWeight:'700', color:`rgb(${s2.color})`, fontFamily:"'Archivo',sans-serif" }}>{s2.value}</div>
            </div>
          );
        })}
      </div>

      {/* PTO Panel */}
      {showPTO && (
        <div style={s.card}>
          <div style={s.cardHeader}><div style={s.cardTitle}>🏖 Set PTO Coverage</div><button style={s.btnGhost} onClick={() => { setShowPTO(false); setPtoMsg(''); }}>✕</button></div>
          <div style={{ padding:'16px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
            <div><label style={s.label}>Who's on PTO</label>
              {isAdmin ? (
                <select style={s.select} value={ptoForm.pto_staff_id || staffUser?.id}
                  onChange={e=>{ const st=staffList.find(x=>(x.id||x.staff_id)===e.target.value); setPtoForm(p=>({...p, pto_staff_id:e.target.value, pto_staff_name:st?.name||'', cover_staff_id:'', cover_name:''})); setPtoTaskIds([]); }}>
                  {staffList.filter(st=>st.active!==false).map(st=>{ const sid=st.id||st.staff_id; return <option key={sid} value={sid}>{sid===staffUser?.id ? `${st.name} (you)` : st.name}</option>; })}
                </select>
              ) : (
                <input style={{ ...s.input, opacity:0.7 }} value={`${staffUser?.name || 'You'} (you)`} disabled />
              )}
            </div>
            <div><label style={s.label}>Cover Person</label>
              <select style={s.select} value={ptoForm.cover_staff_id} onChange={e => { const st=staffList.find(x=>x.id===e.target.value); setPtoForm(p=>({...p,cover_staff_id:e.target.value,cover_name:st?.name||''})); }}>
                <option value="">— Select Staff —</option>
                {staffList.filter(st=>(st.id||st.staff_id)!==(ptoForm.pto_staff_id||staffUser?.id)).map(st=><option key={st.id} value={st.id}>{st.name}</option>)}
              </select>
            </div>
            <div><label style={s.label}>Notes</label><input style={s.input} value={ptoForm.notes} onChange={e=>setPtoForm(p=>({...p,notes:e.target.value}))} placeholder="Optional note..." /></div>
            <div><label style={s.label}>Start Date</label><input style={s.input} type="date" value={ptoForm.start_date} onChange={e=>setPtoForm(p=>({...p,start_date:e.target.value}))} /></div>
            <div><label style={s.label}>End Date</label><input style={s.input} type="date" value={ptoForm.end_date} onChange={e=>setPtoForm(p=>({...p,end_date:e.target.value}))} /></div>
          </div>
          {ptoForm.cover_staff_id && ptoForm.start_date && ptoForm.end_date && (() => {
            const ptoStaffId = ptoForm.pto_staff_id || staffUser?.id;
            const ptoWhose = ptoStaffId === staffUser?.id ? 'your' : `${ptoForm.pto_staff_name || 'their'}'s`;
            // From allTasks (universal now) so an admin can see the SELECTED person's tasks. Exclude terminal only —
            // ⛔ CONFIDENTIAL TASKS ARE NOT COVERABLE — the frozen circle (rls-assessment §203) is exactly
            // {assigned_by, assigned_to} with NO coverer arm, so the tasks RLS policy would hide a covered
            // private task from the very person meant to cover it: loadTasks' covered-tasks fetch (:280)
            // runs under that policy and returns nothing. This UI previously let the assignee or assigner
            // tick one anyway ("the coverer pierces via coverageActive" — it does not), which wrote a
            // task_coverage row that did nothing and silently dropped the task for the whole PTO window.
            // Blocked for EVERYONE here, including the pair, because the block is a property of the task,
            // not of who's arranging. To hand one off: reassign it, or un-privatize it first.
            const coverable = allTasks.filter(t => t.assigned_to === ptoStaffId && t.status !== 'Complete' && t.status !== 'Cancelled');
            // Two different things: `blocked` = cannot be covered (any private task). `rowMasked` = the
            // arranger may not READ it, so the title is withheld too. Masked implies blocked, never the reverse.
            const rowBlocked = (t) => !!t.is_private;
            const rowMasked = (t) => t.is_private && t.assigned_to !== staffUser?.id
              && t.assigned_by !== staffUser?.id
              && !coverageActive.some(cv => cv.task_id === t.id && cv.covered_by_id === staffUser?.id);
            const allBlocked = coverable.length > 0 && coverable.every(rowBlocked);
            const anyBlocked = coverable.some(rowBlocked);
            return (
              <div style={{ padding:'0 16px 12px' }}>
                <label style={s.label}>Tasks {ptoForm.cover_name || 'the coverer'} will cover <span style={{ textTransform:'none', fontWeight:'400', color:c.muted }}>({ptoWhose} open tasks; private excluded)</span></label>
                {coverable.length === 0 ? <div style={{ fontSize:'12px', color:c.muted }}>No coverable tasks.</div> :
                  allBlocked ? <div style={{ fontSize:'12px', color:c.muted, fontStyle:'italic' }}>🔒 All of {ptoForm.pto_staff_name || 'this person'}'s open tasks are confidential, so none can be covered — they must be reassigned or un-privatized to hand off.</div> :
                  <>
                  <div style={{ display:'flex', flexDirection:'column', gap:'4px', maxHeight:'180px', overflowY:'auto', border:`1px solid ${c.inputBorder}`, borderRadius:'8px', padding:'8px' }}>
                    {coverable.map(t => { const b = rowBlocked(t); const m = rowMasked(t); return (
                      <label key={t.id} style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px', color: b?c.muted:c.text, cursor: b?'default':'pointer' }}
                        title={b ? "Confidential tasks can't be covered — reassign or un-privatize to hand off." : undefined}>
                        <input type="checkbox" disabled={b} checked={!b && ptoTaskIds.includes(t.id)}
                          onChange={e => setPtoTaskIds(prev => e.target.checked ? [...prev, t.id] : prev.filter(x => x !== t.id))} />
                        <span style={{ flex:1, fontStyle: b?'italic':'normal' }}>{m ? '🔒 Confidential task' : t.title}</span>
                        {b && !m && <span style={{ fontSize:'10px', color:c.muted, fontStyle:'italic' }}>🔒 confidential</span>}
                        <span style={{ fontSize:'10px', color:c.muted }}>{t._displayStatus || t.status}</span>
                      </label>
                    ); })}
                  </div>
                  {anyBlocked && <div style={{ fontSize:'11px', color:c.muted, marginTop:'6px', fontStyle:'italic' }}>Confidential tasks can't be covered — reassign or un-privatize to hand off.</div>}
                  </>}
              </div>
            );
          })()}
          <div style={{ padding:'0 16px 16px', display:'flex', gap:'8px', alignItems:'center' }}>
            <button style={{ ...s.btn, opacity: ptoSaving ? 0.6 : 1, cursor: ptoSaving ? 'default' : 'pointer' }} disabled={ptoSaving} onClick={savePTO}>{ptoSaving ? 'Saving…' : 'Save Coverage'}</button>
            <button style={s.btnGhost} onClick={() => { setShowPTO(false); setPtoMsg(''); }}>Close</button>
            {ptoMsg && <span style={{ fontSize:'13px', fontWeight:600, color: ptoMsg.startsWith('✓') ? '#34d399' : '#f87171' }}>{ptoMsg}</span>}
          </div>
          {ptoCoverage.length > 0 && (
            <div style={{ borderTop:`1px solid ${c.border}`, padding:'12px 16px' }}>
              <div style={{ fontSize:'11px', color:c.muted, marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.07em' }}>Active Coverage</div>
              {ptoCoverage.map(p => (
                <div key={p.id} style={{ fontSize:'12px', color:c.sub, marginBottom:'4px' }}>
                  <strong style={{ color:c.text }}>{p.staff_name}</strong> → covered by <strong style={{ color:'var(--red)' }}>{p.cover_name}</strong> · {p.start_date} to {p.end_date}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* New Task Panel */}
      {showNewTask && (
        <div style={s.card}>
          <div style={s.cardHeader}>
            <div style={s.cardTitle}>+ New Task</div>
            <div style={{ display:'flex', gap:'8px' }}>
              {extractedTasks.length === 0 && <button style={{ ...s.btn, opacity: savingTask ? 0.6 : 1, cursor: savingTask ? 'wait' : 'pointer' }} disabled={savingTask} onClick={() => saveTask()}>{savingTask ? 'Saving…' : 'Save Task'}</button>}
              <button style={s.btnGhost} onClick={() => { setShowNewTask(false); setEmailPaste(''); setDroppedEmail(null); setDropError(null); setPendingAttachments([]); setAttachNote(null); setExtractedTasks([]); setClientProperties([]); setSelectedProperties([]); setSeriesGroupId(null); }}>✕</button>
            </div>
          </div>
          <div style={{ padding:'16px' }}>
            {/* Email paste section — extraction opens the review panel below (always reviewed, even at N=1) */}
            <div style={{ background: isDark ? 'rgba(180,0,0,0.06)' : 'rgba(180,0,0,0.05)', border:'1px solid rgba(180,0,0,0.2)', borderRadius:'8px', padding:'14px', marginBottom:'16px' }}>
              <div style={{ fontSize:'12px', fontWeight:'700', color:'var(--red)', marginBottom:'8px' }}>🤖 Wigley Email Extractor</div>
              {/* ⛔ THE INSTRUCTION DIFFERS BY OUTLOOK, so it must not name one route as though it
                  were universal. Save As is available in NEW Outlook and GREYED OUT in classic —
                  the reverse of what we assumed — and classic is most of the office. Telling
                  everyone "File → Save As" would send the majority to a disabled menu item and
                  teach them the feature is broken. */}
              <div style={{ fontSize:'12px', color:c.muted, marginBottom:'8px' }}>
                Paste the email text below — Wigley extracts every task it finds. To bring the
                images and attachments across too, add them with the file box further down, or drop
                a saved copy of the email here.
              </div>

              {/* ── DROP ZONE ─────────────────────────────────────────────────────────────────
                  ⛔ onDragOver MUST preventDefault or the browser opens the file instead of
                  firing onDrop. dragleave is guarded on currentTarget: it also fires when the
                  pointer crosses a CHILD element, which would flicker the highlight off mid-drag. */}
              <div
                onDragOver={e => { e.preventDefault(); if (!dropActive) setDropActive(true); }}
                onDragLeave={e => { if (e.currentTarget === e.target) setDropActive(false); }}
                onDrop={handleEmailDrop}
                style={{
                  border: `2px dashed ${dropActive ? 'var(--red)' : c.border}`,
                  borderRadius:'8px', padding:'18px', marginBottom:'8px', textAlign:'center',
                  background: dropActive ? (isDark ? 'rgba(180,0,0,0.12)' : 'rgba(180,0,0,0.06)') : 'transparent',
                  fontSize:'12px', color: dropActive ? 'var(--red)' : c.muted, transition:'all .12s',
                }}>
                {dropBusy
                  ? '📧 Reading the email…'
                  : dropActive
                    ? 'Drop to read the email'
                    : (
                      <>
                        {/* ⛔ WORDING IS DELIBERATE. This used to read "Drag an email here from
                            Outlook", which described the one thing that does not reliably work: an
                            Outlook drag hands over a VIRTUAL file the browser materialises
                            inconsistently. A SAVED file dropped from the desktop is an ordinary
                            file drag and always works. Telling people to do the unreliable thing
                            teaches them the feature is broken. */}
                        📧 Drop a <b>saved</b> email file here (.msg or .eml)
                        <div style={{ marginTop:'6px' }}>
                          <button type="button" style={{ ...s.btnGhost, fontSize:'12px' }}
                            onClick={() => emailFileInputRef.current && emailFileInputRef.current.click()}>
                            Browse for a saved email…
                          </button>
                        </div>
                        {/* Named per app, because the route genuinely differs and getting sent to a
                            greyed-out menu is worse than no instruction at all. */}
                        <div style={{ marginTop:'7px', fontSize:'11px', color:c.muted, lineHeight:'1.55' }}>
                          <b>New Outlook:</b> File → Save As, then drop the file here — you get the whole
                          email, images and attachments.<br />
                          <b>Classic Outlook:</b> Save As is usually disabled. Drag the message to your
                          Desktop and drop that file here, or add the images below.<br />
                          Several at once is fine.
                        </div>
                      </>
                    )}
                {/* Multiple: save-then-drop makes batching natural, and quietly ignoring files
                    2..n would lose work the user believed they had handed over. */}
                <input ref={emailFileInputRef} type="file" multiple accept=".msg,.eml,message/rfc822,application/vnd.ms-outlook"
                  style={{ display:'none' }}
                  onChange={e => { const f = Array.from(e.target.files || []); e.target.value = ''; ingestEmailFiles(f); }} />
              </div>

              {/* ⛔ A parse failure is shown in full and the box is left EMPTY — never a half-filled
                  form that looks ready. The paste box below still works, and the message says so. */}
              {dropError && (
                <div style={{ background: isDark ? 'rgba(180,0,0,0.12)' : '#fff5f5', border:'1px solid rgba(180,0,0,0.35)',
                  borderRadius:'8px', padding:'10px 12px', marginBottom:'8px', fontSize:'12px', color:'var(--red)', lineHeight:'1.5' }}>
                  ⛔ {dropError}
                </div>
              )}

              {/* What actually came out of the file. Counted, not assumed — an attachment we found
                  but have not yet imported is stated as such rather than quietly ignored. */}
              {droppedEmail && !dropError && (
                <div style={{ background: isDark ? 'rgba(26,34,56,0.6)' : '#f0f4ff', border:`1px solid ${c.border}`,
                  borderRadius:'8px', padding:'10px 12px', marginBottom:'8px', fontSize:'12px', color:c.sub, lineHeight:'1.6' }}>
                  <div style={{ fontWeight:600, color:c.text }}>
                    {droppedEmail.subject || '(no subject)'}
                  </div>
                  <div>
                    From: {formatSender(droppedEmail.from) || '—'}
                    {droppedEmail.sentAt && ` · ${new Date(droppedEmail.sentAt).toLocaleString()}`}
                    {` · ${droppedEmail.format.toUpperCase()}`}
                  </div>
                  {pendingAttachments.length > 0 && (
                    <div style={{ marginTop:'4px' }}>
                      📎 {pendingAttachments.length} attachment{pendingAttachments.length === 1 ? '' : 's'}
                      <span style={{ color:c.muted }}> — will be attached to every task created</span>
                    </div>
                  )}
                </div>
              )}

              {/* ⛔ What the clipboard actually carried, counted BOTH ways — captured and not. An
                  image that silently vanishes from a pasted body is the complaint that started
                  this; an image reported as uncapturable, with the reason, is actionable. */}
              {attachNote && !dropError && (
                <div style={{ background: isDark ? 'rgba(26,34,56,0.6)' : '#f0f4ff', border:`1px solid ${c.border}`,
                  borderRadius:'8px', padding:'10px 12px', marginBottom:'8px', fontSize:'12px', color:c.sub, lineHeight:'1.5' }}>
                  {attachNote}
                </div>
              )}

              <textarea style={{ ...s.textarea, minHeight:'100px', fontFamily:'monospace', fontSize:'12px', marginBottom:'8px' }}
                onPaste={handleEmailPaste}
                placeholder="Paste an email here — images on the clipboard come too" value={emailPaste}
                onChange={e => { setEmailPaste(e.target.value); if (droppedEmail) { setDroppedEmail(null); setDropError(null); } }} />
              <button style={{ ...s.btn, opacity: extracting ? 0.7 : 1 }} onClick={() => extractFromEmail()} disabled={extracting || dropBusy || !emailPaste.trim()}>
                {extracting ? '🤖 Extracting…' : '🤖 Extract Tasks from Email'}
              </button>
            </div>

            {/* ── ATTACHMENTS, BEFORE THE TASK EXISTS ──────────────────────────────────────────
                ⛔ The one route here that does not depend on Outlook. Drop, browse, or paste an
                image; on a phone the same input offers Photos and the camera. Files added here
                join the SAME list an email's attachments go into, so they stage, dedupe and roll
                back through one path. */}
            <div
              onDragOver={e => { e.preventDefault(); if (!attachDragActive) setAttachDragActive(true); }}
              onDragLeave={e => { if (e.currentTarget === e.target) setAttachDragActive(false); }}
              onDrop={e => { e.preventDefault(); setAttachDragActive(false); addPendingFiles(e.dataTransfer.files); }}
              onPaste={handleAttachPaste}
              tabIndex={0}
              style={{
                border: `2px dashed ${attachDragActive ? 'var(--red)' : c.border}`,
                borderRadius:'8px', padding:'14px', marginBottom:'16px', textAlign:'center',
                background: attachDragActive ? (isDark ? 'rgba(180,0,0,0.12)' : 'rgba(180,0,0,0.06)') : 'transparent',
                fontSize:'12px', color: attachDragActive ? 'var(--red)' : c.muted, outline:'none', transition:'all .12s',
              }}>
              📎 Drop files here, <button type="button" style={{ ...s.btnGhost, fontSize:'12px', padding:'3px 9px' }}
                onClick={() => attachInputRef.current && attachInputRef.current.click()}>browse</button>
              {' '}or click here and paste an image
              <div style={{ marginTop:'5px', fontSize:'11px', color:c.muted }}>
                Screenshots, PDFs, anything. On a phone this opens Photos and the camera.
              </div>
              {/* ⛔ NO accept= ON PURPOSE. Restricting it stops iOS offering "Take Photo" and
                  "Photo Library", which is the entire mobile capability. */}
              <input ref={attachInputRef} type="file" multiple style={{ display:'none' }}
                onChange={e => { const f = Array.from(e.target.files || []); e.target.value = ''; addPendingFiles(f); }} />
            </div>

            {/* Every pending file, individually removable — nothing is attached by surprise. */}
            {pendingAttachments.length > 0 && (
              <div style={{ marginBottom:'16px', border:`1px solid ${c.border}`, borderRadius:'8px', overflow:'hidden' }}>
                <div style={{ padding:'8px 12px', background: isDark ? 'rgba(26,34,56,0.6)' : '#f0f4ff',
                  fontSize:'11px', fontWeight:700, color:c.text, textTransform:'uppercase', letterSpacing:'0.06em' }}>
                  {pendingAttachments.length} file{pendingAttachments.length === 1 ? '' : 's'} will be attached
                  {extractedTasks.length > 1 && ` to each of the ${extractedTasks.length} tasks`}
                </div>
                {pendingAttachments.map((a, i) => (
                  <div key={`${a.filename || 'file'}-${i}`} style={{ display:'flex', alignItems:'center', gap:'8px',
                    padding:'7px 12px', borderTop:`1px solid ${c.border}`, fontSize:'12px', color:c.sub }}>
                    <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {/* An inline image usually has no name of its own; say what it is rather than
                          printing an empty cell. Staging gives it a distinguishing one. */}
                      {a.filename || <em style={{ color:c.muted }}>{a.disposition === 'inline' ? 'inline image' : 'unnamed file'}</em>}
                    </span>
                    <span style={{ color:c.muted, fontSize:'11px' }}>
                      {a.disposition === 'inline' ? 'inline · ' : ''}{Math.max(1, Math.round(a.size / 1024))} KB
                    </span>
                    <button type="button" onClick={() => removePendingAt(i)}
                      style={{ ...s.btnGhost, fontSize:'11px', padding:'2px 8px' }}>Remove</button>
                  </div>
                ))}
              </div>
            )}

            {extractedTasks.length > 0 ? (
              /* ── REVIEW PANEL — N editable cards, include/exclude, add-row, Create N ── */
              <div>
                {/* Batch header: default assignee applies to every card (each still overridable) */}
                <div style={{ display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', marginBottom:'12px', paddingBottom:'12px', borderBottom:`1px solid ${c.border}` }}>
                  <span style={{ fontSize:'12px', fontWeight:700, color:c.text }}>{extractedTasks.length} task{extractedTasks.length!==1?'s':''} found</span>
                  <label style={{ fontSize:'12px', color:c.muted }}>Default assignee:</label>
                  <select style={{ ...s.select, width:'auto', fontSize:'12px' }} value=""
                    onChange={e=>{ const st=staffList.find(x=>(x.id||x.staff_id)===e.target.value); if(st) setExtractedTasks(prev=>prev.map(t=>({...t, assigned_to: st.id||st.staff_id, assigned_to_name: st.name}))); }}>
                    <option value="">— apply to all —</option>
                    {staffList.map(st=><option key={st.id||st.staff_id} value={st.id||st.staff_id}>{st.name}</option>)}
                  </select>
                </div>
                {extractedTasks.map(t => (
                  <div key={t._key} style={{ border:`1px solid ${t.include?'var(--red)':c.border}`, borderRadius:'9px', padding:'10px', marginBottom:'10px', opacity: t.include?1:0.55 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
                      <input type="checkbox" checked={t.include} onChange={e=>setDraft(t._key,{ include:e.target.checked })} />
                      <input style={{ ...s.input, flex:1, fontWeight:600 }} value={t.title} onChange={e=>setDraft(t._key,{ title:e.target.value })} placeholder="Task title" />
                      <button onClick={()=>setExtractedTasks(prev=>prev.filter(x=>x._key!==t._key))} style={{ background:'none', border:'none', color:c.muted, cursor:'pointer', fontSize:'16px' }}>×</button>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'8px' }}>
                      <select style={{ ...s.select, fontSize:'12px' }} value={t.task_type} onChange={e=>setDraft(t._key,{ task_type:e.target.value })}>
                        {TASK_TYPES.map(v=><option key={v} value={v}>{TASK_TYPE_LABELS[v]}</option>)}
                      </select>
                      <select style={{ ...s.select, fontSize:'12px' }} value={t.priority} onChange={e=>setDraft(t._key,{ priority:e.target.value })}>
                        {PRIORITIES.map(p=><option key={p} value={p}>{p}</option>)}
                      </select>
                      <input type="date" style={{ ...s.input, fontSize:'12px' }} value={t.due_date} onChange={e=>setDraft(t._key,{ due_date:e.target.value })} />
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'8px' }}>
                      <div>
                        <select style={{ ...s.select, fontSize:'12px', width:'100%' }} value={t.assigned_to} onChange={e=>{ const st=staffList.find(x=>(x.id||x.staff_id)===e.target.value); setDraft(t._key,{ assigned_to:e.target.value, assigned_to_name: st?.name||'', _suggestedFromEmail:false }); }}>
                          <option value="">— assign to me —</option>
                          {staffList.map(st=><option key={st.id||st.staff_id} value={st.id||st.staff_id}>{st.name}</option>)}
                        </select>
                        {t._suggestedFromEmail && <div style={{ fontSize:'10px', color:'#60a5fa', fontWeight:700, marginTop:'2px' }}>✉ suggested from email — verify</div>}
                      </div>
                      <select style={{ ...s.select, fontSize:'12px' }} value={t.client_id} onChange={e=>{ const cl=(allClients||[]).find(x=>x.id===e.target.value); setDraft(t._key,{ client_id:e.target.value, client_name: cl?.name||'' }); }}>
                        <option value="">{t.client_name ? `Unmatched: ${t.client_name} — pick client` : '— no client —'}</option>
                        {(allClients||[]).map(cl=><option key={cl.id} value={cl.id}>{cl.name}</option>)}
                      </select>
                    </div>
                    {(() => {
                      // Dedup WARNING (not a block): a same-title task already assigned to this person and not yet terminal.
                      // Matches title + assignee + non-terminal — you can still create it (the #7 case: same task done twice).
                      const title = t.title?.trim();
                      if (!title) return null;
                      const assignee = t.assigned_to || staffUser?.id;
                      const dup = (allTasks || []).some(x => x.title?.trim().toLowerCase() === title.toLowerCase() && x.assigned_to === assignee && !TASK_STATUS_TERMINAL.includes(x.status));
                      return dup ? <div style={{ fontSize:'10px', color:'#fbbf24', fontWeight:700, marginTop:'4px' }}>⚠ A task with this title is already assigned to {t.assigned_to_name || staffUser?.name || 'you'} and isn’t complete — you can still create it.</div> : null;
                    })()}
                    {t.description && <div style={{ fontSize:'11px', color:c.muted, whiteSpace:'pre-wrap' }}>{t.description}</div>}
                  </div>
                ))}
                <div style={{ display:'flex', gap:'8px', marginTop:'10px', alignItems:'center' }}>
                  <button style={{ ...s.btnGhost, fontSize:'12px' }} onClick={()=>setExtractedTasks(prev=>[...prev, { _key:`new-${prev.length}-${Math.random().toString(36).slice(2)}`, include:true, title:'', task_type:'internal', priority:'Normal', due_date:'', description:'', client_id:'', client_name:'', property_name:'', assigned_to:staffUser?.id||'', assigned_to_name:staffUser?.name||'' }])}>＋ Add task</button>
                  <button style={{ ...s.btn, opacity: creatingBatch?0.6:1 }} disabled={creatingBatch} onClick={createBatch}>{creatingBatch ? 'Creating…' : `Create ${extractedTasks.filter(t=>t.include && t.title.trim()).length} task${extractedTasks.filter(t=>t.include && t.title.trim()).length!==1?'s':''}`}</button>
                </div>
              </div>
            ) : (
            /* Task form */
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div style={{ gridColumn:'span 2' }}>
                <label style={s.label}>Task Title *</label>
                <input style={s.input} value={newTask.title} onChange={e=>setNewTask(p=>({...p,title:e.target.value}))} placeholder="What needs to be done?" />
              </div>
              <div>
                <label style={s.label}>Type</label>
                <select style={s.select} value={newTask.task_type} onChange={e=>{ setNewTask(p=>({...p,task_type:e.target.value,client_id:'',client_name:'',property_id:'',property_name:''})); setClientProperties([]); setSelectedProperties([]); }}>
                  {TASK_TYPES.map(t=><option key={t} value={t}>{TASK_TYPE_LABELS[t]}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>Priority</label>
                <select style={s.select} value={newTask.priority} onChange={e=>setNewTask(p=>({...p,priority:e.target.value}))}>
                  {PRIORITIES.map(p=><option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>Assign To</label>
                <select style={s.select} value={newTask.assigned_to} onChange={e=>{ const st=staffList.find(x=>x.id===e.target.value); setNewTask(p=>({...p,assigned_to:e.target.value,assigned_to_name:st?.name||''})); }}>
                  <option value="">— Assign to self —</option>
                  {staffList.map(st=><option key={st.id} value={st.id}>{st.name} — {st.title||st.role}</option>)}
                </select>
                {/* Confidential — settable on ANY task now (I'm the creator = the assigner). Visible to assigner + assignee only. */}
                <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:c.sub, marginTop:'8px', cursor:'pointer' }}>
                  <input type="checkbox" checked={!!newTask.is_private} onChange={e=>setNewTask(p=>({...p, is_private:e.target.checked}))} />
                  🔒 Confidential — only you and the assignee can see this
                </label>
              </div>
              <div>
                <label style={s.label}>Due Date</label>
                <input style={{ ...s.input, cursor:'pointer' }} type="date" value={newTask.due_date} onChange={e=>setNewTask(p=>({...p,due_date:e.target.value}))} min={(()=>{ const n=new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })()} />
              </div>
              {newTask.task_type === 'internal' ? (
                <div style={{ gridColumn:'span 2', background:isDark?'rgba(180,0,0,0.05)':'rgba(180,0,0,0.04)', border:'1px solid rgba(180,0,0,0.15)', borderRadius:'7px', padding:'10px 14px', fontSize:'12px', color:c.muted }}>
                  Internal task — no client or property required
                </div>
              ) : newTask.task_type === 'consultant' ? (
                <div style={{ gridColumn:'span 2', background:isDark?'rgba(180,0,0,0.05)':'rgba(180,0,0,0.04)', border:'1px solid rgba(180,0,0,0.15)', borderRadius:'7px', padding:'10px 14px', fontSize:'12px', color:c.muted }}>
                  Consultant task — consultant picker not built yet, so no consultant/client is attached (a client picker here would be wrong).
                </div>
              ) : (
                <>
                  <div>
                    <label style={s.label}>Client</label>
                    <select style={s.select} value={newTask.client_id} onChange={e => {
                      const cl = (allClients||[]).find(x=>x.id===e.target.value);
                      setNewTask(p=>({...p, client_id:e.target.value, client_name:cl?.name||'', property_id:'', property_name:''}));
                      setSelectedProperties([]);
                      if (cl) loadClientProperties(cl.id, cl.name);
                      else setClientProperties([]);
                    }}>
                      <option value="">— Select Client —</option>
                      {(allClients||[]).map(c2=><option key={c2.id} value={c2.id}>{c2.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>
                      {loadingProps ? 'Loading...' : newTask.client_id ? `Properties (${clientProperties.length})` : 'Properties'}
                    </label>
                    {!newTask.client_id ? (
                      <div style={{ fontSize:'12px', color:c.muted, padding:'8px 0' }}>Select a client first</div>
                    ) : clientProperties.length === 0 ? (
                      <div style={{ fontSize:'12px', color:c.muted, padding:'8px 0' }}>{loadingProps ? 'Loading...' : 'No properties found'}</div>
                    ) : (
                      <div style={{ background:c.input, border:`1px solid ${c.inputBorder}`, borderRadius:'7px', maxHeight:'180px', overflowY:'auto', padding:'6px' }}>
                        <label style={{ display:'flex', alignItems:'center', gap:'8px', padding:'5px 6px', fontSize:'12px', color:c.muted, cursor:'pointer', borderBottom:`1px solid ${c.border}`, marginBottom:'4px' }}>
                          <input type="checkbox"
                            checked={selectedProperties.length === clientProperties.length && clientProperties.length > 0}
                            onChange={e => {
                              if (e.target.checked) {
                                const ids = clientProperties.map(p=>p.id);
                                setSelectedProperties(ids);
                                setNewTask(prev=>({...prev, property_name:clientProperties.map(p=>p.name).join(', '), property_id:''}));
                              } else {
                                setSelectedProperties([]);
                                setNewTask(prev=>({...prev, property_name:'', property_id:''}));
                              }
                            }}
                          /> <strong>Select All</strong>
                        </label>
                        {clientProperties.map(prop => (
                          <label key={prop.id} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'5px 6px', fontSize:'12px', color:c.text, cursor:'pointer', borderRadius:'4px' }}
                            onMouseEnter={e=>e.currentTarget.style.background=c.rowHover}
                            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                            <input type="checkbox"
                              checked={selectedProperties.includes(prop.id)}
                              onChange={e => {
                                const updated = e.target.checked ? [...selectedProperties, prop.id] : selectedProperties.filter(id=>id!==prop.id);
                                setSelectedProperties(updated);
                                const names = clientProperties.filter(p=>updated.includes(p.id)).map(p=>p.name).join(', ');
                                setNewTask(prev=>({...prev, property_name:names, property_id:updated.length===1?updated[0]:''}));
                              }}
                            />
                            <div>
                              <div style={{ fontWeight:'500' }}>{prop.name}</div>
                              {prop.city && <div style={{ fontSize:'10px', color:c.muted }}>{prop.city}, {prop.state}</div>}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
              <div style={{ gridColumn:'span 2' }}>
                <label style={s.label}>Description / Notes</label>
                <textarea style={s.textarea} value={newTask.description} onChange={e=>setNewTask(p=>({...p,description:e.target.value}))} placeholder="Additional context..." />
              </div>
              {/* Bottom Save — same action as the header button, so you don't scroll back up after filling the form.
                  "Save & add for another" saves this copy and keeps the form open (change the assignee, save the next);
                  the whole series shares one group_id so the copies are linked. */}
              <div style={{ gridColumn:'span 2', display:'flex', justifyContent:'flex-end', gap:'8px' }}>
                <button style={s.btnGhost} onClick={() => saveTask(true)}>Save &amp; add for another{seriesGroupId ? ' ✓' : ''}</button>
                <button style={{ ...s.btn, opacity: savingTask ? 0.6 : 1, cursor: savingTask ? 'wait' : 'pointer' }} disabled={savingTask} onClick={() => saveTask()}>{savingTask ? 'Saving…' : 'Save Task'}</button>
              </div>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Selected Task Detail — Split Screen */}
      {selectedTask && (
        <div style={{ ...s.card, marginBottom:'20px' }}>
          {/* Step 6: Email-sent toast — auto-closes after 5s, clickable to return immediately, × to stay */}
          {emailSentToast && (
            <div
              onClick={() => { setEmailSentToast(null); setSelectedTask(null); }}
              style={{
                margin:'0 0 14px 0',
                padding:'10px 16px',
                background: 'linear-gradient(135deg, rgba(52,211,153,0.15), rgba(5,150,105,0.10))',
                border:'1px solid rgba(52,211,153,0.4)',
                borderRadius:'8px',
                display:'flex', alignItems:'center', justifyContent:'space-between', gap:'10px',
                cursor:'pointer',
              }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <span style={{ color:'#34d399', fontSize:'14px', fontWeight:'700' }}>✓</span>
                <span style={{ fontSize:'12px', color: isDark ? '#86efac' : '#047857', fontWeight:'600' }}>
                  Email logged.
                  {emailSentToast.purpose === 'consultant_notify' ? ' Task marked Complete.' : emailSentToast.purpose === 'consultant_partial' ? ' Items marked delivered. Task moved to Partial Delivered.' : emailSentToast.purpose === 'client_request' ? ' Task moved to In Progress.' : ''}
                  &nbsp;Returning to tasks in {emailSentToast.secondsLeft}s… (click to go now)
                </span>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setEmailSentToast(null); }}
                style={{ background:'transparent', border:'none', color: isDark ? '#86efac' : '#047857', fontSize:'14px', cursor:'pointer', padding:'0 4px' }}
                title="Stay on this task">
                ✕
              </button>
            </div>
          )}
          <div style={s.cardHeader}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <button
                style={{
                  display:'inline-flex', alignItems:'center', gap:'6px',
                  padding:'7px 14px',
                  background: isDark ? 'rgba(180,0,0,0.10)' : 'rgba(180,0,0,0.08)',
                  border:'1px solid rgba(180,0,0,0.4)',
                  borderRadius:'7px',
                  color: isDark ? '#d4b078' : 'var(--red-dark)',
                  fontSize:'12px', fontWeight:'700',
                  cursor:'pointer',
                  fontFamily:"'Public Sans',system-ui,sans-serif",
                  transition:'all 0.15s',
                }}
                title="Return to tasks list (Esc)"
                onClick={() => setSelectedTask(null)}>
                <span style={{ fontSize:'14px', lineHeight:'1' }}>←</span>
                <span>Back to Tasks</span>
              </button>
              <div>
                {selectedTask._requestCode && (
                  <div style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: '11px', fontWeight: '700', color: '#956f1d', marginBottom: '2px' }}>
                    {selectedTask._requestCode}
                  </div>
                )}
                <div style={s.cardTitle}>{selectedTask.title}</div>
              </div>
              <span style={s.badge(PRIORITY_COLORS[selectedTask.priority]||'96,165,250')}>{selectedTask.priority}</span>
              <span style={s.badge(STATUS_COLORS[selectedTask.status]||'96,165,250')}>{selectedTask.status}</span>
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              {/* Status is a MUTATION → gated to assignee/assigner/coverer. Non-mutators see the read-only badge above.
                  The three doc-request delivery states are auto-only: if the task is IN one, the dropdown shows that
                  value + only the safe terminal exits (Complete/Cancelled) — lateral moves to a waiting status would
                  strand the flow (recompute skips them; DocumentIntake's auto-flip fires only from Waiting on Intake).
                  Otherwise the plain settable list. */}
              {canMutateSelected && (() => {
                const auto = TASK_STATUS_AUTO_ONLY.includes(selectedTask.status);
                const opts = auto ? [selectedTask.status, TASK_STATUS.COMPLETE, TASK_STATUS.CANCELLED] : TASK_STATUS_SETTABLE;
                return (
              <select style={{ ...s.select, width:'auto', fontSize:'12px', padding:'5px 10px' }} value={cancelPending ? 'Cancelled' : selectedTask.status}
                title={auto ? 'Set by the document-request workflow — advance it with the workflow buttons; only Complete/Cancel can be set by hand' : undefined}
                onChange={e => { const v = e.target.value; if (v === 'Cancelled') { setCancelPending(true); setCancelReason(''); } else { setCancelPending(false); updateTaskStatus(selectedTask.id, v); } }}>
                {opts.map(st=><option key={st} value={st}>{st}</option>)}
              </select>
                );
              })()}
              {(selectedTask.is_private ? staffUser?.id === selectedTask.assigned_by : (staffUser?.id === selectedTask.assigned_by || isAdmin)) && (
                <button onClick={deleteTask} style={{ ...s.select, width:'auto', fontSize:'12px', padding:'5px 10px', color:'#f87171', border:'1px solid rgba(248,113,113,0.4)', cursor:'pointer', background:'transparent' }}>🗑 Delete</button>
              )}
            </div>
          </div>
          {/* Inline cancel-reason — reveals when Cancelled is picked; nothing writes until it has content. Reason is read by the assigner. */}
          {cancelPending && (
            <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 16px', background:'rgba(248,113,113,0.06)', borderBottom:`1px solid ${c.border}` }}>
              <input autoFocus style={{ ...s.input, flex:1, fontSize:'12px' }} placeholder="Reason for cancelling (required — visible to whoever assigned it)…" value={cancelReason} onChange={e=>setCancelReason(e.target.value)} />
              <button disabled={!cancelReason.trim()} onClick={async ()=>{ await updateTaskStatus(selectedTask.id, 'Cancelled', cancelReason); setCancelPending(false); setCancelReason(''); }}
                style={{ ...s.btn, padding:'6px 12px', fontSize:'12px', opacity: cancelReason.trim()?1:0.5, cursor: cancelReason.trim()?'pointer':'default' }}>Confirm cancel</button>
              <button onClick={()=>{ setCancelPending(false); setCancelReason(''); }} style={{ ...s.btnGhost, padding:'6px 12px', fontSize:'12px' }}>Never mind</button>
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', minHeight:'400px' }}>
            {/* LEFT: Email thread / source */}
            <div style={{ borderRight:`1px solid ${c.border}`, padding:'16px', overflowY:'auto', maxHeight:'600px' }}>
              <div style={{ fontSize:'11px', color:'var(--red)', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:'monospace', marginBottom:'12px' }}>Activity & Notes</div>

              {/* Task info */}
              <div style={{ background: isDark ? 'rgba(0,0,0,0.2)' : '#f8f9fc', borderRadius:'8px', padding:'12px', marginBottom:'14px', fontSize:'12px' }}>
                {selectedTask.client_name && <div style={{ color:c.sub, marginBottom:'3px' }}>Client: <strong style={{ color:c.text }}>{selectedTask.client_name}</strong></div>}
                {selectedTask.property_name && <div style={{ color:c.sub, marginBottom:'3px' }}>Property: <strong style={{ color:c.text }}>{selectedTask.property_name}</strong></div>}
                {selectedTask.due_date && !editMode && <div style={{ color:c.sub, marginBottom:'3px' }}>Due: <strong style={{ color: isOverdue(selectedTask) ? '#f87171' : c.text }}>{_localMidnight(selectedTask.due_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</strong></div>}
                {(() => {
                  // Confidential → assigner only (an admin can't see it, and the assignee shouldn't expand the circle).
                  const canReassign = selectedTask.is_private
                    ? staffUser?.id === selectedTask.assigned_by
                    : (staffUser?.id === selectedTask.assigned_to || staffUser?.id === selectedTask.assigned_by || isAdmin);
                  return canReassign ? (
                    <div style={{ display:'flex', alignItems:'center', gap:'6px', color:c.sub }}>
                      Assigned:
                      <select value={selectedTask.assigned_to || ''} onChange={e => {
                          const nid = e.target.value;
                          if (!nid || nid === selectedTask.assigned_to) return;
                          const st = staffList.find(x => (x.id || x.staff_id) === nid);
                          // Confirm — reassign is the highest-stakes action (ownership move + trail + roster shift). Decline → the
                          // controlled select reverts on the next render (value is bound to selectedTask.assigned_to).
                          if (window.confirm(`Reassign from ${selectedTask.assigned_to_name || '—'} to ${st?.name || 'them'}? They take over this task.`)) reassignTask(nid);
                        }}
                        style={{ ...s.select, width:'auto', fontSize:'12px', padding:'4px 8px' }}>
                        {staffList.map(st => { const sid = st.id || st.staff_id; return <option key={sid} value={sid}>{st.name}</option>; })}
                      </select>
                    </div>
                  ) : (
                    selectedTask.assigned_to_name && <div style={{ color:c.sub }}>Assigned: <strong style={{ color:c.text }}>{selectedTask.assigned_to_name}</strong></div>
                  );
                })()}
                {selectedTask.assigned_by_name && <div style={{ color:c.sub, marginTop:'3px' }}>Assigned by: <strong style={{ color:c.text }}>{selectedTask.assigned_by_name}</strong> <span style={{ color:c.muted, fontSize:'10px' }}>· any reassignments show in Activity below</span></div>}
                {staffUser?.id === selectedTask.assigned_by && (
                  <label style={{ display:'flex', alignItems:'center', gap:'6px', marginTop:'8px', cursor:'pointer', color:c.sub }}>
                    <input type="checkbox" checked={!!selectedTask.is_private} onChange={e=>togglePrivate(e.target.checked)} />
                    🔒 Confidential — only the assigner &amp; assignee can see this
                  </label>
                )}
                {/* Group view (read-only) — siblings share group_id; click one to jump. The Duplicate action lives in the footer. */}
                {(() => {
                  // groupSiblings is fetched directly (any status, incl. completed). No confidentiality filter:
                  // RLS returns only siblings the viewer is in the circle for, so anything here is already theirs.
                  const group = groupSiblings || [];
                  if (group.length <= 1) return null;
                  return (
                    <div style={{ marginTop:'8px' }}>
                      <div style={{ fontSize:'10px', color:c.muted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px' }}>⧉ Group ({group.length})</div>
                      {group.slice().sort((a, b) => (a.created_at || '').localeCompare(b.created_at || '')).map(g => (
                        <div key={g.id} onClick={() => { if (g.id !== selectedTask.id) setSelectedTask(g); }}
                          style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color: g.id === selectedTask.id ? c.text : c.sub, cursor: g.id === selectedTask.id ? 'default' : 'pointer', padding:'1px 0' }}>
                          <span style={s.badge(STATUS_COLORS[g._displayStatus || g.status] || '96,165,250')}>{g._displayStatus || g.status}</span>
                          <span>{g.assigned_to_name || '—'}</span>
                          {g.id === selectedTask.id && <span style={{ color:c.muted }}>(this)</span>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {detailMsg && <div style={{ marginTop:'8px', fontSize:'11px', color:'#f87171' }}>⚠ {detailMsg}</div>}
                {/* ── Actions footer — divider-separated from the read-only fields above. Shows the buttons, or the active
                    edit form / duplicate picker (trigger and panel in the same place). ── */}
                {(() => {
                  const canDup = staffUser?.id === selectedTask.assigned_to || staffUser?.id === selectedTask.assigned_by || isAdmin;
                  if (!canMutateSelected && !canDup) return null;
                  return (
                    <div style={{ borderTop:`1px solid ${c.border}`, marginTop:'12px', paddingTop:'10px' }}>
                      {editMode ? (
                        <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                          <div>
                            <div style={{ fontSize:'10px', color:c.muted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'2px' }}>Title</div>
                            <input style={{ ...s.input, fontSize:'12px', padding:'5px 8px', width:'100%' }} value={editDraft.title} onChange={e=>setEditDraft(d=>({...d, title:e.target.value}))} />
                          </div>
                          <div style={{ display:'flex', gap:'8px' }}>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:'10px', color:c.muted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'2px' }}>Due date</div>
                              <input type="date" style={{ ...s.input, fontSize:'12px', padding:'5px 8px', width:'100%' }} value={editDraft.due_date || ''} onChange={e=>setEditDraft(d=>({...d, due_date:e.target.value}))} />
                            </div>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:'10px', color:c.muted, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'2px' }}>Priority</div>
                              <select style={{ ...s.select, fontSize:'12px', padding:'5px 8px', width:'100%' }} value={editDraft.priority} onChange={e=>setEditDraft(d=>({...d, priority:e.target.value}))}>
                                {PRIORITIES.map(p=><option key={p} value={p}>{p}</option>)}
                              </select>
                            </div>
                          </div>
                          <div style={{ display:'flex', gap:'8px', alignItems:'center', marginTop:'2px' }}>
                            <button style={{ ...s.btn, padding:'5px 12px', fontSize:'12px', opacity: savingEdit?0.6:1 }} disabled={savingEdit} onClick={saveEdits}>{savingEdit ? 'Saving…' : 'Save Changes'}</button>
                            <button style={{ ...s.btnGhost, padding:'5px 12px', fontSize:'12px' }} onClick={()=>{ setEditMode(false); setEditMsg(''); }}>Cancel</button>
                            {editMsg && <span style={{ fontSize:'11px', color:'#f87171' }}>{editMsg}</span>}
                          </div>
                        </div>
                      ) : dupOpen ? (
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap' }}>
                          <span style={{ fontSize:'11px', color:c.muted }}>⧉ Duplicate to:</span>
                          <select style={{ ...s.select, width:'auto', fontSize:'12px', padding:'4px 8px' }} value={dupAssignee} onChange={e => setDupAssignee(e.target.value)}>
                            <option value="">— assign the copy to —</option>
                            {staffList.map(st => { const sid = st.id || st.staff_id; return <option key={sid} value={sid}>{st.name}</option>; })}
                          </select>
                          <button disabled={dupBusy || !dupAssignee} onClick={() => { const st = staffList.find(x => (x.id || x.staff_id) === dupAssignee); duplicateTask(dupAssignee, st?.name || ''); }}
                            style={{ ...s.btn, padding:'4px 10px', fontSize:'12px', opacity: (dupBusy || !dupAssignee) ? 0.5 : 1 }}>{dupBusy ? '…' : 'Create copy'}</button>
                          <button onClick={() => { setDupOpen(false); setDupAssignee(''); }} style={{ ...s.btnGhost, padding:'4px 10px', fontSize:'12px' }}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                          {canMutateSelected && <button onClick={()=>{ setEditDraft({ title:selectedTask.title||'', due_date:selectedTask.due_date||'', priority:selectedTask.priority||'Normal' }); setEditMode(true); setEditMsg(''); }} style={{ ...s.btnGhost, fontSize:'11px', padding:'5px 10px', display:'inline-flex', alignItems:'center', gap:'5px' }}>✎ Edit title, due date &amp; priority</button>}
                          {canDup && <button onClick={() => { setDupOpen(true); setDupAssignee(''); }} style={{ ...s.btnGhost, fontSize:'11px', padding:'5px 10px', display:'inline-flex', alignItems:'center', gap:'5px' }}>⧉ Duplicate for someone else</button>}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* ☑ Subtasks — a simple checklist (tasks.subtasks JSONB). Ticks save immediately. */}
              {(() => {
                const canEditSub = staffUser?.id === selectedTask.assigned_to
                  || staffUser?.id === selectedTask.assigned_by
                  || coverageActive.some(cv => cv.task_id === selectedTask.id && cv.covered_by_id === staffUser?.id);
                const subs = Array.isArray(selectedTask.subtasks) ? selectedTask.subtasks : [];
                if (subs.length === 0 && !canEditSub) return null;   // nothing to show a non-editor
                const doneCount = subs.filter(s => s.done).length;
                return (
                  <div style={{ background: isDark ? 'rgba(0,0,0,0.2)' : '#f8f9fc', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                    <div style={{ fontSize:'11px', color:'var(--red)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'8px' }}>
                      ☑ Subtasks{subs.length ? ` (${doneCount}/${subs.length})` : ''}
                    </div>
                    {subs.length === 0 && <div style={{ fontSize:'12px', color:c.muted, marginBottom:'8px' }}>No subtasks yet.</div>}
                    {subs.map(st => {
                      const promoted = !!st.child_task_id;   // this line is delegated to a real child task
                      return (
                      <div key={st.id} style={{ padding:'3px 0' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'12px' }}>
                          {/* Promoted items are ticked by the child's completion (write-through), not by hand → disabled. */}
                          <input type="checkbox" checked={!!st.done} disabled={!canEditSub || promoted} onChange={() => toggleSubtask(st.id)} title={promoted ? 'Completion is driven by the assigned task' : ''} />
                          {promoted
                            ? <span onClick={() => openChildTask(st.child_task_id)} title="Open the assigned task"
                                style={{ flex:1, textDecoration: st.done ? 'line-through' : 'none', color: st.done ? c.muted : '#60a5fa', cursor:'pointer' }}>{st.text}</span>
                            : <span style={{ flex:1, textDecoration: st.done ? 'line-through' : 'none', color: st.done ? c.muted : c.text }}>{st.text}</span>}
                          {promoted && <span onClick={() => openChildTask(st.child_task_id)} title="Open the assigned task" style={{ fontSize:'10px', color:'#60a5fa', fontWeight:700, whiteSpace:'nowrap', cursor:'pointer' }}>→ {st.child_assignee_name || 'assigned'}</span>}
                          {canEditSub && !promoted && promoteSub !== st.id && <button onClick={() => { setPromoteSub(st.id); setPromoteAssignee(''); }} style={{ background:'none', border:'none', color:'#60a5fa', cursor:'pointer', fontSize:'11px', whiteSpace:'nowrap' }} title="Assign this subtask to someone as its own task">Assign →</button>}
                          {/* Remove is hidden once promoted — removing the line would lose the parent's view of delegated work. */}
                          {canEditSub && !promoted && <button onClick={() => removeSubtask(st.id)} style={{ background:'none', border:'none', color:c.muted, cursor:'pointer', fontSize:'13px' }} title="Remove subtask">×</button>}
                        </div>
                        {promoteSub === st.id && (
                          <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap', margin:'4px 0 6px 24px' }}>
                            <select style={{ ...s.select, width:'auto', fontSize:'12px', padding:'4px 8px' }} value={promoteAssignee} onChange={e => setPromoteAssignee(e.target.value)}>
                              <option value="">— assign to —</option>
                              {staffList.map(stf => { const sid = stf.id || stf.staff_id; return <option key={sid} value={sid}>{stf.name}</option>; })}
                            </select>
                            <button disabled={promoteBusy || !promoteAssignee} onClick={() => { const stf = staffList.find(x => (x.id || x.staff_id) === promoteAssignee); promoteSubtask(st.id, promoteAssignee, stf?.name || ''); }}
                              style={{ ...s.btn, padding:'4px 10px', fontSize:'12px', opacity: (promoteBusy || !promoteAssignee) ? 0.5 : 1 }}>{promoteBusy ? '…' : 'Assign'}</button>
                            <button onClick={() => { setPromoteSub(''); setPromoteAssignee(''); }} style={{ ...s.btnGhost, padding:'4px 10px', fontSize:'12px' }}>Cancel</button>
                            {selectedTask.is_private && <span style={{ fontSize:'10px', color:c.muted }}>🔒 stays confidential to you &amp; them</span>}
                          </div>
                        )}
                      </div>
                      );
                    })}
                    {canEditSub && (
                      <div style={{ display:'flex', gap:'6px', marginTop:'8px' }}>
                        <input style={{ ...s.input, flex:1, fontSize:'12px', padding:'5px 8px' }} placeholder="Add a subtask…" value={newSubtask}
                          onChange={e => setNewSubtask(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newSubtask.trim()) addSubtask(); }} />
                        <button style={{ ...s.btnGhost, fontSize:'12px', padding:'5px 10px' }} disabled={!newSubtask.trim()} onClick={addSubtask}>Add</button>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* 📎 Attachments — task-only files (task_attachments → file_exchange, reuses the 'client-files' bucket) */}
              <div style={{ background: isDark ? 'rgba(0,0,0,0.2)' : '#f8f9fc', borderRadius:'8px', padding:'12px', marginBottom:'14px' }}>
                <div style={{ fontSize:'11px', color:'var(--red)', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:'8px' }}>
                  📎 Attachments{taskAttachments.length ? ` (${taskAttachments.length})` : ''}
                </div>
                {taskAttachments.length === 0 && <div style={{ fontSize:'12px', color:c.muted, marginBottom:'8px' }}>No files attached.</div>}
                {taskAttachments.map(a => (
                  <div key={a.id} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'6px 8px', borderRadius:'6px', fontSize:'12px', border:`1px solid ${c.border}`, marginBottom:'6px' }}>
                    <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      <button onClick={()=>openAttachment(a.file?.storage_path)} style={{ background:'none', border:'none', color:'#60a5fa', cursor:'pointer', padding:0, fontSize:'12px', textDecoration:'underline' }} title="Open">
                        {a.file?.file_name || 'file'}
                      </button>
                      {a.file?.file_size_bytes ? <span style={{ color:c.muted, marginLeft:'6px' }}>{Math.max(1, Math.round(a.file.file_size_bytes/1024))} KB</span> : null}
                      {a.file?.folder_key && a.file.folder_key !== DOC_KEYS.taskAttachments && <span style={{ color:'#34d399', marginLeft:'6px', fontSize:'10px' }}>✓ filed → {a.file.folder_name}</span>}
                    </span>
                    {canMutateSelected && <>
                      <button onClick={()=>openFiling(a)} style={{ background:'none', border:'none', color:'#60a5fa', cursor:'pointer', fontSize:'11px', fontWeight:600, textDecoration:'underline' }} title="File this document (adds a client/property scope — stays on the task)">File</button>
                      <button onClick={()=>removeTaskAttachment(a)} style={{ background:'none', border:'none', color:c.muted, cursor:'pointer', fontSize:'14px' }} title={(!a.file?.client_id && !a.file?.property_id && (!a.file?.direction || a.file.direction==='ntrg_internal')) ? 'Remove — this file is only on this task, so it will be deleted (confirms first)' : 'Remove (unlink from this task — the file stays where it is filed)'}>×</button>
                    </>}
                  </div>
                ))}
                {/* File-document panel — intake's filing fields, filing the attachment's existing row in place */}
                {filingFor && (
                  <div style={{ border:`1px solid rgba(180,0,0,0.35)`, borderRadius:'9px', padding:'12px', marginTop:'8px', background: isDark?'rgba(180,0,0,0.06)':'rgba(180,0,0,0.04)' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                      <div style={{ fontSize:'11px', fontWeight:700, color:'var(--red)', textTransform:'uppercase', letterSpacing:'0.06em' }}>File: {filingFor.file?.file_name}</div>
                      <button onClick={()=>{ setFilingFor(null); setFilingMsg(''); }} style={{ background:'none', border:'none', color:c.muted, cursor:'pointer', fontSize:'14px' }}>✕</button>
                    </div>
                    <div style={{ fontSize:'10px', color:c.muted, marginBottom:'10px', fontStyle:'italic' }}>Adds a client/property scope to this file. It stays attached to the task.</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
                      <div>
                        <label style={s.label}>Client</label>
                        <select style={{ ...s.select, fontSize:'12px' }} value={filingForm.clientId}
                          onChange={e=>{ const cl=(allClients||[]).find(x=>x.id===e.target.value); setFilingForm(f=>({...f, clientId:e.target.value, clientName:cl?.name||'', propertyId:'', propertyName:'', parcelId:''})); setFilingParcels([]); if(e.target.value) loadFilingProps(e.target.value); else setFilingProps([]); }}>
                          <option value="">— Select client —</option>
                          {(allClients||[]).map(cl=><option key={cl.id} value={cl.id}>{cl.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={s.label}>Document Type</label>
                        <select style={{ ...s.select, fontSize:'12px' }} value={filingForm.docType} onChange={e=>{
                          const dt = e.target.value;
                          // A client-level type SELECTS the client-level option (visibly, overridably) rather
                          // than hiding the control — and drops any property the task prefilled.
                          const t = vocab?.type(dt);
                          const toClientLevel = !!t && vocab.folder(t.folder_key)?.level === 'client';
                          if (toClientLevel) setFilingParcels([]);
                          setFilingForm(f=>({...f, docType:dt, ...(toClientLevel ? { propertyId:CLIENT_LEVEL, propertyName:'', parcelId:'' } : {})}));
                        }}>
                          <option value="">— Select type —</option>
                          <DocTypeOptions vocab={vocab} current={filingForm.docType} />
                        </select>
                        {filingForm.docType && vocab?.type(filingForm.docType) && <div style={{ fontSize:'10px', color:'#d4b078', marginTop:'3px', fontFamily:'monospace' }}>→ {vocab.folder(vocab.type(filingForm.docType).folder_key)?.name}</div>}
                      </div>
                      <div>
                        <label style={s.label}>Property (optional)</label>
                        <select style={{ ...s.select, fontSize:'12px' }} value={filingForm.propertyId}
                          onChange={e=>{ const p=filingProps.find(x=>String(x.id)===e.target.value); setFilingForm(f=>({...f, propertyId:e.target.value, propertyName:p?.name||'', parcelId:''})); if(p) loadFilingParcels(p); else setFilingParcels([]); }}
                          disabled={!filingForm.clientId}>
                          <option value="">— Property level / none —</option>
                          {/* Explicit client-level: available for EVERY doc type, not just the declared four —
                              "the type says property-level but this instance isn't" is a real case. */}
                          <option value={CLIENT_LEVEL}>— File at Client Level (no property) —</option>
                          {filingProps.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={s.label}>Year</label>
                        <select style={{ ...s.select, fontSize:'12px' }} value={filingForm.year} onChange={e=>setFilingForm(f=>({...f, year:e.target.value}))}>
                          <option value="">— No year —</option>
                          {FILING_YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                        </select>
                      </div>
                      {filingForm.propertyId && filingForm.propertyId !== CLIENT_LEVEL && filingParcels.length > 0 && (
                        <div style={{ gridColumn:'span 2' }}>
                          <label style={s.label}>Parcel (optional)</label>
                          <select style={{ ...s.select, fontSize:'12px' }} value={filingForm.parcelId} onChange={e=>setFilingForm(f=>({...f, parcelId:e.target.value}))}>
                            <option value="">— Property level only —</option>
                            {filingParcels.map(p=><option key={p.id} value={p.id}>{(p.account_number||p.parcel_name)} — {p.re_pp}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:'14px', marginTop:'10px' }}>
                      <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:c.text, cursor:'pointer' }}>
                        <input type="checkbox" checked={filingForm.viewableClient} onChange={e=>setFilingForm(f=>({...f, viewableClient:e.target.checked}))} /> 👤 Viewable by Client
                      </label>
                      <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:c.text, cursor:'pointer' }}>
                        <input type="checkbox" checked={filingForm.viewableConsultant} onChange={e=>setFilingForm(f=>({...f, viewableConsultant:e.target.checked}))} /> 🏛 Viewable by Consultant
                      </label>
                    </div>
                    <div style={{ display:'flex', gap:'8px', alignItems:'center', marginTop:'10px' }}>
                      <button style={{ ...s.btn, fontSize:'12px', opacity: filingBusy?0.6:1 }} disabled={filingBusy} onClick={fileAttachment}>{filingBusy ? 'Filing…' : 'File document'}</button>
                      <button style={{ ...s.btnGhost, fontSize:'12px' }} onClick={()=>{ setFilingFor(null); setFilingMsg(''); }}>Cancel</button>
                      {filingMsg && <span style={{ fontSize:'12px', fontWeight:600, color: filingMsg.startsWith('✓') ? '#34d399' : '#f87171' }}>{filingMsg}</span>}
                    </div>
                  </div>
                )}
                {/* Upload / attach-existing are MUTATIONS → gated to assignee/assigner/coverer. The list + open above stay
                    visible to everyone (they can see the task). Confidential tasks can't be opened by non-mutators anyway. */}
                {canMutateSelected && (<>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'8px', flexWrap:'wrap' }}>
                  <label style={{ ...s.btnGhost, fontSize:'12px', cursor: attaching?'default':'pointer', opacity: attaching?0.6:1 }}>
                    {attaching ? 'Working…' : '⬆ Upload file'}
                    <input type="file" style={{ display:'none' }} disabled={attaching}
                      onChange={e=>{ const f=e.target.files?.[0]; e.target.value=''; if(f) uploadNewAttachment(f); }} />
                  </label>
                  {selectedTask.client_id && (
                    <button style={{ ...s.btnGhost, fontSize:'12px' }} disabled={attaching}
                      onClick={()=>{ const open=!attachPickerOpen; setAttachPickerOpen(open); if(open) loadClientFilesForAttach(); }}>
                      {attachPickerOpen ? 'Close' : '📁 Attach existing'}
                    </button>
                  )}
                  {attachMsg && <span style={{ fontSize:'12px', fontWeight:600, color: attachMsg.startsWith('✓') ? '#34d399' : '#f87171' }}>{attachMsg}</span>}
                </div>
                {!selectedTask.client_id && (
                  <div style={{ fontSize:'10px', color:c.muted, marginTop:'6px', fontStyle:'italic' }}>Internal task — upload only (no client to browse existing files).</div>
                )}
                {attachPickerOpen && selectedTask.client_id && (
                  <div style={{ marginTop:'8px', border:`1px solid ${c.inputBorder}`, borderRadius:'8px', maxHeight:'180px', overflowY:'auto', padding:'6px' }}>
                    {attachPickerLoading ? <div style={{ fontSize:'12px', color:c.muted, padding:'6px' }}>Loading…</div> :
                      attachPickerFiles.length === 0 ? <div style={{ fontSize:'12px', color:c.muted, padding:'6px' }}>No other files for this client.</div> :
                      attachPickerFiles.map(f => (
                        <div key={f.id} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'5px 6px', fontSize:'12px', color:c.text, borderRadius:'4px', cursor:'pointer' }}
                          onMouseEnter={e=>e.currentTarget.style.background=c.rowHover} onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                          onClick={()=>attachExisting(f.id)}>
                          <span style={{ flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.file_name}</span>
                          {f.folder_name && <span style={{ fontSize:'10px', color:c.muted }}>{f.folder_name}</span>}
                        </div>
                      ))}
                  </div>
                )}
                </>)}
              </div>

              {/* Document Request panel (shown only when task.related_type = 'document_request') */}
              {docRequest && (
                <div style={{ background: isDark ? 'rgba(180,0,0,0.06)' : 'rgba(180,0,0,0.04)', border:'1px solid rgba(180,0,0,0.3)', borderRadius:'10px', padding:'14px', marginBottom:'14px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                      <div style={{ fontSize:'11px', color:'var(--red)', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.08em' }}>📋 Document Request</div>
                      {docRequest.request_code && (
                        <span style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize:'11px', fontWeight:'700', color: isDark?'#d4b078':'#956f1d', padding:'2px 8px', background:'rgba(180,0,0,0.12)', borderRadius:'5px' }}>
                          {docRequest.request_code}
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize:'10px', padding:'3px 9px', borderRadius:'20px', fontWeight:'700', background:'rgba(180,0,0,0.15)', color:isDark?'#d4b078':'var(--red-dark)', border:'1px solid rgba(180,0,0,0.3)' }}>
                      {docReqStageLabel()}
                    </span>
                  </div>

                  {docReqLoading && <div style={{ color:c.muted, fontSize:'12px' }}>Loading…</div>}

                  {!docReqLoading && docRequestItems.filter(i => !i.removed_at).length > 0 && (
                    <>
                      {(() => {
                        const activeItems = docRequestItems.filter(i => !i.removed_at);
                        const fulfilledCount = activeItems.filter(i => i.status === 'fulfilled').length;
                        const naCount = activeItems.filter(i => i.status === 'na').length;
                        const notReadyCount = activeItems.filter(i => i.status === 'not_ready').length;
                        return (
                          <div style={{ fontSize:'11px', color:c.muted, marginBottom:'8px' }}>
                            <strong style={{ color: c.text }}>{fulfilledCount}</strong> of {activeItems.length} received
                            {naCount > 0 && <> · {naCount} N/A</>}
                            {notReadyCount > 0 && <> · <span style={{ color:'#b45309' }}>{notReadyCount} not ready</span></>}
                          </div>
                        );
                      })()}

                      <div style={{ display:'flex', flexDirection:'column', gap:'5px', marginBottom:'10px' }}>
                        {docRequestItems.filter(i => !i.removed_at).map(item => {
                          const isFulfilled = item.status === 'fulfilled';
                          const isNA = item.status === 'na';
                          const isNotReady = item.status === 'not_ready';
                          const isPending = item.status === 'pending';
                          const isDelivered = !!item.delivered_to_consultant_at;
                          const iconColor = isFulfilled ? '#22c55e' : isNA ? '#94a3b8' : isNotReady ? '#b45309' : c.muted;
                          const iconGlyph = isFulfilled ? '✓' : isNA ? '—' : isNotReady ? '⏱' : '○';
                          let rightLabel = 'pending';
                          if (isFulfilled && item.fulfilled_at) rightLabel = new Date(item.fulfilled_at).toLocaleDateString('en-US',{month:'short',day:'numeric'});
                          else if (isNA) rightLabel = 'N/A';
                          else if (isNotReady && item.promised_by_date) rightLabel = `Expected ${new Date(item.promised_by_date + 'T00:00').toLocaleDateString('en-US',{month:'short',day:'numeric'})}`;
                          // Step 5b: allow removal only while item is pending OR not_ready
                          // (Once fulfilled or N/A, the item has real downstream consequences — don't allow.)
                          const canRemove = (isPending || isNotReady) && docRequest.status !== 'fulfilled' && docRequest.status !== 'cancelled';
                          const panelOpen = !!removeItemOpen[item.id];
                          const itemBusy = !!removeItemBusy[item.id];
                          return (
                            <div key={item.id} style={{ display:'flex', flexDirection:'column' }}>
                              <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'6px 8px', background:isDark?'rgba(0,0,0,0.15)':'#ffffff', border:`1px solid ${c.border}`, borderRadius: panelOpen ? '6px 6px 0 0' : '6px', fontSize:'12px' }}>
                                <span style={{ width:'14px', textAlign:'center', color: iconColor, fontWeight:'700' }}>{iconGlyph}</span>
                                <span style={{ flex:1, color: isPending ? c.sub : c.text }}>
                                  {item.document_type}{item.other_description ? `: ${item.other_description}` : ''}
                                  {isFulfilled && item.shortcut_fulfilled ? <span style={{ fontSize:'10px', color:c.muted, marginLeft:'5px', fontStyle:'italic' }}>(already provided)</span> : null}
                                </span>
                                <span style={{ fontSize:'11px', color: isNotReady ? '#b45309' : c.muted, fontFamily:"'DM Mono',monospace", fontWeight: isNotReady ? '600' : '400' }}>{rightLabel}</span>
                                {isDelivered && (
                                  <span title={`Delivered to consultant ${new Date(item.delivered_to_consultant_at).toLocaleDateString('en-US',{month:'short',day:'numeric'})}`}
                                    style={{ fontSize:'9px', fontWeight:'700', padding:'1px 6px', borderRadius:'10px', background:'rgba(14,165,233,0.14)', color:'#0369a1', border:'1px solid rgba(14,165,233,0.32)', letterSpacing:'0.04em' }}>
                                    📦 delivered
                                  </span>
                                )}
                                {canRemove && !panelOpen && (
                                  <button
                                    title="Remove this item from the request"
                                    onClick={() => setRemoveItemOpen(prev => ({ ...prev, [item.id]: true }))}
                                    style={{ background:'transparent', border:'none', color:c.muted, cursor:'pointer', fontSize:'14px', padding:'0 4px', lineHeight:'1' }}>
                                    ✕
                                  </button>
                                )}
                              </div>
                              {panelOpen && (
                                <div style={{ padding:'10px', background: isDark ? 'rgba(248,113,113,0.06)' : 'rgba(248,113,113,0.04)', border:`1px solid rgba(248,113,113,0.25)`, borderTop:'none', borderRadius:'0 0 6px 6px' }}>
                                  <div style={{ fontSize:'11px', color:c.muted, marginBottom:'6px' }}>
                                    Why are you removing <strong style={{ color: c.text }}>{item.document_type}</strong> from this request? (required)
                                  </div>
                                  <textarea
                                    autoFocus
                                    style={{ ...s.textarea, minHeight:'60px', fontSize:'12px', marginBottom:'8px' }}
                                    placeholder="e.g., Already have latest on file; client emailed directly last week; not applicable to this property..."
                                    value={removeItemReason[item.id] || ''}
                                    onChange={e => setRemoveItemReason(prev => ({ ...prev, [item.id]: e.target.value }))}
                                    disabled={itemBusy}
                                  />
                                  <div style={{ display:'flex', gap:'6px' }}>
                                    <button
                                      style={{ ...s.btnSm, background:'#f87171', color:'#ffffff', fontWeight:'700', padding:'5px 12px', opacity: itemBusy || !(removeItemReason[item.id]||'').trim() ? 0.5 : 1 }}
                                      disabled={itemBusy || !(removeItemReason[item.id]||'').trim()}
                                      onClick={() => removeItem(item, removeItemReason[item.id] || '')}>
                                      {itemBusy ? 'Removing…' : 'Remove Item'}
                                    </button>
                                    <button
                                      style={{ ...s.btnSm, background:'transparent', color:c.sub, border:`1px solid ${c.inputBorder}`, padding:'5px 12px' }}
                                      disabled={itemBusy}
                                      onClick={() => {
                                        setRemoveItemOpen(prev => ({ ...prev, [item.id]: false }));
                                        setRemoveItemReason(prev => ({ ...prev, [item.id]: '' }));
                                      }}>
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Step 5b: "Removed Items" collapsible section — shows what was removed and why */}
                      {docRequestItems.filter(i => i.removed_at).length > 0 && (
                        <div style={{ marginBottom:'10px', borderTop:`1px dashed ${c.border}`, paddingTop:'8px' }}>
                          <button
                            onClick={() => setShowRemovedItems(prev => !prev)}
                            style={{ background:'transparent', border:'none', color:c.muted, fontSize:'10px', fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', cursor:'pointer', padding:'2px 0', display:'flex', alignItems:'center', gap:'4px' }}>
                            <span>{showRemovedItems ? '▾' : '▸'}</span>
                            Removed items ({docRequestItems.filter(i => i.removed_at).length})
                          </button>
                          {showRemovedItems && (
                            <div style={{ display:'flex', flexDirection:'column', gap:'4px', marginTop:'6px' }}>
                              {docRequestItems.filter(i => i.removed_at).map(ri => (
                                <div key={ri.id} style={{ padding:'5px 8px', background: isDark?'rgba(0,0,0,0.2)':'var(--line-soft)', border:`1px solid ${c.border}`, borderRadius:'5px', fontSize:'11px', opacity:0.75 }}>
                                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'2px' }}>
                                    <span style={{ textDecoration:'line-through', color:c.muted }}>
                                      {ri.document_type}{ri.other_description ? `: ${ri.other_description}` : ''}
                                    </span>
                                    <span style={{ fontSize:'10px', color:c.muted, fontFamily:"'DM Mono',monospace" }}>
                                      {ri.removed_at ? new Date(ri.removed_at).toLocaleDateString('en-US',{month:'short',day:'numeric'}) : ''}
                                    </span>
                                  </div>
                                  <div style={{ fontSize:'10px', color:c.muted, fontStyle:'italic', whiteSpace:'pre-wrap' }}>
                                    Removed by <strong style={{ color: c.sub }}>{ri.removed_by || 'Unknown'}</strong>
                                    {ri.removal_reason && <>: "{ri.removal_reason}"</>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* AOA templates attached by consultant */}
                  {docRequestAoaFiles.length > 0 && (
                    <div style={{ padding:'10px 12px', background:'rgba(96,165,250,0.08)', border:'1px solid rgba(96,165,250,0.25)', borderRadius:'6px', marginBottom:'10px', fontSize:'12px' }}>
                      <div style={{ color:'#60a5fa', fontWeight:'600', marginBottom:'8px' }}>🏛 Consultant attachments ({docRequestAoaFiles.length})</div>
                      {docRequestAoaFiles.map(f => {
                        const panelOpen = !!removeAttachOpen[f.id];
                        const busy = !!removeAttachBusy[f.id];
                        const previewing = !!previewAttachBusy[f.id];
                        const reasonText = removeAttachReason[f.id] || '';
                        return (
                          <div key={f.id} style={{ marginBottom:'6px' }}>
                            <div style={{
                              display:'flex', alignItems:'center', gap:'8px',
                              padding:'6px 8px',
                              background:isDark ? 'rgba(0,0,0,0.15)' : '#ffffff',
                              border:`1px solid ${c.border}`,
                              borderRadius: panelOpen ? '6px 6px 0 0' : '6px',
                              fontSize:'11px',
                            }}>
                              <span style={{ fontSize:'13px' }}>📎</span>
                              <span style={{ flex:1, color:c.sub, wordBreak:'break-all' }}>{f.file_name}</span>
                              {f.storage_path && (
                                <button
                                  title="Preview in new tab"
                                  style={{ ...s.btnSm, padding:'3px 8px', fontSize:'10px', background:'transparent', color:'#60a5fa', border:'1px solid rgba(96,165,250,0.4)', opacity: previewing ? 0.5 : 1 }}
                                  disabled={previewing}
                                  onClick={() => previewAttachment(f)}>
                                  {previewing ? '…' : '👁 Preview'}
                                </button>
                              )}
                              <button
                                title="Email consultant about this attachment"
                                style={{ ...s.btnSm, padding:'3px 8px', fontSize:'10px', background:'transparent', color:c.sub, border:`1px solid ${c.inputBorder}` }}
                                onClick={() => emailConsultantAboutAttachment(f)}>
                                ✉ Email
                              </button>
                              {!panelOpen && (
                                <button
                                  title="Remove this attachment (with reason)"
                                  style={{ background:'transparent', border:'none', color:c.muted, cursor:'pointer', fontSize:'14px', padding:'0 4px', lineHeight:'1' }}
                                  onClick={() => setRemoveAttachOpen(prev => ({ ...prev, [f.id]: true }))}>
                                  ✕
                                </button>
                              )}
                            </div>
                            {panelOpen && (
                              <div style={{ padding:'10px', background: isDark ? 'rgba(248,113,113,0.06)' : 'rgba(248,113,113,0.04)', border:`1px solid rgba(248,113,113,0.25)`, borderTop:'none', borderRadius:'0 0 6px 6px' }}>
                                <div style={{ fontSize:'11px', color:c.muted, marginBottom:'6px' }}>
                                  Why are you removing <strong style={{ color: c.text }}>{f.file_name}</strong>? (required)
                                </div>
                                <textarea
                                  autoFocus
                                  style={{ ...s.textarea, minHeight:'60px', fontSize:'12px', marginBottom:'8px' }}
                                  placeholder="e.g., Wrong file attached; outdated template; not applicable to this property..."
                                  value={reasonText}
                                  onChange={e => setRemoveAttachReason(prev => ({ ...prev, [f.id]: e.target.value }))}
                                  disabled={busy}
                                />
                                <div style={{ display:'flex', gap:'6px' }}>
                                  <button
                                    style={{ ...s.btnSm, background:'#f87171', color:'#ffffff', fontWeight:'700', padding:'5px 12px', opacity: busy || !reasonText.trim() ? 0.5 : 1 }}
                                    disabled={busy || !reasonText.trim()}
                                    onClick={() => removeAttachment(f, reasonText)}>
                                    {busy ? 'Removing…' : 'Remove Attachment'}
                                  </button>
                                  <button
                                    style={{ ...s.btnSm, background:'transparent', color:c.sub, border:`1px solid ${c.inputBorder}`, padding:'5px 12px' }}
                                    disabled={busy}
                                    onClick={() => {
                                      setRemoveAttachOpen(prev => ({ ...prev, [f.id]: false }));
                                      setRemoveAttachReason(prev => ({ ...prev, [f.id]: '' }));
                                    }}>
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Actions (depend on request/task stage) */}
                  {docRequest.status === 'pending' && !docRequestToken && (
                    <button
                      style={{ ...s.btn, width:'100%', opacity: docReqBusy ? 0.6 : 1 }}
                      disabled={docReqBusy}
                      onClick={approveDocRequest}>
                      {docReqBusy ? 'Approving…' : '✉️ Approve & Compose Email'}
                    </button>
                  )}

                  {docRequestToken && docRequest.status !== 'fulfilled' && (
                    <>
                      <div style={{ padding:'8px 10px', background:isDark?'rgba(0,0,0,0.2)':'#ffffff', border:`1px solid ${c.border}`, borderRadius:'6px', marginBottom:'8px' }}>
                        <div style={{ fontSize:'10px', color:c.muted, fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px' }}>Client Upload Link</div>
                        <div style={{ fontSize:'11px', color:c.sub, fontFamily:'monospace', wordBreak:'break-all', marginBottom:'6px' }}>
                          {window.location.origin}/upload/{docRequestToken.token}
                        </div>
                        <button style={{ ...s.btnSm, background:'linear-gradient(135deg,#B40000,#9C0000)', color: 'white', fontWeight:'700', padding:'4px 10px' }} onClick={copyUploadLink}>
                          📋 Copy Link
                        </button>
                        <span style={{ fontSize:'10px', color:c.muted, marginLeft:'8px' }}>
                          Expires {docRequestToken.expires_at ? new Date(docRequestToken.expires_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'}
                        </span>
                      </div>

                      {/* Step 6: 'Approved but email not yet sent' nudge */}
                      {docRequest.status === 'approved' && !docRequest.email_sent_at && selectedTask.status === 'Open' && (
                        <div style={{ padding:'8px 10px', background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.3)', borderRadius:'6px', fontSize:'11px', color: isDark?'#fbbf24':'#b45309', marginBottom:'8px' }}>
                          <strong>✉️ Ready to email.</strong> Review the pre-filled client email in the Compose Email tab → click <strong>Open in Outlook</strong> → send → confirm. Task will flip to In Progress once confirmed.
                        </div>
                      )}

                      {/* Notify & Complete appears when task is 'Ready to Deliver' */}
                      {selectedTask.status === TASK_STATUS.READY_TO_DELIVER && (
                        <button
                          style={{ ...s.btn, width:'100%', background:'linear-gradient(135deg,#a769fa,#7c3aed)', color:'#ffffff', opacity: docReqBusy ? 0.6 : 1 }}
                          disabled={docReqBusy}
                          onClick={notifyAndCompleteDocRequest}>
                          {docReqBusy ? 'Preparing email…' : '📤 Email Consultant & Close'}
                        </button>
                      )}

                      {/* Step 8: Deliver Partial — appears when SOME items are
                          ready (fulfilled + intaked + not yet delivered) AND
                          others are still owed. Lets admin send received items
                          to consultant now while continuing to track outstanding ones. */}
                      {canDeliverPartial() && (
                        <div style={{ marginBottom:'8px' }}>
                          <button
                            style={{ ...s.btn, width:'100%', background:'linear-gradient(135deg,#0ea5e9,#0369a1)', color:'#ffffff', opacity: docReqBusy ? 0.6 : 1, marginBottom:'6px' }}
                            disabled={docReqBusy}
                            onClick={deliverPartialDocRequest}>
                            {docReqBusy ? 'Preparing email…' : `📦 Deliver Partial (${partialDeliveryDeliverable().length} of ${partialDeliveryDeliverable().length + partialDeliveryOwed().length})`}
                          </button>
                          <div style={{ fontSize:'10px', color:c.muted, lineHeight:'1.4', textAlign:'center' }}>
                            Send <strong style={{ color:c.text }}>{partialDeliveryDeliverable().length}</strong> received item{partialDeliveryDeliverable().length === 1 ? '' : 's'} to consultant now · <strong style={{ color:'#854F0B' }}>{partialDeliveryOwed().length}</strong> still owed
                          </div>
                        </div>
                      )}

                      {/* Step-Gap1: Waiting on Intake callout.
                          Task items are all addressed, but files haven't been processed
                          through Document Intake yet. Email Consultant button is suppressed
                          until admin completes intake. */}
                      {selectedTask.status === TASK_STATUS.WAITING_ON_INTAKE && (
                        <div style={{ padding:'10px 12px', background:'rgba(249,115,22,0.08)', border:'1px solid rgba(249,115,22,0.35)', borderRadius:'8px', marginBottom:'8px' }}>
                          <div style={{ fontSize:'12px', fontWeight:'700', color: isDark?'#fb923c':'#c2410c', marginBottom:'4px' }}>
                            ⚠ {unintakedFileCount} file{unintakedFileCount === 1 ? '' : 's'} awaiting intake
                          </div>
                          <div style={{ fontSize:'11px', color: isDark?'#fb923c':'#9a3412', lineHeight:'1.5', marginBottom:'10px' }}>
                            Before the consultant receives these, staff must process each file through Document Intake — split multi-document PDFs, verify doc type, file to the right folder/year. The Email Consultant button will activate once intake is complete.
                          </div>
                          <button
                            style={{ ...s.btn, width:'100%', background:'linear-gradient(135deg,#f97316,#c2410c)', color:'#ffffff', fontWeight:'700' }}
                            onClick={() => {
                              // Tab-switch to Document Intake. Pass the token + return-task
                              // id via sessionStorage sidecar so DocumentIntake can filter
                              // and so "Back to Task" can return here.
                              try {
                                sessionStorage.setItem('wh_intake_token', docRequestToken?.token || '');
                                sessionStorage.setItem('wh_intake_return_task', selectedTask.id);
                              } catch {}
                              // Navigate via URL — StaffPortal hydrates activePage from
                              // ?page= on load, so this lands cleanly on the Intake page
                              // and the browser back button returns here.
                              window.location.href = `${window.location.pathname}?page=intake`;
                            }}>
                            → Process in Document Intake
                          </button>
                        </div>
                      )}

                      {/* Awaiting client message — shown only when nothing actionable is available.
                          Suppressed when canDeliverPartial() because the partial-delivery
                          button already conveys "you have something to do here". */}
                      {selectedTask.status === TASK_STATUS.IN_PROGRESS && !docReqAllAddressed() && !canDeliverPartial() && (
                        <div style={{ fontSize:'11px', color:c.muted, fontStyle:'italic', textAlign:'center', padding:'6px' }}>
                          Awaiting client to address remaining items…
                        </div>
                      )}

                      {/* Partial Delivered status callout — admin has already delivered
                          some items. Show a quick reminder of state. */}
                      {selectedTask.status === TASK_STATUS.PARTIAL_DELIVERED && !canDeliverPartial() && (
                        <div style={{ padding:'10px 12px', background:'rgba(14,165,233,0.08)', border:'1px solid rgba(14,165,233,0.32)', borderRadius:'8px', marginBottom:'8px' }}>
                          <div style={{ fontSize:'12px', fontWeight:'700', color: isDark?'#38bdf8':'#0369a1', marginBottom:'2px' }}>
                            ✓ Partial delivery sent
                          </div>
                          <div style={{ fontSize:'11px', color: isDark?'#7dd3fc':'#075985', lineHeight:'1.5' }}>
                            {docRequestItems.filter(i => !i.removed_at && i.delivered_to_consultant_at).length} item{docRequestItems.filter(i => !i.removed_at && i.delivered_to_consultant_at).length === 1 ? '' : 's'} already delivered to consultant. Awaiting client to address remaining {partialDeliveryOwed().length} item{partialDeliveryOwed().length === 1 ? '' : 's'}.
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {docRequest.status === 'fulfilled' && (
                    <div style={{ padding:'8px 10px', background:'rgba(52,211,153,0.08)', border:'1px solid rgba(52,211,153,0.25)', borderRadius:'6px', fontSize:'11px', color:'#34d399', textAlign:'center' }}>
                      ✓ Request completed
                    </div>
                  )}

                  {docReqError && (
                    <div style={{ marginTop:'8px', padding:'6px 10px', background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:'6px', fontSize:'11px', color:'#f87171' }}>
                      ⚠ {docReqError}
                    </div>
                  )}
                </div>
              )}

              {/* Source email */}
              {selectedTask.source_email && (
                <div style={{ marginBottom:'14px' }}>
                  <div style={{ fontSize:'11px', color:c.muted, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.06em' }}>Original Email</div>
                  <div style={{ background: isDark ? 'rgba(26,34,56,0.6)' : '#f0f4ff', border:`1px solid ${c.border}`, borderRadius:'8px', padding:'12px', fontSize:'12px', color:c.sub, lineHeight:'1.6', whiteSpace:'pre-wrap', maxHeight:'200px', overflowY:'auto' }}>
                    {selectedTask.source_email_from && <div style={{ fontWeight:'600', color:c.text, marginBottom:'6px' }}>From: {selectedTask.source_email_from}</div>}
                    {selectedTask.source_email}
                  </div>
                </div>
              )}

              {/* Thread messages */}
              {(selectedTask.email_thread || []).length > 0 && (
                <div>
                  <div style={{ fontSize:'11px', color:c.muted, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.06em' }}>Correspondence</div>
                  {[...(selectedTask.email_thread || [])].reverse().map((msg, i) => {
                    const isSystem = msg.direction === 'system';
                    const isNote = msg.direction === 'note';
                    const isOutbound = msg.direction === 'outbound';
                    const bg = isSystem ? (isDark?'rgba(167,105,250,0.06)':'rgba(167,105,250,0.05)')
                      : isNote ? (isDark?'rgba(52,211,153,0.06)':'rgba(52,211,153,0.04)')
                      : isOutbound ? (isDark?'rgba(180,0,0,0.06)':'rgba(180,0,0,0.05)')
                      : (isDark?'rgba(96,165,250,0.06)':'rgba(96,165,250,0.05)');
                    const border = isSystem ? 'rgba(167,105,250,0.25)'
                      : isNote ? 'rgba(52,211,153,0.2)'
                      : isOutbound ? 'rgba(180,0,0,0.2)'
                      : 'rgba(96,165,250,0.2)';
                    const headerColor = isSystem ? '#a769fa'
                      : isNote ? '#34d399'
                      : isOutbound ? 'var(--red)'
                      : '#60a5fa';
                    const headerText = isSystem
                      ? `⚙ ${msg.label || 'System'} · ${msg.from_name}`
                      : isNote
                        ? `📝 Note by ${msg.from_name}`
                        : `${msg.from_name} → ${msg.to_email || ''}`;
                    return (
                      <div key={i} style={{ marginBottom:'10px', background: bg, border:`1px solid ${border}`, borderRadius:'8px', padding:'10px 12px' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'5px' }}>
                          <div style={{ fontSize:'11px', fontWeight:'600', color: headerColor }}>{headerText}</div>
                          <div style={{ fontSize:'10px', color:c.muted }}>{new Date(msg.sent_at).toLocaleDateString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</div>
                        </div>
                        <div style={{ fontSize:'12px', color:c.sub, lineHeight:'1.5', whiteSpace:'pre-wrap' }}>{msg.body}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* From the group — read-only aggregate of notes on SIBLING tasks I'm entitled to see. Each task keeps its
                  own thread (independent-task model); this surfaces sibling notes so they aren't invisible. Not editable
                  here — reply on the sibling itself. Confidential siblings are already filtered out of groupNotes. */}
              {groupNotes.length > 0 && (
                <div style={{ marginTop:'14px' }}>
                  <div style={{ fontSize:'11px', color:c.muted, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.06em' }}>From the group ({groupNotes.length})</div>
                  {groupNotes.map((msg, i) => (
                    <div key={`gn-${i}`} onClick={() => { const sib = (groupSiblings || []).find(g => g.id === msg._sibId); if (sib && sib.id !== selectedTask.id) setSelectedTask(sib); }}
                      style={{ marginBottom:'10px', background: isDark?'rgba(148,163,184,0.06)':'rgba(148,163,184,0.06)', border:`1px solid ${c.border}`, borderRadius:'8px', padding:'10px 12px', cursor:'pointer' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'5px' }}>
                        <div style={{ fontSize:'11px', fontWeight:'600', color:c.sub }}>📝 Note by {msg.from_name} · on {msg._sibName}'s task</div>
                        <div style={{ fontSize:'10px', color:c.muted }}>{msg.sent_at ? new Date(msg.sent_at).toLocaleDateString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : ''}</div>
                      </div>
                      <div style={{ fontSize:'12px', color:c.sub, lineHeight:'1.5', whiteSpace:'pre-wrap' }}>{msg.body}</div>
                    </div>
                  ))}
                </div>
              )}

              {(!selectedTask.source_email && (selectedTask.email_thread||[]).length === 0 && groupNotes.length === 0) && (
                <div style={{ color:c.muted, fontSize:'12px', textAlign:'center', padding:'20px' }}>No activity yet — add a note or send an email to get started</div>
              )}
            </div>

            {/* RIGHT: Action panel */}
            <div style={{ padding:'16px', display:'flex', flexDirection:'column', gap:'12px' }}>
              {/* Toggle */}
              <div style={{ display:'flex', gap:'6px', background: isDark?'rgba(0,0,0,0.2)':'var(--bg)', borderRadius:'8px', padding:'4px' }}>
                {[['note','📝 Add Note'],['email','📧 Compose Email']].map(([mode,label]) => (
                  <button key={mode} onClick={() => setReplyMode(mode)}
                    style={{ flex:1, padding:'6px 10px', borderRadius:'6px', border:'none', cursor:'pointer', fontSize:'12px', fontWeight:'600', fontFamily:"'Public Sans',system-ui,sans-serif",
                      background: replyMode===mode ? (isDark?'#2F3854':'#ffffff') : 'transparent',
                      color: replyMode===mode ? (mode==='email'?'var(--red)':c.text) : c.muted,
                      boxShadow: replyMode===mode ? '0 1px 3px rgba(0,0,0,0.15)' : 'none' }}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Note mode */}
              {replyMode === 'note' && (
                <div>
                  <label style={s.label}>Internal Note</label>
                  <textarea style={{ ...s.textarea, minHeight:'160px' }} value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Add a note, update, or action taken..." />
                  <button style={{ ...s.btn, marginTop:'8px', opacity: savingNote||!noteText?0.6:1 }} disabled={savingNote||!noteText} onClick={saveNote}>
                    {savingNote ? 'Saving...' : '💾 Save Note'}
                  </button>
                </div>
              )}

              {/* Email mode */}
              {replyMode === 'email' && (
                <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                  {/* Client contact picker — only shown if this task has a document_request with contacts loaded */}
                  {clientContacts.length > 0 && (
                    <div>
                      <label style={s.label}>Quick-pick recipient</label>
                      <select
                        style={s.select}
                        value={replyTo}
                        onChange={e => setReplyTo(e.target.value)}>
                        <option value="">— Pick a contact or type below —</option>
                        {clientContacts.map((ct, i) => (
                          <option key={i} value={ct.email}>
                            {ct.role}: {ct.name ? `${ct.name} <${ct.email}>` : ct.email}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div><label style={s.label}>To</label><input style={s.input} value={replyTo} onChange={e=>setReplyTo(e.target.value)} placeholder="recipient@email.com" /></div>
                  <div>
                    <label style={s.label}>CC <span style={{ fontSize:'10px', color:c.muted, fontWeight:'400' }}>(optional — separate multiple with commas)</span></label>
                    <input style={s.input} value={replyCc} onChange={e=>setReplyCc(e.target.value)} placeholder="cc1@email.com, cc2@email.com" />
                  </div>
                  <div><label style={s.label}>Subject</label><input style={s.input} value={replySubject} onChange={e=>setReplySubject(e.target.value)} placeholder={`Re: ${selectedTask.title}`} /></div>
                  <div><label style={s.label}>Message</label><textarea style={{ ...s.textarea, minHeight:'200px', fontFamily:"'Public Sans',system-ui,sans-serif" }} value={replyText} onChange={e=>setReplyText(e.target.value)} placeholder="Type your message..." /></div>
                  <div style={{ fontSize:'11px', color:c.muted, padding:'8px 10px', background: isDark?'rgba(96,165,250,0.08)':'rgba(96,165,250,0.06)', border:'1px solid rgba(96,165,250,0.25)', borderRadius:'6px' }}>
                    ℹ Clicking Send opens Outlook with this email pre-filled. Review, click Send in Outlook, then confirm in the dialog so WigleyHub logs it.
                  </div>
                  <div style={{ display:'flex', gap:'8px' }}>
                    <button style={{ ...s.btn, opacity:sendingReply||!replyText||!replyTo?0.6:1 }} disabled={sendingReply||!replyText||!replyTo} onClick={sendReply}>
                      {sendingReply?'Opening Outlook...':'📤 Open in Outlook'}
                    </button>
                    <button style={s.btnGhost} onClick={()=>{ setReplyText(''); setReplyTo(''); setReplyCc(''); setReplySubject(''); }}>Clear</button>
                  </div>
                </div>
              )}

              {/* Task description */}
              {selectedTask.description && (
                <div style={{ borderTop:`1px solid ${c.border}`, paddingTop:'12px' }}>
                  <div style={{ fontSize:'11px', color:c.muted, marginBottom:'5px', textTransform:'uppercase', letterSpacing:'0.06em' }}>Task Description</div>
                  <div style={{ fontSize:'12px', color:c.sub, lineHeight:'1.6', whiteSpace:'pre-wrap' }}>{selectedTask.description}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Step 6: Email Sent confirmation modal.
          Shown after admin clicks "Open in Outlook" — asks them to confirm
          that they actually clicked Send in Outlook. Only on Yes do we log
          the email + update task status. */}
      {emailConfirmOpen && emailConfirmData && (
        <div
          onClick={cancelEmailConfirm}
          style={{
            position:'fixed', top:0, left:0, right:0, bottom:0,
            background:'rgba(0,0,0,0.5)', zIndex:1000,
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: c.card,
              border:`1px solid ${c.border}`,
              borderRadius:'12px',
              padding:'24px',
              maxWidth:'480px', width:'90%',
              boxShadow:'0 20px 50px rgba(0,0,0,0.3)',
            }}>
            <div style={{ fontFamily:"'Archivo',sans-serif", fontSize:'18px', fontWeight:'700', color:c.text, marginBottom:'10px' }}>
              📤 Did you send the email?
            </div>
            <div style={{ fontSize:'13px', color:c.sub, lineHeight:'1.5', marginBottom:'14px' }}>
              Outlook should have opened with your email pre-filled. Once you click <strong style={{ color:c.text }}>Send</strong> in Outlook, confirm below so WigleyHub logs it.
            </div>
            <div style={{ fontSize:'11px', color:c.muted, padding:'10px 12px', background: isDark?'rgba(0,0,0,0.2)':'var(--line-soft)', borderRadius:'6px', marginBottom:'14px' }}>
              <div style={{ marginBottom:'4px' }}><strong style={{ color:c.text }}>To:</strong> {emailConfirmData.to}</div>
              <div style={{ marginBottom:'4px' }}><strong style={{ color:c.text }}>Subject:</strong> {emailConfirmData.subject}</div>
              <div style={{ fontSize:'10px', color:c.muted }}>
                Purpose: {emailConfirmData.purpose === 'client_request' ? 'Client — document upload request'
                  : emailConfirmData.purpose === 'consultant_notify' ? 'Consultant — docs-ready notification'
                  : emailConfirmData.purpose === 'consultant_partial' ? 'Consultant — partial delivery notification'
                  : 'General correspondence'}
              </div>
            </div>
            <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
              <button
                style={{ ...s.btnGhost, padding:'10px 18px' }}
                onClick={cancelEmailConfirm}>
                No, I didn't send it
              </button>
              <button
                style={{ ...s.btn, padding:'10px 18px', background:'linear-gradient(135deg,#34d399,#059669)', color:'#ffffff' }}
                onClick={confirmEmailSent}>
                ✓ Yes, I sent it — log it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      {!selectedTask && (
        <>
          <div style={{ display:'flex', gap:'8px', marginBottom:'14px', flexWrap:'wrap' }}>
            {[['my','My Tasks'], ['assigned','Tasks I Assigned'], ['all','All Tasks']]
              .map(([id,label]) => (
              <button key={id} onClick={() => {
                  setActiveTab(id);
                  // Reset ALL filters on tab change — each tab is a fresh view. Fixes the leak where a filter set on
                  // All Tasks (esp. the roster's filterAssignee, which has no control on other tabs) silently emptied
                  // My Tasks with no way to clear it.
                  setKpiFilter(''); setFilterStatus(''); setFilterPriority(''); setFilterClient(''); setFilterAssignee(''); setSearchQ('');
                  if (id === 'assigned') { try { localStorage.setItem(assignedSeenKey, new Date().toISOString()); } catch {} setAssignedNewlyDone(0); }   // stamping = "seen" → clear the badge
                }}
                style={{ ...s.btnGhost, fontWeight: activeTab===id?'700':'400', borderColor: activeTab===id?'var(--red)':c.inputBorder, color: activeTab===id?'var(--red)':c.sub }}>
                {label} {id==='my'?`(${tasks.length})`:id==='all'?(() => {
                  // The tab counts allTasks.length — rows RLS actually returned — while the cards above read
                  // staff_workload_counts(), which counts confidential tasks too. Both are right and they
                  // legitimately differ, so the label states both rather than leaving one looking broken.
                  // "of N" appears ONLY when they differ: a viewer who can see everything gets the plain
                  // count and no new vocabulary. ⛔ Guarded with > rather than !==: the RPC is a superset of
                  // what RLS returns, so a total BELOW the visible count would mean something is wrong, and
                  // "17 of 15" would present that as fact. Unavailable RPC → plain count, never "17 of —".
                  const shown = allTasks.length;
                  const partial = rpcOpenTotal !== null && rpcOpenTotal > shown;
                  return (
                    <span title={partial ? 'Tasks you can open, of all open work. The difference is confidential tasks, which are counted but not listed.' : undefined}>
                      ({partial ? `${shown} of ${rpcOpenTotal}` : shown})
                    </span>
                  );
                })():id==='assigned'?`(${assignedTasks.length} open${assignedNewlyDone?`, ${assignedNewlyDone} newly done`:''})`:''}
              </button>
            ))}
            <div style={{ marginLeft:'auto', display:'flex', gap:'8px', flexWrap:'wrap' }}>
              <input style={{ ...s.input, width:'200px' }} placeholder="Search..." value={searchQ} onChange={e=>setSearchQ(e.target.value)} />
              <select style={{ ...s.select, width:'140px' }} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
                <option value="">All Statuses</option>
                {STATUSES.map(s2=><option key={s2} value={s2}>{s2}</option>)}
              </select>
              <select style={{ ...s.select, width:'120px' }} value={filterPriority} onChange={e=>setFilterPriority(e.target.value)}>
                <option value="">All Priorities</option>
                {PRIORITIES.map(p=><option key={p} value={p}>{p}</option>)}
              </select>
              {activeTab === 'all' && <input style={{ ...s.input, width:'160px' }} placeholder="Filter by client..." value={filterClient} onChange={e=>setFilterClient(e.target.value)} />}
              {activeTab === 'all' && (
                <select style={{ ...s.select, width:'160px' }} value={filterAssignee} onChange={e=>setFilterAssignee(e.target.value)}>
                  <option value="">All Staff</option>
                  {staffList.map(s => (
                    <option key={s.id || s.staff_id || s.name} value={s.id || s.staff_id || s.name}>{s.name}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {activeTab === 'all' && (
            <div style={{ ...s.card, marginBottom:'14px' }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:c.sub, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'10px' }}>Team Workload <span style={{ fontWeight:400, textTransform:'none', color:c.muted }}>· lightest load first · click a person to filter below</span></div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:'8px' }}>
                {workloadRoster.map(({ sid, name, active, waiting, overdue }) => {
                  const selected = filterAssignee === sid;
                  return (
                    <div key={sid} onClick={() => setFilterAssignee(selected ? '' : sid)}
                      style={{ cursor:'pointer', padding:'10px 12px', borderRadius:'9px', border:`1px solid ${selected?'var(--red)':c.inputBorder}`, background:selected?'rgba(180,0,0,0.06)':'transparent' }}>
                      <div style={{ fontSize:'13px', fontWeight:600, color:c.text, marginBottom:'4px' }}>{name}</div>
                      <div style={{ display:'flex', gap:'10px', fontSize:'11px', alignItems:'baseline' }}>
                        <span style={{ color:c.text, fontWeight:700 }}>{active ?? '—'} <span style={{ color:c.muted, fontWeight:400 }}>active</span></span>
                        <span style={{ color:c.sub }}>{waiting ?? '—'} waiting</span>
                        {overdue > 0 && <span style={{ color:'#f87171', fontWeight:700 }}>⚠ {overdue}</span>}
                        {/* overdue is null only when the RPC is unavailable — active/waiting already show '—' */}
                      </div>
                    </div>
                  );
                })}
              </div>
              {!workloadCounts && (
                <div style={{ fontSize:'11px', color:'#f87171', marginTop:'8px' }}>
                  ⚠ Workload counts unavailable{workloadErr ? ` — ${workloadErr}` : ''}. These are capacity numbers, so they show “—” rather than a possibly-wrong zero.
                </div>
              )}
              {filterAssignee && <div style={{ fontSize:'11px', color:c.muted, marginTop:'8px' }}>Filtered to the selected person below · <span onClick={()=>setFilterAssignee('')} style={{ color:'var(--red)', cursor:'pointer', textDecoration:'underline' }}>clear</span></div>}
            </div>
          )}

          {/* (b) The gap line. The bubble counts a person's confidential tasks (RPC); the list below cannot
              show them (RLS). Rendered ONLY when the two disagree, so an ordinary person shows nothing extra
              — otherwise "No tasks for this person" reads as "she has no work" when she has four. */}
          {(() => {
            if (activeTab !== 'all' || !filterAssignee || !workloadCounts) return null;
            const wl = workloadCounts.get(filterAssignee);
            const rpcActive = wl ? wl.todo + wl.in_progress : 0;
            const shownActive = openList.filter(t => TASK_STATUS_ON_US.includes(effectiveStatus(t))).length;
            if (rpcActive === shownActive) return null;
            return (
              <div style={{ fontSize:'11px', color:c.muted, margin:'0 0 8px 2px' }}
                title="Counts come from an aggregate that includes confidential tasks; the list can only show tasks you're permitted to read.">
                {rpcActive} active · {shownActive} shown
              </div>
            );
          })()}

          <div style={s.card}>
            <table style={s.table}>
              <thead><tr>
                {[['title','Task'],['type','Type'],['client','Client / Property']].map(([key,label]) => (
                  <th key={key} style={{ ...s.th, cursor:'pointer', userSelect:'none' }} onClick={()=>toggleSort(key)}>{label}{sortKey===key ? (sortDir==='asc'?' ▲':' ▼') : ''}</th>
                ))}
                {showAssigneeCol && <th style={{ ...s.th, cursor:'pointer', userSelect:'none' }} onClick={()=>toggleSort('assignee')}>Assigned To{sortKey==='assignee' ? (sortDir==='asc'?' ▲':' ▼') : ''}</th>}
                {activeTab === 'my' && <th style={{ ...s.th, cursor:'pointer', userSelect:'none' }} onClick={()=>toggleSort('assigner')}>Assigned By{sortKey==='assigner' ? (sortDir==='asc'?' ▲':' ▼') : ''}</th>}
                {[['priority','Priority'],['status','Status'],['due','Due Date']].map(([key,label]) => (
                  <th key={key} style={{ ...s.th, cursor:'pointer', userSelect:'none' }} onClick={()=>toggleSort(key)}>{label}{sortKey===key ? (sortDir==='asc'?' ▲':' ▼') : ''}</th>
                ))}
                <th style={{ ...s.th, textAlign:'center' }}>Action</th>
              </tr></thead>
              <tbody>
                {(() => {
                  // Unified across all tabs: an Open list (open-only load) + a collapsed "Completed (N)" section backed
                  // by the scoped paged terminal query (true count + Load older), auto-expanding + DB-searching when searching.
                  const emptyAll = activeTab === 'assigned' ? "You haven't assigned any tasks to others yet."
                    : activeTab === 'all' ? (filterAssignee ? 'No tasks for this person.' : 'No tasks found')
                    : 'No tasks assigned to you';
                  const emptyOpen = activeTab === 'assigned' ? 'Nothing open — all handled.'
                    : activeTab === 'my' ? "Nothing open — you're clear."
                    : 'Nothing open.';
                  if (openList.length === 0 && terminalTotal === 0 && doneF.length === 0 && cancelledF.length === 0) {
                    return <tr><td colSpan="8" style={{ ...s.td, textAlign:'center', color:c.muted, padding:'32px' }}>{searchingTerminal ? 'No tasks match your search' : emptyAll}</td></tr>;
                  }
                  return (<>
                    {sectionRow('Open', openList.length)}
                    {openList.length === 0
                      ? <tr><td colSpan="8" style={{ ...s.td, color:c.muted, fontStyle:'italic' }}>{searchingTerminal ? 'No open tasks match your search.' : emptyOpen}</td></tr>
                      : openList.map(task => renderTaskRow(task, false))}
                    {(terminalTotal > 0 || searchingTerminal) && (
                      <tr onClick={() => { if (!searchingTerminal) setShowTerminal(v => !v); }} style={{ cursor: searchingTerminal ? 'default' : 'pointer' }}>
                        <td colSpan="8" style={{ ...s.td, background:c.rowHover, fontWeight:700, fontSize:'11px', color:c.sub, textTransform:'uppercase', letterSpacing:'0.06em', borderTop:`2px solid ${c.border}` }}>
                          {searchingTerminal
                            ? `Completed — ${doneF.length + cancelledF.length} matching “${searchQ.trim()}”`
                            : `${showTerminal ? '▾' : '▸'} Completed (${terminalTotal})`}
                        </td>
                      </tr>
                    )}
                    {(showTerminal || searchingTerminal) && (terminalTotal > 0 || searchingTerminal) && (<>
                      {doneF.length === 0
                        ? <tr><td colSpan="8" style={{ ...s.td, color:c.muted, fontStyle:'italic' }}>{searchingTerminal ? 'No completed tasks match your search.' : 'No completed tasks match the current filter.'}</td></tr>
                        : doneF.map(task => renderTaskRow(task, true))}
                      {cancelledF.length > 0 && (<>
                        {sectionRow('Cancelled', cancelledF.length)}
                        {cancelledF.map(task => renderTaskRow(task, false))}
                      </>)}
                      {!searchingTerminal && terminal.length < terminalTotal && (
                        <tr><td colSpan="8" style={{ ...s.td, textAlign:'center' }}>
                          <button style={{ ...s.btnGhost, fontSize:'11px' }} onClick={loadMoreTerminal}>Load older — showing {terminal.length} of {terminalTotal}</button>
                        </td></tr>
                      )}
                    </>)}
                  </>);
                })()}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
